<template>
  <main class="csv-page-shell">
    <section class="center-card">
      <header class="head">
        <h2>CSV Helper</h2>
        <p class="muted">Please upload a csv spreadsheet.</p>
      </header>
<!-- Quick start guide -->
<section
  v-if="showGuide"
  class="panel lite"
  role="region"
  aria-label="How to use CSV Helper"
  style="margin-bottom:12px"
>
  <h3 class="panel-title">Quick start</h3>

  <ol class="compact">
    <li><strong>Drop</strong> your CSV (or choose a file).</li>
    <li><strong>Review</strong> detected withdrawals. Use <em>All valid</em> or tick specific rows.</li>
    <li><strong>Submit checked</strong>. </li>
    <li>
      <strong>View results</strong> in the
      <router-link to="/AnalysisTool">Analysis Tool</router-link>
      as they are processed.
    </li>
  </ol>
<!--
  <p class="tiny muted" style="margin-top:6px">
    Only requests that successfully queue will consume credits. Rows that need more info are skipped without charge.
  </p>
  <p
    v-if="canUseNormalMode"
    class="tiny muted"
    style="margin-top:2px"
  >
    Advanced users: open the <em>Advanced panel</em> to pick columns, inspect detection scores, and preview the parsed grid before submitting.
  </p>-->

  <div class="actions" style="margin-top:10px">
    <button class="btn btn--ghost btn--sm" @click="dismissGuide">Hide guide</button>
  </div>
</section>

<!-- Collapsed nudge (shown when guide is hidden) -->
<section
  v-else
  class="panel lite"
  role="region"
  aria-label="Show quick guide"
  style="margin-bottom:12px; padding:10px"
>
  <button class="ghost tiny" @click="showGuide = true">Show quick guide</button>
</section>

      <!-- Upload -->
<section class="panel">
  <div
    class="dropzone"
    :class="dropzoneClass"
    @dragover.prevent="onDragOver"
    @dragenter.prevent="onDragEnter"
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop"
    role="button"
    aria-label="CSV or TSV dropzone"
    :aria-invalid="isDragInvalid ? 'true' : 'false'"
  >
    <div class="dz-default">
     <p>Drag & drop a .csv, .tsv, or .txt file here</p>
<p class="muted">or</p>

<label class="file-btn" for="csvInput">Choose CSV / TSV</label>
<input
  id="csvInput"
  ref="fileInput"
  type="file"
  accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
  @change="onFileInput"
/>

    </div>

    <div class="dz-overlay" aria-hidden="true">
      <p v-if="isDragInvalid" class="dz-callout dz-invalid">Only CSV, TSV, or TXT files are allowed</p>
      <p v-else class="dz-callout">Release to load {{ hoverFilename || 'file' }}</p>
    </div>
  </div>

  <!-- Top row: just the advanced toggle + any error -->
  <div class="actions">
    <button
      class="ghost"
      @click="toggleAdvancedPanel"
      :aria-expanded="showAdvanced ? 'true' : 'false'"
      aria-controls="advanced-panel"
      v-if="canUseNormalMode"
    >
      {{ showAdvanced ? 'Hide advanced panel' : 'Show advanced panel' }}
    </button>

    <span v-if="analyzeError" class="err">{{ analyzeError }}</span>
  </div>

  <div v-if="errorMessage" class="error">{{ errorMessage }}</div>

  <!-- Advanced controls row (hidden by default) -->
  <div
    id="advanced-panel"
    class="actions advanced-row"
    v-show="showAdvanced"
  >
    <button class="primary" @click="analyze" :disabled="isAnalyzing">Analyze</button>
    <button class="ghost" @click="clearHighlights" :disabled="isAnalyzing">Clear highlights</button>
<!-- Somewhere in your overrides panel -->
<label class="toggle">
  <input type="checkbox" v-model="assumeAllWithdrawalsWhenNoType">
  <span>Assume withdrawals if Type column is missing (beta)</span>
</label>

    <button
      class="ghost"
      :disabled="headerRowIdx < 0"
      @click="showOverridesPanel = !showOverridesPanel"
      :aria-expanded="showOverridesPanel ? 'true' : 'false'"
    >
      {{ showOverridesPanel ? 'Hide columns' : 'Choose columns' }}
    </button>

    <label class="inline">
      <input type="checkbox" v-model="useAmountFallback" />
      Enable fallback by amount sign
    </label>
  </div>
</section>

<!-- Column overrides (only when Advanced is open) -->
 <!-- Column overrides + detection summary (hidden behind toggle) -->
      <section
        v-if="headerRowIdx >= 0 && showOverridesPanel"
        class="panel lite"
      >
        <h3 class="panel-title">Column Overrides & Detection Summary</h3>

        <div class="grid-2">
          <div>
            <div class="field">
              <label>Tx Hash column</label>
              <select v-model.number="selectedCols.txHash">
                <option :value="-1">— none —</option>
                <option v-for="(h,i) in headerRaw" :key="'txh'+i" :value="i">{{ prettyHeader(h, i) }}</option>
              </select>
            </div>
            <div class="field">
              <label>To Address column</label>
              <select v-model.number="selectedCols.toAddress">
                <option :value="-1">— none —</option>
                <option v-for="(h,i) in headerRaw" :key="'toh'+i" :value="i">{{ prettyHeader(h, i) }}</option>
              </select>
            </div>
            <div class="field">
              <label>Transfer Type column</label>
              <select v-model.number="selectedCols.transferType">
                <option :value="-1">— none —</option>
                <option v-for="(h,i) in headerRaw" :key="'tth'+i" :value="i">{{ prettyHeader(h, i) }}</option>
              </select>
            </div>
            <div class="field">
              <label>Asset/Symbol column</label>
              <select v-model.number="selectedCols.asset">
                <option :value="-1">— none —</option>
                <option v-for="(h,i) in headerRaw" :key="'ash'+i" :value="i">{{ prettyHeader(h, i) }}</option>
              </select>
            </div>
          </div>

          <div>
            <p class="muted">We score columns by headers and by content samples (hash/address patterns).</p>
   
            
   
            <table class="mini results">
  <thead>
    <tr>
      <th>Col</th>
      <th>Header</th>
      <th class="right">TxHash-likes</th>
      <th class="right">Address-likes</th>
      <th class="right">Amount score</th> <!-- NEW -->
    </tr>
  </thead>
  <tbody>
    <tr v-for="(h,i) in headerRaw" :key="'stats'+i">
      <td>C{{ i+1 }}</td>
      <td>{{ h }}</td>
      <td class="right">{{ detectionStats.txHashScore[i] || 0 }}</td>
      <td class="right">{{ detectionStats.addrScore[i] || 0 }}</td>
      <td class="right">{{ detectionStats.amountScore[i] != null ? detectionStats.amountScore[i].toFixed(2) : '0.00' }}</td> <!-- NEW -->
    </tr>
  </tbody>
</table>

          </div>
        </div>

        <div class="actions">
          <button class="primary" @click="analyze" :disabled="isAnalyzing">Re-analyze with overrides</button>
          <span class="muted">Format guess: <b>{{ analysis?.format?.label || '—' }}</b></span>
        </div>
      </section>

<!-- Output Spreadsheet: keep mounted for analysis but hide visually when not advanced -->
<div class="section">
  <h2 v-show="showAdvanced">Output Spreadsheet</h2>
  <SpreadsheetComponentAdvancedB
    ref="outputSpreadsheet"
    button-label="Add 100 Rows"
    :highlight-header="false"
    width="100%"
    height="360px"
    v-show="showAdvanced"   
  />
</div>
<!-- Results (only when available) -->
<section v-if="analysis" class="analysis">
  <header class="analysis__head">
    <h3>CSV Helper Results</h3>
    <p class="muted" v-if="showAdvanced">
      Columns:
      <span>txHash = {{ colName(analysis.columns.txHash) }}</span> ·
      <span>toAddress = {{ colName(analysis.columns.toAddress) }}</span> ·
      <span>type = {{ colName(analysis.columns.transferType) }}</span> ·
      <span>asset = {{ colName(analysis.columns.asset) }}</span> ·
      <span>amount = {{ colName(analysis.columns.amount) }}</span>
    </p>
    <div v-if="analysis.validationNote && showAdvanced" class="notif notif--error" style="margin:8px 0">
      {{ analysis.validationNote }}
    </div>
  </header>

  <div v-if="!analysis.withdrawals || analysis.withdrawals.length === 0" class="empty">
    No withdrawals identified.
  </div>

<!-- Add this bulk bar just above <table class="results"> -->
<!-- Compact bulk actions bar -->
<div v-if="analysis && analysis.withdrawals?.length" class="bulkbar" role="region" aria-label="Bulk actions">
  <div class="bulkbar__left">
    <label class="checkbox">
      <input
        type="checkbox"
        :checked="allValidSelected"
        @change="setAllValid($event.target.checked)"
      >
      <span>All valid ({{ validCount }})</span>
    </label>


    <button class="btn btn--ghost btn--sm" @click="clearSelection" :disabled="selectedCount===0">
      Clear
    </button>
  </div>
<div class="bulkbar__right">
 <span class="tiny muted">
  Selected <strong>{{ selectedActionableCount }}</strong> / {{ validCount }} valid
  <span class="hide-sm"> · {{ analysis.withdrawals.length }} rows</span>
</span>

 
<button
  class="btn btn--primary"
  :disabled="selectedActionableCount===0 || bulkBusy"
  @click="beginBulkSubmit"
  :title="selectedActionableCount===0 ? 'No rows selected' : 'Submit all checked to Analysis'"
>
  {{ bulkBusy ? 'Working…' : `Submit ${selectedActionableCount} checked` }}
</button>
<div class="sep hide-sm"></div>
<!-- Bulk bar action -->
<button
  class="btn btn--outline btn--sm"
  :disabled="eligibleShownCompleteCount === 0 || bundling"
  @click="bundleShownReports"
  :title="eligibleShownCompleteCount===0
    ? 'No completed results in the current view'
    : 'Create a ZIP with reports + images for all shown, up to 500'"
>
  <template v-if="!bundling">
    ⬇︎ Download all shown (.zip)
  </template>
  <template v-else>
    Preparing… {{ bundleProgress.done }}/{{ bundleProgress.total }}
  </template>
</button>
<span class="tiny muted" v-if="eligibleShownCompleteCount>0">
  Will include {{ Math.min(eligibleShownCompleteCount, bundleCap) }} item(s) from this view
</span>
<button
  v-if="bundling"
  class="btn btn--ghost btn--sm"
  @click="cancelBundling"
  title="Cancel bundling"
>Cancel</button>




</div>

</div>
<div v-if="bundling" class="tiny muted" style="margin-top:6px">
  Queued: {{ bundleProgress.total }} · Done: {{ bundleProgress.done }} · Skipped: {{ bundleProgress.skipped }} · Failed: {{ bundleProgress.failed }}
</div>
  <table v-else class="results"></table>
<!-- Wrap the table(s) so we can manage scroll affordances -->
<div class="results-wrap" ref="resultsWrap" @scroll="onResultsScroll">

  <table v-if="analysis && analysis.withdrawals?.length" class="results">
    <colgroup>
  <col style="width:52px" />    <!-- Select -->
  <col style="width:72px" />    <!-- Row -->
  <col style="width:110px" />   <!-- Network (smaller) -->
  <col class="col-tx" />        <!-- Tx Hash (flex) -->
  <col class="col-to" />        <!-- Destination (flex) -->
  <col style="width:140px" />   <!-- Type (slightly smaller) -->
  <col style="width:110px" />   <!-- Amount (smaller) -->
  <col style="width:62px" />    <!-- Asset (tighter) -->
  <col style="width:120px" />   <!-- Resolve (sticky-right 3) -->
  <col style="width:96px" />    <!-- Send (sticky-right 2) -->
  <col style="width:110px" />   <!-- Status (sticky-right 1) -->
</colgroup>

    <thead>
      <tr>
        <th style="width:46px">Select</th>
        <th style="width:80px">Row</th>
        <th style="width:140px">Network</th>

        <!-- Truncated headers are fine; content truncation happens in <td> -->
        <th class="th-tx">Tx Hash</th>
        <th class="th-to">Destination</th>
        <th style="width:140px">Type</th>
        <th style="width:150px">Amount</th>
        <th style="width:120px">Asset</th>

        <!-- Sticky-right action columns -->
        <th class="sticky-resolve">Actions</th>
        <th class="sticky-send">Send</th>
        <th class="sticky-status">Status</th>
      </tr>
    </thead>

    <tbody>
      <tr v-for="row in analysis.withdrawals" :key="row.key">
        <td>
          <input
            type="checkbox"
            v-model="row._sel"
            :disabled="!isRowSelectable(row)"
            :title="rowIsActionable(row)
              ? (canRowRunResolve(row) && !canSendDirect(row)
                  ? 'Include this row (will resolve first)'
                  : 'Include this row')
              : 'Needs info or unsupported'"
          />
        </td>

        <td>#{{ row.rowIndex + 1 }}</td>
        <td>{{ row.network }}</td>

        <!-- Tx Hash: truncate visually, copy full on dblclick or Copy button -->
       <!-- Tx Hash cell -->
<td class="mono cell-clip td-tx" :title="row.txHash || '—'">
  <span class="truncate">{{ row.txHash || '—' }}</span>
  <button v-if="row.txHash"
          class="copychip"
          @click.stop="copyFull(row.txHash, 'Transaction hash copied')"
          aria-label="Copy full transaction hash">Copy</button>
</td>

<!-- Destination cell -->
<td class="mono cell-clip td-to" :title="row.toAddress || '—'">
  <span class="truncate">{{ row.toAddress || '—' }}</span>
  <button v-if="row.toAddress && row.toAddress !== '—'"
          class="copychip"
          @click.stop="copyFull(row.toAddress, 'Destination address copied')"
          aria-label="Copy full destination address">Copy</button>
</td>


        <td>{{ row.transferType }}</td>
        <td class="mono">{{ row.amountDisplay || '—' }}</td>
        <td>
          {{ (row.assetSymbol || '').toUpperCase() }}
          <span v-if="!isAssetResolvableForNetwork(row.assetSymbol, row.network)"
                class="pill pill--warn tiny" title="Not resolvable with current engine"
                style="margin-left:6px">NS</span>
        </td>

        <!-- Sticky-right 3: Resolve -->
        <td class="sticky-resolve">
          <template v-if="canRowRunResolve(row)">
            <button class="ghost"
                    :disabled="globalResolveBusy || row.resolveBusy"
                    @click="resolveDestination(row)"
                    :title="!row.amountRaw ? 'No amount to match' : 'Attempt to find destination by tx details'">
              {{ row.resolveBusy ? 'Resolving…' : 'Resolve' }}
            </button>
            <span v-if="row.resolveMsg" class="muted" style="margin-left:6px">{{ row.resolveMsg }}</span>
          </template>
          <template v-else-if="(!row.toAddress || row.toAddress === '—')">
            <span class="pill pill--warn tiny" title="Asset or network unsupported for automatic resolve">
              -
            </span>
          </template>
          <template v-else>
            <span class="muted">—</span>
          </template>
        </td>

        <!-- Sticky-right 2: Send -->
        <td class="sticky-send">
          <template v-if="row._history && row._history.exact">
          <!--  <span class="pill pill--ok tiny" title="You already ran this TX + destination">
             Already analyzed
            </span>-->
            <button
              v-if="row._history.best && String(row._history.best.status).toLowerCase() === 'complete' && row._history.best.result"
              class="ghost tiny"
              style="margin-left:6px"
              @click="openFullReportForRow(row)"
              title="Open prior analysis here"
            >Open</button>
            <span v-else class="muted tiny" style="margin-left:6px"
                  title="Prior request exists but is not complete or has no payload">Not ready</span>
          </template>
          <template v-else>
            <button v-if="shouldShowSendButton(row)"
                    class="ghost"
                    :disabled="!canSendToAnalysis(row)"
                    @click="sendToAnalysis(row)"
                    title="Prefill Analysis form with this row"
                    data-track="interpreter_send_to_analysis">Send</button>
            <div v-if="row._history && (row._history.txSeen || row._history.toSeen)"
                 class="muted tiny" style="margin-top:4px">
              <span v-if="row._history.txSeen" class="pill tiny" title="This TX was seen in your history">TX seen</span>
              <span v-if="row._history.toSeen" class="pill tiny" style="margin-left:4px"
                    title="This destination was seen in your history">Address seen</span>
            </div>
          </template>
        </td>

        <!-- Sticky-right 1: Status -->
        <td class="sticky-status">
          <template v-if="row._status === 'queued'">
            <span class="pill pill--ok tiny">Queued</span>
            <div v-if="row._requestId" class="tiny mono muted">#{{ row._requestId }}</div>
          </template>
          <template v-else-if="row._status === 'in progress'">
            <span class="pill pill--ok tiny">In Progress</span>
            <div v-if="row._requestId" class="tiny mono muted">#{{ row._requestId }}</div>
          </template>
          <template v-else-if="row._status === 'skipped'">
            <span class="pill pill--warn tiny">Skipped</span>
            <div v-if="row._reason" class="tiny muted">{{ row._reason }}</div>
          </template>
          <template v-else-if="row._status === 'error'">
            <span class="pill pill--danger tiny">Error</span>
            <div v-if="row._reason" class="tiny muted">{{ row._reason }}</div>
          </template>
          <template v-else-if="row._status === 'needs_input'">
            <span class="pill pill--warn tiny">Needs input</span>
            <div class="tiny muted">Open in Tool to complete</div>
          </template>
          <template v-else-if="row._status === 'complete'">
            <span class="pill pill--ok tiny">Complete</span>
          </template>
          <template v-else>
            <span class="tiny muted">—</span>
          </template>
        </td>
      </tr>
    </tbody>
  </table>

  <!-- Right fade to hint scroll when content overflows -->
  <div class="scroll-fade-right" v-show="showRightFade"></div>
</div>

</section>
<!-- Additional findings (Advanced only) -->
<section
  v-if="showAdvanced && extraFindings && extraFindings.byRow.length"
  class="panel lite"
  style="margin-top:12px"
>
  <h3 class="panel-title">Additional findings not in Interpreter Results</h3>
  <p class="muted" style="margin:6px 0 10px">
    Found <b>{{ extraFindings.uniqueHashes.length }}</b> extra hashes and
    <b>{{ extraFindings.uniqueAddresses.length }}</b> extra addresses elsewhere in the file.
    Grouped by the row they appear on.
  </p>

  <table class="results mini" style="width:100%">
    <thead>
      <tr>
        <th style="width:90px">Row</th>
        <th>Hashes (not shown above)</th>
        <th>Addresses (not shown above)</th>
        <th style="width:38%">Context</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="grp in extraFindings.byRow" :key="'xrow'+grp.rowIndex">
        <td>#{{ grp.rowIndex + 1 }}</td>

        <!-- Hashes with origin + network -->
        <td class="mono">
          <template v-if="(grp.hashTokens && grp.hashTokens.length) || grp.hashes.length">
            <div v-for="t in (grp.hashTokens && grp.hashTokens.length ? grp.hashTokens : grp.hashes.map(h=>({ value:h })) )"
                 :key="'h'+t.value"
                 class="token-line">
              <span>{{ t.value }}</span>
              <span v-if="t.net" class="badge">{{ t.net }}</span>
              <span v-if="t.header" class="muted">in {{ t.header }}</span>
              <span v-else-if="t.colIndex != null" class="muted">in {{ colName(t.colIndex) }}</span>
              <span v-if="t.source" class="chip chip--ghost">{{ t.source }}</span>
            </div>
          </template>
          <span v-else class="muted">—</span>
        </td>

        <!-- Addresses with origin + network -->
        <td class="mono">
          <template v-if="(grp.addrTokens && grp.addrTokens.length) || grp.addresses.length">
            <div v-for="t in (grp.addrTokens && grp.addrTokens.length ? grp.addrTokens : grp.addresses.map(a=>({ value:a })) )"
                 :key="'a'+t.value"
                 class="token-line">
              <span>{{ t.value }}</span>
              <span v-if="t.net" class="badge">{{ t.net }}</span>
              <span v-if="t.header" class="muted">in {{ t.header }}</span>
              <span v-else-if="t.colIndex != null" class="muted">in {{ colName(t.colIndex) }}</span>
              <span v-if="t.source" class="chip chip--ghost">{{ t.source }}</span>
            </div>
          </template>
          <span v-else class="muted">—</span>
        </td>

        <!-- Row-level analysis note -->
        <td>
          <div class="muted" v-if="grp.contextNote">{{ grp.contextNote }}</div>
          <div class="muted" v-else>Not enough context in this row.</div>
        </td>
      </tr>
    </tbody>
  </table>
</section>



    </section>
   <!-- Inline Prior Analysis Modal -->
<teleport to="body">
  <transition name="fade">
    <div
      v-if="inlineReportOpen"
      class="modal-overlay modal-overlay--floating"
      role="dialog"
      aria-modal="true"
      @click.self="closeInlineReport"
    >
      <section class="modal modal--report" role="document" aria-labelledby="inline-report-title">
        <header class="modal__head">
          <h2 id="inline-report-title" class="card__title">Prior analysis</h2>
          <button class="close" @click="closeInlineReport" aria-label="Close">×</button>
        </header>

        <div class="modal__body">
          <div class="report__meta">
            <div class="meta__row">
              <span class="label">Request ID</span>
              <span class="value mono">{{ inlineReportReq?.request_id }}</span>
            </div>
            <div class="meta__row">
              <span class="label">Transaction</span>
              <span class="value mono truncate" :title="inlineReportReq?.transaction_hash">
                {{ inlineReportReq?.transaction_hash }}
              </span>
            </div>
            <div class="meta__row" v-if="inlineReportReq?.to_address">
              <span class="label">To</span>
              <span class="value mono truncate" :title="inlineReportReq?.to_address">
                {{ inlineReportReq?.to_address }}
              </span>
            </div>
            <div class="meta__row">
              <span class="label">Mode</span>
              <span class="value">{{ inlineReportReq?.mode === 'normal' ? 'Advanced' : 'Normal' }}</span>
            </div>
            <div class="meta__row">
              <span class="label">Status</span>
              <span class="value">
                <span class="pill pill--ok tiny">complete</span>
              </span>
            </div>
          </div>

          <div class="report__body">
            <div class="report__text">
              <div
                v-for="(line, idx) in inlineReportLines"
                :key="'ir-'+idx"
                class="report-line"
              >
                <span>{{ line.text }}</span>
              </div>

              <details class="raw-report" style="margin-top:10px">
                <summary>{{ inlineReportIsAdvanced ? 'Raw data (TSV)' : 'Raw report' }}</summary>
                <pre>{{ inlineReportReq?.result }}</pre>
              </details>
            </div>
          </div>
        </div>

        <footer class="modal__actions">
          <router-link
            class="btn btn--ghost btn--sm"
            :to="{ path:'/AnalysisTool', query: inlineReportReq?.request_id ? { open: String(inlineReportReq.request_id) } : {} }"
          >
            View in Analysis
          </router-link>
          <button class="btn btn--primary btn--sm" @click="closeInlineReport">Close</button>
        </footer>
      </section>
    </div>
  </transition>
</teleport>


<!-- Runner Modal (simulated conveyor) -->
<teleport to="body">
  <transition name="fade">
    <div
      v-if="runnerVisible"
      class="modal-overlay modal-overlay--floating"
      role="dialog"
      aria-modal="true"
      @click.self="onRunnerBackdrop"
      tabindex="-1"
      ref="runnerOverlay"
    >
      <section class="modal modal--runner" role="document" aria-labelledby="runner-title">
        <header class="modal__head">
          <h2 id="runner-title" class="card__title">Processing submissions</h2>

          <div class="runner__head-actions">
            <button
              class="btn btn--ghost btn--sm"
              ref="runnerPauseBtn"
              @click="toggleRunnerPause"
              :disabled="runnerState==='complete' || runnerState==='cancelled'"
            >
              {{ runnerPaused ? 'Resume' : 'Pause' }}
            </button>

            <button
              class="btn btn--ghost btn--sm"
              @click="cancelRunner"
              :disabled="runnerState==='complete' || runnerState==='cancelled'"
            >
              Cancel
            </button>

            <button
              class="btn btn--ghost btn--sm"
              v-if="runnerState==='complete' || runnerState==='cancelled'"
              @click="closeRunnerModal"
            >
              Close
            </button>

            <button
              type="button"
              class="close"
              :disabled="runnerState!=='complete' && runnerState!=='cancelled'"
              @click="closeRunnerModal"
              aria-label="Close"
              title="Close"
            >×</button>
          </div>
        </header>

        <!-- State banners -->
        <div
          v-if="runnerState==='paused'"
          class="runner__banner runner__banner--paused"
          role="status"
        >
          Paused — processing is temporarily halted.
        </div>
        <div
          v-else-if="runnerState==='cancelled'"
          class="runner__banner runner__banner--cancelled"
          role="status"
        >
          Cancelled — remaining items were skipped.
        </div>
        <div
          v-else-if="runnerState==='complete'"
          class="runner__banner runner__banner--done"
          role="status"
        >
          Complete — {{ runnerStats.ok }} ok, {{ runnerStats.failed }} failed, {{ runnerStats.skipped }} skipped.
        </div>

        <!-- Belt -->
        <div class="runner__belt" :class="{'is-paused': runnerPaused, 'is-stopped': runnerState!=='running'}">
          <div class="runner__belt-bg" aria-hidden="true"></div>

          <!-- Wrap to grid; scroll vertically for hundreds -->
          <div class="runner__grid" ref="runnerGrid">
            <div
              v-for="(job, idx) in runnerJobs"
              :key="job.id"
              class="runner__box"
              :class="[
                'phase--' + job.phase,
                { 'is-active': idx === runnerActiveIndex }
              ]"
              :title="jobHoverTitle(job)"
              :ref="el => setJobRef(el, idx)"
            >
              <div class="runner__badge">
                #{{ job.row?.rowIndex != null ? (job.row.rowIndex+1) : '—' }}
              </div>
              <div class="runner__phase">{{ phaseLabel(job.phase) }}</div>
              <div class="runner__msg tiny muted" v-if="job.msg">{{ job.msg }}</div>
            </div>
          </div>
        </div>

        <!-- Progress + counts -->
        <div class="runner__meta">
          <div class="runner__progress">
            <div class="runner__bar" :style="{ width: runnerProgress + '%' }"></div>
          </div>
          <div class="runner__counts tiny muted">
            {{ runnerStats.ok }} ok · {{ runnerStats.failed }} failed · {{ runnerStats.skipped }} skipped · {{ runnerStats.total }} total
          </div>
        </div>

        <!-- Log -->
        <details class="runner__log">
          <summary>Event log</summary>
          <ul>
            <li v-for="(ln, i) in runnerLog" :key="'log'+i" class="mono tiny">{{ ln }}</li>
          </ul>
        </details>

        <footer class="modal__actions">
          <button
            type="button"
            class="btn btn--ghost"
            v-if="runnerState==='running'"
            @click="toggleRunnerPause"
          >
            {{ runnerPaused ? 'Resume' : 'Pause' }}
          </button>

          <button
            type="button"
            class="btn btn--ghost"
            v-if="runnerState==='running' || runnerState==='paused'"
            @click="cancelRunner"
          >
            Cancel
          </button>

          <button
            type="button"
            class="btn btn--primary"
            v-if="runnerState==='complete' || runnerState==='cancelled'"
            @click="closeRunnerModal"
          >
            Close
          </button>
        </footer>
      </section>
    </div>
  </transition>
</teleport>


<!-- Bulk confirmation modal (single source of truth) -->
<teleport to="body">
  <transition name="fade">
  <div
      v-if="showBulkCreditModal"
      class="modal-overlay modal-overlay--floating"
      role="dialog"
      aria-modal="true"
      @click.self="onBulkModalCancel"        
      tabindex="-1"                           
      ref="bulkModalOverlay"                  
    >
      <section class="modal modal--credits" role="document">
        <header class="modal__head">
          <h2 class="card__title">Confirm submission</h2>
          <button
            type="button"
            class="close"
            ref="bulkCloseBtn"  
            @click="onBulkModalCancel"
            aria-label="Close"
          >×</button>
        </header>

        <div class="modal__body">
          <!-- Busy -->
          <div v-if="creditModalBusy" class="submitting">
            <div class="spinner md"></div>
            <p class="small muted">Preparing your summary…</p>
          </div>

          <!-- Error -->
          <div v-else-if="creditModalError">
            <p class="error">{{ creditModalError }}</p>
          </div>
<!-- Summary -->
<template v-else>
<p>
  You’re about to submit
  <strong>{{ modalSelectedCount }}</strong>
  transaction{{ modalSelectedCount === 1 ? '' : 's' }} for analysis.
</p>


  <!-- Mode choice affects pricing -->
 <div class="field inline-row" style="margin:10px 0 6px">
  <div class="field inline" style="margin:0">
    <label for="bulk-mode">Mode</label>
    <select
      id="bulk-mode"
      v-model="bulkMode"
      :disabled="creditModalBusy"
      @change="onBulkModeChange"
      aria-label="Batch mode"
    >
      <option value="bucket">Normal</option>
      <option value="normal">Advanced</option>
    </select>
  </div>

  <div class="field inline" style="margin:0">
    <label for="bulk-hop">Hop logic</label>
    <select
      id="bulk-hop"
      v-model="bulkHopLogic"
      :disabled="creditModalBusy"
      aria-label="Batch hop logic"
    >
      <option value="fifo">FIFO</option>
      <option value="lifo">LIFO</option>
      <option value="next_out">Next Out</option>
    </select>
  </div>
</div>


  <div class="summary-grid">
    <!-- Account status -->
    <div class="summary-row">
      <span class="label">Account</span>
      <span class="value">
        <template v-if="isUnlimitedUser">
          Unlimited access
        </template>
        <template v-else-if="isSubscriptionUser">
          Subscription ({{ subscriptionDays }} day{{ subscriptionDays === 1 ? '' : 's' }} remaining)
        </template>
        <template v-else>
          Credit balance: <strong class="mono">{{ creditBalance ?? '—' }}</strong>
        </template>
      </span>
    </div>

    <!-- Estimated unit price -->
    <div class="summary-row" v-if="!isUnlimitedUser && !isSubscriptionUser">
      <span class="label">Per-request cost</span>
      <span class="value">
        <template v-if="bulkUnitPriceBusy">…</template>
        <template v-else-if="bulkUnitPriceErr">
          <span class="pill pill--warn tiny">Unavailable</span>
        </template>
        <template v-else>
          <strong>{{ effectiveUnitCost }}</strong> credit{{ effectiveUnitCost === 1 ? '' : 's' }}
          <span v-if="bulkPrice?.discountLabel" class="tiny muted"> ({{ bulkPrice.discountLabel }})</span>
        </template>

        
      </span><span v-if="discountIsActive && bulkPrice?.discountLabel" class="tiny muted">
  ({{ bulkPrice.discountLabel }})
</span>

    </div>

    <!-- Estimated total -->
    <div class="summary-row" v-if="!isUnlimitedUser && !isSubscriptionUser">
      <span class="label">Estimated total</span>
      <span class="value">
        <template v-if="bulkUnitPriceBusy">…</template>
        <template v-else-if="bulkUnitPriceErr">
          <span class="pill pill--warn tiny">Unavailable</span>
        </template>
        <template v-else>
          Up to <strong>{{ estimatedBulkCost }}</strong> credit{{ estimatedBulkCost === 1 ? '' : 's' }}
        </template>
      </span>
    </div>

    <!-- Insufficient credit warning -->
    <div class="summary-row" v-if="insufficientCredits">
      <span class="label">Status</span>
      <span class="value">
        <span class="pill pill--warn tiny">Insufficient credits</span>
        <span class="tiny muted" style="margin-left:6px">
          Needed {{ estimatedBulkCost }}, available {{ creditBalance ?? 0 }}.
        </span>
      </span>
    </div>
  </div>

  <p class="tiny muted" style="margin-top:10px">
    Only successfully enqueued requests will deduct credits. Items that require more information or fail prechecks are skipped without charge.
  </p>
<!--
  <div class="disclaimer__ack">
    <label class="checkbox">
      <input type="checkbox" v-model="skipBulkCreditConfirm">
      <span>Do not show this reminder again on this device.</span>
    </label>
  </div>-->
</template>

        </div>

        <footer class="modal__actions">
          <button type="button" class="btn btn--ghost" @click="onBulkModalCancel">
            Cancel
          </button>

          <router-link
            v-if="insufficientCredits && !isSubscriptionUser && !isUnlimitedUser"
            class="btn btn--ghost"
            to="/products"
          >
            Get more access
          </router-link>

    <button
  type="button"
  class="btn btn--primary"
  :disabled="creditModalBusy || insufficientCredits || !modalSelectedCount || bulkUnitPriceBusy || (!!bulkUnitPriceErr && !isUnlimitedUser && !isSubscriptionUser)"
  @click="onBulkModalConfirm"
>
  Confirm and submit
</button>


        </footer>
      </section>
    </div>
  </transition>
</teleport>

      <teleport to="body">
  <div
    v-if="showReportModal"
    class="report-overlay"
    @click.self="closeReport"
  >
    <div
      class="modal modal--report"
      role="dialog"
      aria-modal="true"
      aria-label="Analysis report"
      ref="reportModal"
    >
      <div class="modal__head">
        <strong>Analysis report</strong>
        <button
  class="close"
  @click="closeReport"
  aria-label="Close"
  data-track="tool_modal_close_report"
>×</button>
      </div>

      <div class="modal__body">
        <!-- Context header -->
        <div class="report__meta">
          <div class="meta__row">
            <span class="label">Request ID</span>
            <span class="value mono">{{ reportRequest.request_id }}</span>
          </div>
          <div class="meta__row">
            <span class="label">Transaction</span>
            <span class="value mono truncate" :title="reportRequest.transaction_hash">
              {{ reportRequest.transaction_hash }}
            </span>
          </div>
          <div class="meta__row" v-if="reportRequest.to_address">
            <span class="label">To</span>
            <span class="value mono truncate" :title="reportRequest.to_address">
              {{ reportRequest.to_address }}
            </span>
          </div>
          <div class="meta__row">
            <span class="label">Mode</span>
            <span class="value">{{ getModeDisplay(reportRequest.mode) }}</span>
          </div>
          <div class="meta__row">
            <span class="label">Requested</span>
            <span class="value">{{ reportLocalTime(reportRequest.request_time) }}</span>
          </div>
          <div class="meta__row">
            <span class="label">Status</span>
            <span class="value">
              <span class="pill" :class="reportRequest.status === 'complete' ? 'pill--ok' : 'pill--warn'">
                {{ reportRequest.status }}
              </span>
            </span>
          </div>
        </div>

       <!-- Pretty report body -->
<div class="report__body">
  <div class="report__text">

    <!-- Normal Mode (existing behavior) -->
    <template v-if="!isAdvancedReport">
      <div
        v-for="(line, idx) in humanReport"
        :key="'n-'+idx"
        class="report-line"
      >
        <img v-if="line.icon" :src="line.icon" alt="" class="flag" />
        <span>{{ line.text }}</span>
      </div>

      <details class="raw-report">
        <summary>Raw report</summary>
        <pre>{{ reportRequest.result }}</pre>
      </details>
    </template>

    <!-- Advanced Mode (new UX) -->
    <template v-else>
      <div
        v-for="(line, idx) in humanReport"
        :key="'a-'+idx"
        class="report-line"
      >
        <img v-if="line.icon" :src="line.icon" alt="" class="flag" />
        <span>{{ line.text }}</span>
      </div>

     <div class="advanced-actions">
  <button
    class="btn btn--primary btn--sm"
    @click="ensureAdvancedSheetLoaded"
    data-track="advanced_open_spreadsheet"
  >
    Open spreadsheet preview
  </button>

<button
  type="button"
  class="btn btn--ghost btn--sm copy-btn"
  :class="{ 'is-copied': copiedTSV }"
  :disabled="copyingTSV"
  @click="copyRawTSV"
  aria-live="polite"
  aria-atomic="true"
>
  <span v-if="!copiedTSV && !copyingTSV">Copy TSV</span>
  <span v-else-if="copyingTSV">Copying…</span>
  <span v-else>Copied</span>
</button>



  <button class="btn btn--ghost btn--sm" @click="downloadAdvanced('csv')" data-track="advanced_download_csv">
    Download .csv
  </button>
</div>

     <div
  v-if="showAdvancedSheet"
  class="spreadsheet-wrapper"
  style="margin-block:12px;"
>
  <SpreadsheetComponent
    ref="advancedSheet"
    :key="`adv-${reportRequest?.request_id || 'tmp'}`"
    :width="'100%'"
    :height="'420px'"
  />
</div>

      <details class="raw-report">
        <summary>Raw data (TSV)</summary>
        <pre>{{ reportRequest.result }}</pre>
      </details>
    </template>
  </div>
</div>


        <!-- Actions -->
        <div class="report__actions">
        <button
  class="btn btn--image btn--sm"
  @click="openScreenshotAndClose(reportRequest.request_id)"
  data-track="tool_modal_open_screenshot"
  :data-track-props="JSON.stringify({ id: reportRequest.request_id })"
>
  Open screenshot
</button>
<!--
        <button
  class="btn btn--ghost btn--sm copy-btn"
  :class="{ 'is-copied': copiedReport }"
  @click="copyReport"
  :disabled="copyingReport"
  data-track="tool_modal_copy_report"
  aria-live="polite"
  aria-atomic="true"
>
  <span class="copy-label-default">Copy text</span>
  <span class="copy-label-done">Copied</span>
</button>-->


<!-- DISCLAIMER (new) -->
<section class="report__disclaimer" :aria-expanded="disclaimerOpen ? 'true' : 'false'">
<details class="disclaimer" :open="disclaimerOpen" @toggle="disclaimerOpen = $event.target.open">
<summary class="disclaimer__summary">
<strong>Disclaimer</strong>
<span class="disclaimer__hint" aria-hidden="true">(click to {{ disclaimerOpen ? 'collapse' : 'expand' }})</span>
</summary>


<div class="disclaimer__card" role="group" aria-label="Report disclaimer text">
<p class="muted"><em>PathTracer — Important Notice & Limitations</em></p>


<ol class="disclaimer__list">
<li><strong>Open‑source & third‑party data.</strong> Reports are generated from publicly available blockchains, block explorers, and other third‑party/open‑source sources. These sources can be incomplete, inaccurate, or change without notice. PathTracer does not control these sources and cannot guarantee their content.</li>
<li><strong>Point‑in‑time snapshot.</strong> Each report reflects data available at the time it was produced. Subsequent blocks, chain reorganizations, amended labels, or API corrections may alter interpretations.</li>
<li><strong>No guarantee of completeness, accuracy, or timeliness.</strong> The report is provided on an <q>as is</q> and <q>as available</q> basis without warranties of any kind, express or implied.</li>
<li><strong>User responsibility.</strong> You are solely responsible for independently verifying the results, obtaining professional advice where appropriate, and determining how to use or interpret the report.</li>
<li><strong>No legal, financial, or accounting advice.</strong> PathTracer reports are technical analyses and are not a substitute for legal counsel, financial due diligence, or accounting review.</li>
<li><strong>Assumption of risk; limitation of liability.</strong> To the maximum extent permitted by law, PathTracer, its operators, and affiliates are not liable for any decisions, actions, losses, or damages (direct, indirect, incidental, consequential, or otherwise) arising from or related to use of a report.</li>
<li><strong>Jurisdiction & compliance.</strong> You represent that your use complies with all applicable laws and you have any necessary authorizations to analyze, process, or store the data involved.</li>
<li><strong>Use of the term “Report.”</strong> “Report” includes the narrative text, flags, icons, screenshots, diagrams, raw output, and any derived visualizations packaged with this modal or export.</li>
<li><strong>Updates.</strong> PathTracer may update its methods, datasets, and this disclaimer at any time. </li>
<li><strong>Contact.</strong> For questions about methodology or to report suspected data errors, contact the PathTracer team.</li>
</ol>


<p class="muted">© {{ new Date().getFullYear() }} PathTracer. All rights reserved.</p>
</div>
</details>



</section>
        </div>
      </div>
    </div>
  </div>
</teleport>


  </main>
</template>
<script>
import SpreadsheetComponentAdvancedB from '@/components/SpreadsheetComponentAdvancedB.vue';
import axios from 'axios';
import SpreadsheetComponent from "@/components/SpreadsheetComponent.vue"; // for Advanced mode preview
import JSZip from 'jszip';

import AuthService from '@/services/AuthService';
import {
  inferNetwork,
  inferNetworkFromAddrOrAsset,
  parseAmount,

  isCsvLike,
 
} from '@/utils/interpreter_utils.js';
import {
  isLikelyBinary,
  extractFirstAddressFromText,
  termSets,
} from '@/utils/txn_interpreter_core.js';
import {
  analyzeTabularTransactions,
  parseTabularFile,
} from '@/services/txnImport/tabularTxnImportService';
const RESOLVE_URL = '/api/etherscan/resolve_destination_by_hash.php'; 
const LOOKUP_TXHASH_URL = '/api/etherscan/lookup_txhash_by_addr_amount.php';
// Address requirement check (same as Analysis Tool)
const TO_ADDR_CHECK_URL = '/api/etherscan/check_to_address_needed.php';

// Existence checks (same paths you used in the Analysis Tool)
const CHECK_ETH_URL  = '/api/etherscan/check_eth_tx_exists.php';
const CHECK_BTC_URL  = '/api/etherscan/check_btc_tx_exists.php';
const CHECK_TRX_URL  = '/api/etherscan/check_tron_tx_exists.php';

// Accept if asset is unknown, or clearly BTC / ETH / TRX, or a well-known ERC20.
// Tweak this allowlist as you expand support on the backend.
const CORE_ASSETS = new Set(['BTC', 'ETH', 'USDT', 'TRX']);
// A conservative ERC-20 allowlist (stablecoins + blue chips you already handle)
const WELL_KNOWN_ERC20 = new Set([
  'USDT','USDC','DAI','WBTC','WETH','LINK','UNI','MKR','AAVE','TUSD','PAXG'
]);
// Cache per run to avoid duplicate calls
const _txFactsCache = new Map();



// Optional explicit blocklist to be extra safe (not EVM/BTC/TRON)
const EXPLICIT_UNSUPPORTED = new Set(['XRP','SOL','ADA','BNB','XLM','ALGO','DOT','LTC','BCH','DOGE','MATIC','ATOM']);
function normalizeTicker(sym) {
  if (!sym) return '';
  const s = String(sym).toUpperCase().trim();
  const core = s.split(/[-/\s()_]+/)[0] || s;   // e.g., USDT-TRON -> USDT
  return core.replace(/[^A-Z0-9]/g, '');
}
// New: cope with more variants and typos; returns 'bitcoin' | 'ethereum' | 'tron' | null
function normalizeNetworkTag(netRaw) {
  if (!netRaw) return null;
  const n = String(netRaw).trim().toLowerCase();

  // Bitcoin family
  if (['btc','bitcoin','btc-like','btclike','bitcoin-like','bitcoinlike','utxo'].includes(n)) return 'bitcoin';
  if (n.includes('btc')) return 'bitcoin'; // lenient

  // Ethereum / EVM family
  if (['eth','ethereum','eth-like','ethlike','evm'].includes(n)) return 'ethereum';
  if (n.includes('evm') || n.includes('eth')) return 'ethereum'; // lenient (polygon/bsc still route to EVM path)

  // Tron family
  if (['tron','trx','trc20','tron-like','tronlike'].includes(n)) return 'tron';
  if (n.includes('tron')) return 'tron'; // lenient

  return null;
}
// If network label is flaky/missing, infer from asset
function coerceNormalizedNetworkFromRow(row) {
  const netNorm = normalizeNetworkTag(row?.network);
  if (netNorm) return netNorm;

  const t = normalizeTicker(row?.assetSymbol || '');
  if (t === 'BTC') return 'bitcoin';
  if (t === 'TRX') return 'tron';
  if (t === 'ETH' || WELL_KNOWN_ERC20.has(t)) return 'ethereum';

  return null; // unknown
}
function isWellKnownErc20(sym) {
  const t = normalizeTicker(sym);
  return WELL_KNOWN_ERC20.has(t);
}
function isCoreAsset(sym) {
  const t = normalizeTicker(sym);
  return CORE_ASSETS.has(t);
}
function isExplicitlyUnsupported(sym) {
  const t = normalizeTicker(sym);
  return EXPLICIT_UNSUPPORTED.has(t);
}// --- Actionability (used by resolve buttons, checkboxes, and bulk counts) ---
function canRowRunResolve(row) {
  if (!row) return false;
  if (row._status === 'queued' || row._status === 'complete') return false;
  if (row.toAddress && row.toAddress !== '—') return false; // nothing to resolve
  if (!row.txHash || row.amountRaw == null) return false;

  const net = coerceNormalizedNetworkFromRow(row);
  if (!net) return false;
  return isAssetResolvableForNetwork(row.assetSymbol, net);
}

function canRowLookupHash(row) {
  if (!row) return false;
  if (row._status === 'queued' || row._status === 'complete') return false;
  if (row.txHash) return false;
  if (!row.canLookupTxHash) return false;

  // Your lookup endpoint requires toAddress + amount + asset
  const c = row.lookupContext || {};
  const to = c.toAddress || row.toAddress;
  const amt = c.amountAbs;
  const asset = row.assetSymbol || c.assetSymbol || '';
  if (!to || amt == null || !asset) return false;

  const net = coerceNormalizedNetworkFromRow(row);
  if (!net) return false;
  return isAssetResolvableForNetwork(asset, net);
}

/**
 * Asset support rules per network.
 * - Empty asset => treat as native coin of the network (allowed).
 * - Ethereum => allow well-known ERC20s and ETH.
 * - Tron => allow TRX and USDT (extend later).
 * - Bitcoin => allow BTC.
 */
function isAssetResolvableForNetwork(assetSym, netRaw) {
  const net = normalizeNetworkTag(netRaw);
  const t = normalizeTicker(assetSym);

  if (isExplicitlyUnsupported(t)) return false;

  // No symbol -> native asset on supported nets
  if (!t) return net === 'bitcoin' || net === 'ethereum' || net === 'tron';

  // Core tickers are broadly OK (guarded by the net test below)
  if (isCoreAsset(t)) {
    if (net === 'bitcoin' && t === 'BTC') return true;
    if (net === 'ethereum' && (t === 'ETH' || isWellKnownErc20(t))) return true;
    if (net === 'tron' && (t === 'TRX' || t === 'USDT')) return true;
    // If core but mismatched (e.g., USDT on bitcoin), return false.
    return false;
  }

  if (net === 'ethereum') return isWellKnownErc20(t);          // ERC20s you handle
  if (net === 'tron')      return (t === 'TRX' || t === 'USDT');
  if (net === 'bitcoin')   return (t === 'BTC');

  return false;
}




function isSupportedNetworkTag(netRaw) {
  const n = normalizeNetworkTag(netRaw);
  return n === 'bitcoin' || n === 'ethereum' || n === 'tron';
}



export default {
  name: 'TxnHistoryInterpreter',
  components: { SpreadsheetComponentAdvancedB, SpreadsheetComponent  },
 props: {
  
    treatMethodTransferAsWithdrawal: { type: Boolean, default: true }
  },
  data() {
    return {
      bundling: false,
bundleProgress: { total: 0, done: 0, skipped: 0, failed: 0 },
bundleErrors: [],
bundleDelayMs: 300,     // small pause between network fetches
bundleConcurrency: 2,   // keep this low to protect the server
bundleAbort: null,     // AbortController for axios (modern)
bundleCap: 500,
bundleSizeBudgetBytes: 300*1024*1024,
bundleBytesSoFar: 0,
bundleManifest: [],
       pollTimer: null,
    pollCfg: {
      fastMs: 2000,     // first N cycles
      normalMs: 5000,   // default cadence
      slowMs: 10000,    // when things are steady
      fastCycles: 12,   // 12*2s = ~24s of fast polling after analysis or runner
      hardRefreshEvery: 6, // every 6 polls do a deeper refresh
      maxErrors: 5
    },
    pollState: {
      enabled: false,
      cycles: 0,
      errors: 0,
      lastHardRefreshAt: 0
    },
      copyingTSV: false,
    copiedTSV: false,
    copiedTSVTimer: null,
screenshotPopupBlocked: null, // { requestId, url } | null
       // Report modal (full report)
      showReportModal: false,
      reportRequest: null,        // the chosen request object (from history or after submit)
      showAdvancedSheet: false,   // controls preview mount
      advancedHeaders: [],
      advancedRows: [],
      advancedRowCount: 0,
      advancedColCount: 0,
      advancedDateRange: null,
      disclaimerOpen: true,
      copiedReport: false,
      copyingReport: false,



       inlineReportOpen: false,
    inlineReportReq: null,   // the request row from userRequests
    inlineReportLines: [],   // humanized lines for Normal/Advanced
    inlineReportIsAdvanced: false,
 enableBulkDryRun: false, 
lastFocusedEl: null,
showGuide: true,
  // Account state
      canUseNormalMode: false, 
    creditBalance: null,
    isSubscriptionUser: false,
    isUnlimitedUser: false,
    subscriptionExpiresAt: null,
    subscriptionDays: 0,
    role: null,
 runnerCheckCooldownMs: 600,     // between quality-check API calls
      // Runner (simulated batch conveyor)
      stagedRows: [], // snapshot used by the confirm modal + runner
     runnerVisible: false,
    runnerJobs: [],              // [{ id, row, phase, msg }]
    runnerActiveIndex: -1,
    runnerPaused: false,
    runnerCancelled: false,
    runnerCooldownMs: 1200,
    runnerStats: { total: 0, ok: 0, failed: 0, skipped: 0 },
    runnerLog: [],
    runnerState: 'idle',         // 'idle' | 'running' | 'paused' | 'cancelled' | 'complete'
    runnerJobEls: new Map(),    // idx -> element (for auto-scroll)
    
    runnerSubmitCooldownMs: 650,   // between real submit calls
    runnerResolveCooldownMs: 1400, // pause after each real resolve

    // Bulk modal state
     bulkMode: 'bucket',        // 'bucket' = Normal, 'normal' = Advanced (keep same mapping)
     
    bulkHopLogic: 'fifo',  
     bulkUnitPriceBusy: false,
    bulkUnitPriceErr: '',
    creditModalBusy: false,
    creditModalError: '',
   bulkSummary: { count: 0, needed: 0, available: 0 },
    skipBulkCreditConfirm: false, // device-level toggle

    // Internal cache from dry-run
    bulkConfirmPayload: null,

         bulkBusy: false,
    bulkPricingBusy: false,
    bulkPricingErr: '',
    bulkPrice: null,        // { unitCost, discountCost?, label? }  (reuse your pricing shape)
    showBulkCreditModal: false,
      extraFindings: null, // { byRow: [{rowIndex, hashes[], addresses[]}], uniqueHashes[], uniqueAddresses[] }
 isLoggedIn: false,          // simple local flag
    userRequests: [],           // raw fetched requests
    historyIndex: null,         // { exact:Set, byTx:Map, byTo:Map, total:number, ts:number }
      // security + parsing
      neutralizeFormulas: true,
  showAdvanced: false,        // new master toggle (default: simple mode)
    autoAnalyzeOnLoad: true,    // run analysis as soon as a CSV is loaded
      trimWhitespace: true,  
       assumeAllWithdrawalsWhenNoType: true, // gate for the new behavior (default true for your tester)
 strictAnchorValidation: true,            // NEW: turn on/off anchor validation
      MAX_CELL_CHARS: 10_000,
// rate-limit state
resolveQueue: [],            // FIFO of rows to resolve
resolveProcessing: false,    // true while a resolve is in flight
resolveCooldownMs: 2200,     // gap between resolves (tune as desired) // was 1200
maxQueuedResolves: 25,       // optional safety cap

      // UI state
      errorMessage: '',
      analyzeError: '',
      isAnalyzing: false,
      showOverridesPanel: false,

      // drag UI
      isDragOver: false,
      isDragInvalid: false,
      dragDepth: 0,
      hoverFilename: '',

      // parse limits
      MAX_BYTES: 5 * 1024 * 1024,
      MAX_ROWS: 20000,
      MAX_COLS: 200,

      // analysis
      SAMPLE_ROWS: 500,
      analysis: null,
      headerRowIdx: -1,
      headerRaw: [],
      headerNorm: [],
      detectionStats: { txHashScore: [], addrScore: [], amountScore: [] },

      selectedCols: { txHash: -1, toAddress: -1, transferType: -1, asset: -1, amount: -1 },
      useAmountFallback: false,

      // thresholds to avoid false positives
      MIN_TXHASH_SCORE: 2,
      MIN_ADDR_SCORE: 3,
      MIN_ADDR_RATIO: 0.04,

      // amount scoring knobs
      MIN_AMOUNT_SCORE: 1.5,          // floor to accept an amount column
      AMT_NUMERIC_WEIGHT: 5.0,        // weight for numeric ratio
      AMT_USD_PENALTY: 6.0,           // strong penalty for usd-like headers
      AMT_NATIVE_PENALTY: 4.0,        // penalty for native-like headers (often fiat valuation)
      AMT_STRONG_AMOUNT_BONUS: 6.0,   // strong bump if header is exactly "amount"
      AMT_SIGN_DIVERSITY_WEIGHT: 4.0, // prefer columns with both + and - values
      AMT_TYPE_CORR_WEIGHT: 5.0       // prefer columns whose sign matches transfer kind
    };
  },
mounted() {
   this.$nextTick(() => this.onResultsScroll());
  // Optionally: also update on window resize
  window.addEventListener('resize', this.onResultsScroll);
  // Try to pull the user’s request history in the background
  this.initHistoryCache();
     window.addEventListener('keydown', this.onGlobalKeydown); 
document.addEventListener('visibilitychange', this._onVisChange);
     document.body.classList.add('route-tool');

 AuthService.getUserRole().then(data => {
    this.userRole = data.role;
    this.canUseNormalMode = data.canUseNormalMode; // Set permission here
  }).catch(error => {
    console.error("Error fetching user role:", error);
    this.showNotification('Error fetching permissions, please reload.', 'error');
  });

  
  // restore guide preference
  try {
    const hidden = localStorage.getItem('csvhelper.guide.hidden');
    if (hidden === '1') this.showGuide = false;
  } catch (e) { /* non-fatal */ console.log("catch"); }


   const openId = this.$route?.query?.open;
  if (openId) {
    // find it in current page or fetch the page containing it
    this.tryOpenRequestById(String(openId));
  }
  
},
beforeUnmount() {
  
  window.removeEventListener('resize', this.onResultsScroll);

    window.removeEventListener('keydown', this.onGlobalKeydown); // NEW
  if (this.copiedTSVTimer) clearTimeout(this.copiedTSVTimer);

  
  this._stopInterpreterPolling();
  document.removeEventListener('visibilitychange', this._onVisChange);

    document.body.classList.remove('route-tool');

  },
created() {
    // Device preference
  this.skipBulkCreditConfirm = (localStorage.getItem('pt_skip_bulk_credit_confirm') === '1');

  // If you already set Authorization globally in a layout, you can skip this:
  const tok = localStorage.getItem('token');
  if (tok) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${tok}`;
    this.isLoggedIn = true;
    this.refreshUserCredits(); // -> fills subscription/credits
  }

  const raw = localStorage.getItem('assumeAllWithdrawalsWhenNoType');
  if (raw !== null) this.assumeAllWithdrawalsWhenNoType = (raw === 'true');
},
watch: {
   selectedCount(v) {
    if (this.showBulkCreditModal && v === 0) this.onBulkModalCancel();
  },
  
  skipBulkCreditConfirm(v) {
    localStorage.setItem('pt_skip_bulk_credit_confirm', v ? '1' : '0');
  },
  assumeAllWithdrawalsWhenNoType(v) {
    localStorage.setItem('assumeAllWithdrawalsWhenNoType', String(v));
  }
},
  computed: {
visibleWithdrawalRows() {
 return (this.analysis && Array.isArray(this.analysis.withdrawals))
 ? this.analysis.withdrawals
: [];
},

eligibleShownCompleteCount() {
let n= 0; 
for (const row of this.visibleWithdrawalRows) {
const req = this._bestReqForRow(row);
const st = String(req?.status || '').toLowerCase();
if (st === 'complete' && !!req?.result) n++;
if (n >= this.bundleCap) break; // respect cap
} 
return n;
},

  selectedRows() {
    const rows = this.analysis?.withdrawals || [];
    return rows.filter(r => r._sel);
  },  

selectedCompleteCount() {
  // rows that have a “best” request with status complete
  return this.selectedRows.reduce((n, row) => {
    const req = this._bestReqForRow(row);
    const st = String(req?.status || '').toLowerCase();
    return n + (st === 'complete' && !!req?.result ? 1 : 0);
  }, 0);
},

      // Full report modal: decide Advanced vs Normal
    isAdvancedReport() {
    const mode = this.reportRequest?.mode;
    const modeSaysAdvanced = !!mode && mode !== 'bucket';

    const text = this.reportRequest?.result || "";
    const firstLine = text.split(/\r?\n/, 1)[0] || "";
    const looksTSV =
      firstLine.includes("\t") &&
      /(Label|Hash|FromAddress|ToAddress|Date)/i.test(firstLine);

    return modeSaysAdvanced || looksTSV;
  },
     parsedReport() {
      if (this.isAdvancedReport) return null;
      // Use your existing parseReport from Analysis Tool, or fallback to plain lines
      return this.parseReport
        ? this.parseReport(this.reportRequest?.result || "")
        : (this.reportRequest?.result || "").split(/\r?\n/);
    },

    humanReport() {
      if (!this.isAdvancedReport) {
        // Reuse your interpreter if available
        if (this.interpretReport) return this.interpretReport(this.parsedReport);
        // Fallback: show plain lines
        return (this.parsedReport || []).map(t => ({ text: String(t || "") }));
      }
      return this.buildAdvancedHumanReport();
    },
     selectedActionableRows() {
    const rows = this.analysis?.withdrawals || [];
    return rows.filter(r => r._sel && this.rowIsActionable(r));
  },
  selectedActionableCount() {
    return this.selectedActionableRows.length;
  },
       runnerActive() {
    return this.runnerVisible && this.runnerActiveIndex >= 0 && this.runnerActiveIndex < this.runnerJobs.length;
  },
  runnerProgress() {
    const done = Math.max(0, this.runnerStats.ok + this.runnerStats.failed + this.runnerStats.skipped);
    const total = Math.max(1, this.runnerStats.total);
    return Math.min(100, Math.round((done / total) * 100));
  },
    discountIsActive() {
  if (!this.bulkPrice) return false;
  if (this.bulkPrice.discountCost == null) return false;

  // Use server time if provided; otherwise local now
  const now = this.bulkPrice.serverNowUtc ? new Date(this.bulkPrice.serverNowUtc) : new Date();
  const startOk = !this.bulkPrice.discountStartsUtc || now >= new Date(this.bulkPrice.discountStartsUtc);
  const endOk   = !this.bulkPrice.discountEndsUtc   || now <= new Date(this.bulkPrice.discountEndsUtc);
  return startOk && endOk;
},

      // Choose the per-request cost the same way you did on the main tool's modal
effectiveUnitCost() {
  if (this.isUnlimitedUser || this.isSubscriptionUser) return 0;
  if (!this.bulkPrice) return 0;

  const base = this.bulkPrice.baseCost | 0;
  const disc = this.bulkPrice.discountCost != null ? (this.bulkPrice.discountCost | 0) : null;

  return this.discountIsActive && disc != null ? disc : base;
},

  estimatedBulkCost() {
    if (this.isUnlimitedUser || this.isSubscriptionUser) return 0;
    return (this.modalSelectedCount | 0) * (this.effectiveUnitCost | 0);
  },
    effectiveBulkCost() {
    if (!this.bulkPrice) return 0;
    const cost = (this.bulkPrice.totalCost ?? this.bulkPrice.discountCost ?? this.bulkPrice.baseCost ?? 0) | 0;
    // If covered by plan, show 0 in the label
    if (this.isUnlimitedUser || this.isSubscriptionUser) return 0;
    return cost;
  },
   insufficientCredits() {
    if (this.isUnlimitedUser || this.isSubscriptionUser) return false;
    if (this.bulkUnitPriceBusy || this.bulkUnitPriceErr) return false; // don’t block on fetch error
    const need = this.estimatedBulkCost | 0;
    const have = (this.creditBalance ?? 0) | 0;
    return need > have;
  },
     allSelected() {
    const rows = this.analysis?.withdrawals || [];
    if (!rows.length) return false;
    return rows.every(r => !!r._sel || !this.isRowSendable(r));
  },

validCount() {
  const rows = this.analysis?.withdrawals || [];
  return rows.filter(r => this.rowIsActionable(r)).length;
},
 // Only count selected rows that are also selectable
  selectedRowsSendable() {
    const rows = this.analysis?.withdrawals || [];
    return rows.filter(r => r._sel && this.isRowSelectable(r));
  },
  // If you want to keep the old name used in your template, alias it:
  selectedSendableCount() {
    return this.selectedActionableCount;
  },
   // Staged selection count used by the modal and credit math
  modalSelectedCount() {
    return this.stagedRows?.length || 0;
  },
allValidSelected() {
  const rows = this.analysis?.withdrawals || [];
  const valid = rows.filter(r => this.rowIsActionable(r));
  if (!valid.length) return false;
  return valid.every(r => !!r._sel);
},
  selectedCount() { return this.selectedRows.length; },
  sendableCount() {
    const rows = this.analysis?.withdrawals || [];
    return rows.filter(r => this.isRowSendable(r)).length;
  },
      globalResolveBusy() { return this.resolveProcessing || this.resolveQueue.length > 0; },
    dropzoneClass() {
      return {
        'dropzone--over': this.isDragOver && !this.isDragInvalid,
        'dropzone--invalid': this.isDragOver && this.isDragInvalid,
      };
    }
  },

  methods: {
     copyFull(value, okMsg = 'Copied') {
        const text = String(value || '');
        if (!text) return;
        if (navigator.clipboard?.writeText) {
          navigator.clipboard.writeText(text).then(() => {
            this.notify?.(okMsg, 'success');
          }).catch(() => {/* no-op */});
        } else {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.setAttribute('readonly', '');
          ta.style.position = 'absolute';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          this.notify?.(okMsg, 'success');
        }
      },
    
      onResultsScroll() {
        const wrap = this.$refs.resultsWrap;
        if (!wrap) return;
        const atRight = Math.abs((wrap.scrollLeft + wrap.clientWidth) - wrap.scrollWidth) < 1;
        this.showRightFade = !atRight;
      },
    // Maps internal -> user-facing
_modeDisplay(mode) {
  // current contract: anything not 'bucket' is advanced
  return (mode && mode !== 'bucket') ? 'advanced' : 'normal';
}
,
  // Build the list from visible rows, not checkboxes
  _collectShownPicks() {
    const out = [];
    const rows = this.visibleWithdrawalRows;
    for (const row of rows) {
      const req = this._bestReqForRow(row);
      if (!req) continue;
      const st = String(req.status || '').toLowerCase();
      if (st !== 'complete' || !req.result) continue;
      out.push({ row, req });
      if (out.length >= this.bundleCap) break;
    }
    return out;
  },

  async bundleShownReports() {
    const username = localStorage.getItem('username');
    const token = localStorage.getItem('token');
    if (!username || !token) {
      this.notify?.('Please sign in first.', 'error');
      return;
    }

    const picks = this._collectShownPicks();
    if (picks.length === 0) {
      this.notify?.('No completed results in the current view.', 'warn');
      return;
    }

    this.bundling = true;
    this.bundleErrors = [];
    this.bundleBytesSoFar = 0;
    this.bundleManifest = [];
    this.bundleProgress = { total: picks.length, done: 0, skipped: 0, failed: 0 };
    this.bundleAbort = new AbortController();

    const zip = new JSZip();

    const queue = [...picks];
    const workers = Math.max(1, Math.min(this.bundleConcurrency, queue.length));

    const runWorker = async () => {
      while (queue.length && this.bundling) {
        const item = queue.shift();

        try {
          // Optional size budget guard before each item (soft stop)
          if (this.bundleSizeBudgetBytes && this.bundleBytesSoFar > this.bundleSizeBudgetBytes) {
            this.bundleErrors.push({ rid: item?.req?.request_id, error: 'bundle-size-budget-exceeded' });
            this.bundleProgress.skipped++;
            continue;
          }

          const addedBytes = await this._bundleOne(zip, item);
          if (Number.isFinite(addedBytes)) this.bundleBytesSoFar += addedBytes;
        } catch (err) {
          this.bundleErrors.push({ rid: item?.req?.request_id, error: String(err) });
          this.bundleProgress.failed++;
        } finally {
          this.bundleProgress.done++;
        }

        await this._delay(this.bundleDelayMs);
      }
    };

    try {
      await Promise.all(Array.from({ length: workers }, runWorker));

      // errors manifest (if any)
      if (this.bundleErrors.length) {
        const ndjson = this.bundleErrors.map(e => JSON.stringify(e)).join('\n');
        zip.file('errors.ndjson', ndjson);
      }

      // CSV manifest from the per-item records filled by _bundleOne
      const header = 'request_id,network,asset,tx_hash,to_address,mode,report_path,image_path';
      const lines = this.bundleManifest.map(m => ([
        m.rid,
        m.network || '',
        (m.asset || '').toUpperCase(),
        m.tx || '',
        m.to || '',
        m.mode || '',
        `${m.base}/${m.reportName}`,
        `${m.base}/${m.imageName || 'image_missing.txt'}`
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')));
      zip.file('manifest.csv', [header, ...lines].join('\n'));

      const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
      const ts = new Date();
      const stamp = `${ts.getFullYear()}${String(ts.getMonth()+1).padStart(2,'0')}${String(ts.getDate()).padStart(2,'0')}-${String(ts.getHours()).padStart(2,'0')}${String(ts.getMinutes()).padStart(2,'0')}`;
      this._downloadBlob(blob, `PathTracer_Reports_${stamp}.zip`);
    } catch (err) {
      this.notify?.(`Bundling failed: ${String(err)}`, 'error');
    } finally {
      this.bundling = false;
      this.bundleAbort = null;
    }
  },

    // Find the best matching request object for a row.
// Priority: exact history -> request_id on the row -> best by tx -> none
_bestReqForRow(row) {
  const h = row?._history;
  if (h?.best) return h.best;

  // If submitted this session
  if (row?._requestId && Array.isArray(this.userRequests)) {
    const rid = String(row._requestId);
    const found = this.userRequests.find(r => String(r.request_id) === rid);
    if (found) return found;
  }

  // Fallback: best by tx (less ideal, but allows grabbing report + image when unique)
  if (h?.byTxBest) return h.byTxBest;

  return null;
},

_resultIsAdvanced(req) {
  const modeSaysAdv = !!req?.mode && req.mode !== 'bucket';
  const firstLine = (req?.result || '').split(/\r?\n/, 1)[0] || '';
  const looksTSV = firstLine.includes('\t') && /(Label|Hash|FromAddress|ToAddress|Date)/i.test(firstLine);
  return !!(modeSaysAdv || looksTSV);
},

_tsvToCsv(tsv) {
  // Simple but robust: split on tabs, quote fields with commas/quotes/newlines, join with commas.
  const lines = String(tsv || '').split(/\r?\n/);
  const quote = v => {
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  return lines
    .map(line => line.split('\t').map(quote).join(','))
    .join('\n');
},

_safePart(s) {
  return String(s || '')
    .trim()
    .replace(/[^\w.-]+/g, '_')   // keep word chars, dot, hyphen
    .replace(/^_+|_+$/g, '')     // trim leading/trailing underscores
    .slice(0, 64);
}
,
_baseNameFor(req, row) {
  const rid = req?.request_id ? `RID_${req.request_id}` : 'RID_unknown';
  const net = this._safePart(row?.network);
  const asset = this._safePart((row?.assetSymbol || '').toUpperCase());
  const txShort = this._safePart((row?.txHash || '').slice(0, 12));
  const toShort = this._safePart((row?.toAddress || '').slice(0, 12));
  return [rid, net, asset, txShort ? `TX_${txShort}` : null, toShort ? `TO_${toShort}` : null]
    .filter(Boolean)
    .join('__');
},

_delay(ms) {
  return new Promise(res => setTimeout(res, ms));
},

// Axios GET with AbortController
async _get(url, cfg = {}) {
  if (!this.bundleAbort) this.bundleAbort = new AbortController();
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const merged = { ...cfg, headers: { ...(cfg.headers || {}), ...headers }, signal: this.bundleAbort.signal };
  return await this.$axios?.get ? this.$axios.get(url, merged) : axios.get(url, merged);
},

// Try HQ; if not available, fall back to LQ base64 and return a Blob.
async _fetchImageBlob(req, username) {
  const rid = req?.request_id;
  if (!rid) throw new Error('no-request-id');

  // 1) Ask if HQ exists
  try {
    const meta = await this._get('/user/hq_images/meta.php', { params: { request_id: rid } });
    if (meta?.data?.available) {
      const dl = await this._get('/user/hq_images/download.php', {
        params: { request_id: rid },
        responseType: 'blob'
      });
      return dl.data; // Blob
    }
  } catch (_) {
    console.log("_");
    // fall back to LQ
  }

  // 2) Low quality JSON -> base64 -> Blob
  const lq = await this._get('etherscan/retrieve_screenshot.php', {
    params: { request_id: rid, username },
  });
  if (lq?.data?.status === 'success' && lq.data.image_data) {
    return this._dataUrlToBlob(this._ensureDataUrl(lq.data.image_data));
  }
  throw new Error('no-image');
},

_ensureDataUrl(image_data) {
  if (typeof image_data !== 'string') return '';
  if (image_data.startsWith('data:')) return image_data;

  // best-effort MIME sniff
  let raw = image_data.replace(/-/g, '+').replace(/_/g, '/');
  const pad = raw.length % 4;
  if (pad) raw += '='.repeat(4 - pad);

  let mime = 'image/jpeg';
  if (raw.startsWith('iVBOR')) mime = 'image/png';
  else if (raw.startsWith('/9j/')) mime = 'image/jpeg';
  else if (raw.startsWith('UklG')) mime = 'image/webp';
  return `data:${mime};base64,${raw}`;
},

_dataUrlToBlob(dataUrl) {
  const [head, b64] = String(dataUrl).split(',');
  const m = head.match(/data:([^;]+);base64/i);
  const mime = m ? m[1] : 'image/jpeg';
  const bin = atob(b64 || '');
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
},

_downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
},
async bundleSelectedReports() {
  const username = localStorage.getItem('username');
  const token = localStorage.getItem('token');
  if (!username || !token) {
    this.notify?.('Please sign in first.', 'error');
    return;
  }

  // Build the work list: only completed items with a usable request + result
  const picks = this.selectedRows
    .map(row => ({ row, req: this._bestReqForRow(row) }))
    .filter(x => x.req && String(x.req.status).toLowerCase() === 'complete' && !!x.req.result);

  if (picks.length === 0) {
    this.notify?.('No completed results selected.', 'warn');
    return;
  }

  this.bundling = true;
  this.bundleErrors = [];
  this.bundleProgress = { total: picks.length, done: 0, skipped: 0, failed: 0 };
  this.bundleAbort = new AbortController();

  const zip = new JSZip();

  // Simple throttled queue with limited concurrency
  const queue = [...picks];
  const workers = Math.max(1, Math.min(this.bundleConcurrency, queue.length));
  const runWorker = async () => {
    while (queue.length && this.bundling) {
      const item = queue.shift();
      await this._bundleOne(zip, item).catch(err => {
        this.bundleErrors.push({ rid: item?.req?.request_id, error: String(err) });
        this.bundleProgress.failed++;
      });
      this.bundleProgress.done++;
      await this._delay(this.bundleDelayMs);
    }
  };

  try {
    await Promise.all(Array.from({ length: workers }, runWorker));

    // If any errors, include a manifest in the zip
    if (this.bundleErrors.length) {
      const ndjson = this.bundleErrors.map(e => JSON.stringify(e)).join('\n');
      zip.file('errors.ndjson', ndjson);
    }

    // Also include a CSV manifest mapping request -> filenames
    const manifestRows = picks.map(({ row, req }) => {
      const base = this._baseNameFor(req, row);
      const adv = this._resultIsAdvanced(req);
      const modeDisplay = this._modeDisplay(req.mode);

      return [
        req.request_id,
        row.network || '',
        (row.assetSymbol || '').toUpperCase(),
        row.txHash || '',
        row.toAddress || '',
        modeDisplay,
        `${base}/${adv ? 'report.csv' : 'report.txt'}`,
        `${base}/image.(hq-or-lq)`
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
    });
    zip.file(
      'manifest.csv',
      ['request_id,network,asset,tx_hash,to_address,mode,report_path,image_path', ...manifestRows].join('\n')
    );

    const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
    const ts = new Date();
    const stamp = `${ts.getFullYear()}${String(ts.getMonth()+1).padStart(2,'0')}${String(ts.getDate()).padStart(2,'0')}-${String(ts.getHours()).padStart(2,'0')}${String(ts.getMinutes()).padStart(2,'0')}`;
    this._downloadBlob(blob, `PathTracer_Reports_${stamp}.zip`);
  } catch (err) {
    this.notify?.(`Bundling failed: ${String(err)}`, 'error');
  } finally {
    this.bundling = false;
    this.bundleAbort = null;
  }
},

async _bundleOne(zip, { row, req }) {
  const base = this._baseNameFor(req, row);     // e.g., RID_123__ETH__USDT__TX_abcd...__TO_ef01...
  const folder = zip.folder(base);
  const prefix = base;                          // reuse as filename prefix for uniqueness

  // ----- Report file (unique, descriptive) -----
  let reportName, reportBytes = 0;
  if (this._resultIsAdvanced(req)) {
    const csv = this._tsvToCsv(req.result || '');
    reportName = `${prefix}__report_advanced.csv`;
    folder.file(reportName, csv);
    reportBytes = new Blob([csv]).size;
  } else {
    const txt = String(req.result || '');
    reportName = `${prefix}__report_normal.txt`;
    folder.file(reportName, txt);
    reportBytes = new Blob([txt]).size;
  }

  // ----- Meta (optional to prefix; safe to leave as meta.json if you prefer) -----
// ----- Meta (mapped mode) -----
const metaObj = {
  request_id: req.request_id,
  mode: this._modeDisplay(req.mode),   // <-- mapped: 'normal' or 'advanced'
  // Optional: keep raw for internal debugging (remove if you prefer)
  mode_raw: (req.mode ?? null),

  status: req.status,
  network: row.network,
  asset: (row.assetSymbol || '').toUpperCase(),
  tx_hash: row.txHash || null,
  to_address: row.toAddress || null,
  requested_at: req.request_time || null,
};
const metaStr = JSON.stringify(metaObj, null, 2);
  // If you want uniqueness even when users flatten everything later, uncomment next line:
  // const metaName = `${prefix}__meta.json`;
  // folder.file(metaName, metaStr);
  folder.file('meta.json', metaStr);
  const metaBytes = new Blob([metaStr]).size;

  // ----- Screenshot (HQ first, fallback to LQ) with unique names -----
  let imageName = null;
  let imageBytes = 0;

  try {
    const blob = await this._fetchImageBlob(req, localStorage.getItem('username'));

    const ext = (blob.type === 'image/png') ? 'png'
              : (blob.type === 'image/webp') ? 'webp'
              : 'jpg';

    // Check HQ meta to name accordingly
    let isHQ = false;
    try {
      const meta = await this._get('/user/hq_images/meta.php', { params: { request_id: req.request_id } });
      isHQ = !!meta?.data?.available;
    } catch { /* ignore */ }

    imageName = `${prefix}__${isHQ ? 'image_hq' : 'image_lq'}.${ext}`;
    folder.file(imageName, blob);
    imageBytes = blob.size || 0;
  } catch {
    // No image; create a marker file (not prefixed since it stays inside the per-request folder)
    folder.file('image_missing.txt', `No image available for request ${req.request_id}`);
    this.bundleProgress.skipped++;
  }

  // ----- Feed the manifest with the exact names we wrote -----
  this.bundleManifest.push({
    rid: req.request_id,
    base,
    mode: (req.mode && req.mode !== 'bucket') ? 'advanced' : 'normal',
    network: row.network,
    asset: (row.assetSymbol || '').toUpperCase(),
    tx: row.txHash || '',
    to: row.toAddress || '',
    reportName,
    imageName
  });

  return reportBytes + metaBytes + imageBytes;
}
,

cancelBundling() {
  if (this.bundleAbort) {
    try { this.bundleAbort.abort(); } catch (_) { console.log("_"); }
  }
  this.bundling = false;
},

     _startInterpreterPolling(resetFast = false) {
    this._stopInterpreterPolling();

    if (resetFast) {
      this.pollState.cycles = 0;
      this.pollState.errors = 0;
      this.pollState.lastHardRefreshAt = 0;
    }

    // Guard: only poll if we have results to annotate
    if (!this.analysis || !Array.isArray(this.analysis.withdrawals) || this.analysis.withdrawals.length === 0) {
      return;
    }

    // Guard: not logged in
    const auth = this._getAuth && this._getAuth();
    if (!auth) return;

    const tick = async () => {
      // Pause while runner is actively submitting to avoid thrash
      const runnerBusy = (this.runnerActiveIndex != null && this.runnerActiveIndex >= 0) ||
                         (this.runnerState === 'running');
      if (runnerBusy || document.hidden) {
        this.pollTimer = setTimeout(tick, this.pollCfg.normalMs);
        return;
      }

      try {
        await this._pollOnce();
        this.pollState.errors = 0;
      } catch (e) {
        this.pollState.errors++;
        if (this.pollState.errors > this.pollCfg.maxErrors) {
          this._stopInterpreterPolling();
          this.notify?.('Status updates paused due to repeated errors.', 'error');
          return;
        }
      }

      // Adaptive cadence
      this.pollState.cycles++;
      const nextMs =
        (this.pollState.cycles <= this.pollCfg.fastCycles) ? this.pollCfg.fastMs
        : (this.pollState.cycles % 12 === 0) ? this.pollCfg.slowMs
        : this.pollCfg.normalMs;

      this.pollTimer = setTimeout(tick, nextMs);
    };

    // kick off
    this.pollTimer = setTimeout(tick, 0);
  },

  _stopInterpreterPolling() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  },
    async _pollOnce() {
    const auth = this._getAuth();
    if (!auth) return;

    const hardRefresh = (this.pollState.cycles === 0) ||
                        ((this.pollState.cycles - this.pollState.lastHardRefreshAt) >= this.pollCfg.hardRefreshEvery);

    let fetched = [];
    if (hardRefresh) {
      // Deeper sweep to heal inconsistencies
      fetched = await this._fetchAllRequests(auth.username, auth.token, 200, 6);
      this.pollState.lastHardRefreshAt = this.pollState.cycles;
    } else {
      // Cheap refresh: first page with a generous limit
      fetched = await this._fetchAllRequests(auth.username, auth.token, 200, 1);
      // If your backend accepts a "since" or "updated_after" param, switch to that here.
    }

    if (!Array.isArray(fetched)) return;

    // Replace cache and indexes
    this.userRequests = fetched;
    this.buildHistoryIndex();

    // Annotate table rows with new info
    if (this.analysis && Array.isArray(this.analysis.withdrawals)) {
      this._applyRequestUpdatesToRows(this.analysis.withdrawals);
    }
  },
   _applyRequestUpdatesToRows(rows) {
    const rank = this._statusRank();
    const norm = (v) => String(v || '').trim().toLowerCase();

    for (const row of rows) {
      let req = null;

      // 1) If we have a requestId on the row, prefer that exact record
      if (row._requestId) {
        const idStr = String(row._requestId);
        req = this.userRequests.find(r => String(r.request_id) === idStr) || null;
      }

      // 2) Otherwise, try exact tx+to map
      if (!req && this.historyIndex) {
        const tx = norm(row.txHash);
        const to = norm((row.toAddress && row.toAddress !== '—') ? row.toAddress : '');
        if (tx && to) {
          const key = `${tx}|${to}`;
          req = this.historyIndex.byExactBest.get(key) || null;
        }
        // 3) fallback to byTx or byTo
        if (!req && tx) req = this.historyIndex.byTxBest.get(tx) || null;
        if (!req && to) req = this.historyIndex.byToBest.get(to) || null;
      }

      if (!req) continue;

      // Upgrade the row status if the request has advanced
      this._updateRowFromRequest(row, req, rank);
    }
  },
    _statusRank() {
    // Higher is better/newer
    return {
      '': 0,
      unknown: 0,
      pending: 1,
      queued: 2,
      'in progress': 3,
      processing: 3,
      complete: 4,
      error: 2,      // treat error as not better than in-progress, but can replace pending
      skipped: 1,
      'needs_input': 1
    };
  },

  _updateRowFromRequest(row, req, rankMap) {
    const norm = (s) => String(s || '').trim().toLowerCase();
    const reqStatus = norm(req.status);

    // Determine current rank on the row
    const curStatus = norm(row._status);
    const curRank = rankMap[curStatus] != null ? rankMap[curStatus] : 0;
    const newRank = rankMap[reqStatus] != null ? rankMap[reqStatus] : 0;

    // Only apply upgrades (or fill empty)
    const shouldApply = (newRank > curRank) || !row._status;
    if (!shouldApply) return;

    row._status = reqStatus || 'unknown';

    // When it becomes queued/processing/complete, attach the request id for persistence
    if (req.request_id != null) {
      row._requestId = req.request_id;
    }

    // Recompute _history snapshot for this row (keeps your “Open” button logic in sync)
    row._history = row._history || {};
    row._history.exact = true; // the index we picked is the best for this row’s context
    row._history.best = req;
    row._history.bestStatus = req.status;
    row._history.bestRequestId = req.request_id;
    row._history.bestHasResult = !!(req.result && String(req.result).length);

    // If it just turned complete, optionally auto-nudge the user
    // e.g., flash a small “row completed” indicator; left as a UI choice
  },
    onVisChange() {
    if (document.hidden) {
      this._stopInterpreterPolling();
    } else if (this.pollState.enabled) {
      this._startInterpreterPolling(/*resetFast*/ true);
    }
  },
    // Call this when analysis finishes or when the user re-opens a saved file
  startPollingAfterAnalysis() {
    // Only if there are withdrawals to watch
    if (!this.analysis || !Array.isArray(this.analysis.withdrawals) || this.analysis.withdrawals.length === 0) {
      return;
    }
    this.pollState.enabled = true;
    this._startInterpreterPolling(true); // begin in fast mode
  },
     getFlagIcon(flag) {
    switch (flag) {
      case "green":  return require("../assets/greenflag.png");
      case "blue":   return require("../assets/blueflag.png");
      case "orange": return require("../assets/orangeflag.png");
      case "red":    return require("../assets/redflag.png");
      default:       return null;
    }
  },
    parseReport(result) {
    if (!result || /error/i.test(result)) {
      return { error: true };
    }

    // Normalize and split
    const lines = result
      .split("\n")
      .map(l => l.trim())
      .filter(l => l.length);

    const flagRe = /^(Green|Blue|Orange|Red)\s+flags?\s*-\s*(.+)$/i;
    const initRe = /^Initially followed:\s*(.+)$/i;

    const flags = [];
    const seenFlagKeys = new Set();
    const initialCandidates = [];

    for (const line of lines) {
      const f = line.match(flagRe);
      if (f) {
        const flag = f[1].toLowerCase();
        const amount = f[2].trim(); // e.g. "2225.923 USDT" or "1.87 ETH"
        const key = `${flag}|${amount}`.toLowerCase();

        // De-duplicate exact same flag+amount (handles full report repeats)
        if (!seenFlagKeys.has(key)) {
          seenFlagKeys.add(key);
          flags.push({ flag, amount });
        }
        continue;
      }

      const i = line.match(initRe);
      if (i) {
        initialCandidates.push(i[1].trim());
      }
    }

    // Determine initial
    let initial = null;
    let notes = { deduped: false, duplicateInitialCount: 0, conflictingInitials: false };

    if (initialCandidates.length > 0) {
      // If multiple, keep the first; track duplicates and conflicts
      initial = initialCandidates[0];
      notes.duplicateInitialCount = initialCandidates.length;
      const uniqueInits = Array.from(new Set(initialCandidates.map(s => s.toLowerCase())));
      if (uniqueInits.length > 1) {
        notes.conflictingInitials = true;
      }
      if (initialCandidates.length > 1) {
        notes.deduped = true;
      }
    }

    // If we saw any repeated lines (either by duplicate flags or multiple initial lines), mark deduped
    if (seenFlagKeys.size < flags.length || initialCandidates.length > 1) {
      notes.deduped = true;
    }

    return { flags, initial, error: false, notes };
  },

  interpretReport(parsed) {
      // Advanced mode is handled elsewhere
  if (this.isAdvancedReport) return this.buildAdvancedHumanReport();
  
  // Always return an array of { icon?, text } objects to render with flag icons
    const out = [];

    if (parsed.error) {
      out.push({
        text: "Something went wrong with this report. Please press Report Bug and request a refund for the credit used."
      });
      return out;
    }

    // Warn if we detected duplication or conflicts
    if (parsed.notes?.deduped) {
      const base = parsed.notes.conflictingInitials
        ? "We found repeated and conflicting lines in the report. We interpreted the first occurrence."
        : "We found repeated lines in the report. We removed duplicates and interpreted the first occurrence.";
      out.push({ text: base + " If this looks off, please press Report Bug and request a refund." });
    }

    // No flags? Treat as error per your rule set.
    if (!parsed.flags || parsed.flags.length === 0) {
      const initText = parsed.initial ? ` of ${parsed.initial}` : "";
      out.push({
        text: `The analysis only shows an initially followed amount${initText}. This indicates something went wrong. Please report a bug and request a refund.  To report a bug, you can press Report Bug beside the request in your Request History below.`
      });
      return out;
    }

    // Initial line (if present)
    if (parsed.initial) {
      out.push({ text: `The analysis began by following ${parsed.initial}.` });
    }

    // Flag lines with your flag icons (no emojis)
    for (const f of parsed.flags) {
      let message;
      switch (f.flag) {
        case "green":
          message = `${f.amount} arrived at a likely compliant exchange or other subpoenable entity.`; break;
        case "blue":
          message = `${f.amount} remains in a dormant wallet.`; break;
        case "orange":
          message = `${f.amount} requires manual review due to complications.`; break;
        case "red":
          message = `${f.amount} reached a non-compliant or unrecoverable destination.`; break;
        default:
          message = `${f.amount} was flagged (${f.flag}).`; break;
      }
      out.push({ flag: f.flag, icon: this.getFlagIcon(f.flag), text: message });
    }

    // Multi-token swap note: detect different symbols (last token in the amount string)
    if (parsed.flags.length > 1) {
      const tokens = parsed.flags.map(f => {
        const parts = f.amount.split(/\s+/);
        return parts[parts.length - 1]; // ETH, USDT, BTC, etc.
      });
      if (new Set(tokens.map(t => t.toUpperCase())).size > 1) {
        out.push({ text: "Different token types appear in the outcome, which suggests swaps occurred along the way." });
      }
    }

    return out;
  },
    async copyRawTSV() {
  const tsv = String(this.reportRequest?.result || '');
  if (!tsv) return;
  try {
    this.copyingTSV = true;

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(tsv);
    } else {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = tsv;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }

    // Visual feedback
    this.copiedTSV = true;
    if (this.copiedTSVTimer) clearTimeout(this.copiedTSVTimer);
    this.copiedTSVTimer = setTimeout(() => {
      this.copiedTSV = false;
      this.copiedTSVTimer = null;
    }, 1600);
  } catch (e) {
    console.warn('Copy TSV failed:', e);
  } finally {
    this.copyingTSV = false;
  }
},

     async copyReport() {
    try {
      this.copyingReport = true;
      // Prefer copying the human-friendly interpretation; fall back to raw if needed.
      const human = Array.isArray(this.humanReport)
        ? this.humanReport.map(l => l.text).join("\n")
        : String(this.humanReport || "");
      const raw = String(this.reportRequest?.result || "");
      const textToCopy = human?.trim()
        ? human + (raw?.trim() ? `\n\n--- Raw Report ---\n${raw}` : "")
        : raw;

      // Try modern clipboard API first
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        // Fallback for older browsers
        const ta = document.createElement("textarea");
        ta.value = textToCopy;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }

      // Visual feedback
      this.copiedReport = true;
      if (this.copiedReportTimer) clearTimeout(this.copiedReportTimer);
      this.copiedReportTimer = setTimeout(() => {
        this.copiedReport = false;
        this.copiedReportTimer = null;
      }, 1600);
    } catch (e) {
      // Optional: toast/notice – keep silent if you prefer
      console.warn("Copy failed:", e);
    } finally {
      this.copyingReport = false;
    }
  },
    openFullReportForRow(row) {
  const req = row?._history?.best;
  if (!req) return;

  // Defensive normalization (your fetch_requests.php returns these fields)
  const hydrated = {
    request_id: req.request_id,
    transaction_hash: req.transaction_hash,
    to_address: req.to_address || null,
    mode: req.mode,                 // 'bucket' => Normal, anything else => Advanced
    status: req.status,
    request_time: req.request_time,
    result: req.result || ""        // TSV for advanced, human text for normal
  };

  this.openReport(hydrated);        // Reuse the full modal you brought over
}
,
       // ====== OPEN/CLOSE full report modal ======
    openReport(req) {
      this.reportRequest = req;
      this.showReportModal = true;
      this.showAdvancedSheet = false;
      this.advancedHeaders = [];
      this.advancedRows = [];
      this.advancedRowCount = 0;
      this.advancedColCount = 0;
      this.advancedDateRange = null;
      this.$nextTick(() => {
        this.$refs.reportModal?.focus();
      });
    },
    closeReport() {
      this.showReportModal = false;
      this.reportRequest = null;
    },
    _resolveScreenshotUrl(requestId) {
    const route = { name: 'ViewImage', params: { requestID: String(requestId) } };
    const { href } = this.$router.resolve(route);
    return href.startsWith('http') ? href : `${window.location.origin}${href}`;
  },

  _openInNewTab(url) {
    // Create a hidden <a target="_blank"> and click it – avoids several
    // false negatives you get from window.open across browsers.
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener'; // avoid noreferrer to reduce null-return quirks
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // We can't reliably detect success; do not auto-fallback.
  },

  openScreenshotAndClose(requestId) {
    const url = this._resolveScreenshotUrl(requestId);
    this._openInNewTab(url);
  

  },

  // If user clicks the fallback action in your toast/banner:
  openScreenshotHere() {
    const item = this.screenshotPopupBlocked;
    if (item?.requestId) {
      this.$router.push({ name: 'ViewImage', params: { requestID: String(item.requestId) } });
    }
    this.screenshotPopupBlocked = null;
  },

  // If you call this elsewhere, keep it new-tab too:
  viewScreenshot(requestId) {
    const url = this._resolveScreenshotUrl(requestId);
    this._openInNewTab(url);
  },
    // ====== Helpers used in the modal ======
    reportLocalTime(iso) {
      const d = new Date(iso);
      return isNaN(d) ? '' : d.toLocaleString();
    },
    getModeDisplay(mode) {
      return mode === 'bucket' ? 'Normal' : 'Advanced';
    },
    getExplorerUrlForAddress(address, network) {
      switch ((network || '').toLowerCase()) {
        case 'bitcoin':  return `https://mempool.space/address/${address}`;
        case 'ethereum': return `https://etherscan.io/address/${address}`;
        case 'tron':     return `https://tronscan.org/#/address/${address}`;
        default:         return '';
      }
    },

    // ====== Advanced-mode (TSV) rendering ======
    buildAdvancedHumanReport() {
      const out = [];
      const tsv = this.reportRequest?.result || "";
      const { headers, rows } = this.parseTSV(tsv);
      this.advancedHeaders = headers;
      this.advancedRows = rows;
      this.advancedRowCount = rows.length;
      this.advancedColCount = headers.length;
      this.advancedDateRange = this.computeDateRange(headers, rows);

      out.push({
        text: `Advanced analysis complete — spreadsheet output with ` +
              `${this.advancedRowCount.toLocaleString()} row${this.advancedRowCount === 1 ? "" : "s"} ` +
              `and ${this.advancedColCount} column${this.advancedColCount === 1 ? "" : "s"}.`
      });
      if (this.advancedDateRange?.min && this.advancedDateRange?.max) {
        const { min, max } = this.advancedDateRange;
        out.push({ text: `Date range detected: ${min} → ${max}.` });
      }
      out.push({
        text: "Tip: Click “Open spreadsheet preview” to browse the results here, or copy/download and paste into your spreadsheet app."
      });
      return out;
    },

    parseTSV(tsvText) {
      const text = (tsvText || "").trim();
      if (!text) return { headers: [], rows: [] };
      const lines = text.split(/\r?\n/);
      if (!lines.length) return { headers: [], rows: [] };
      const headers = (lines[0] || "").split("\t");
      const rows = lines.slice(1).filter(Boolean).map(line => line.split("\t"));
      return { headers, rows };
    },

    computeDateRange(headers, rows) {
      const dateIdx = headers.findIndex(h => /date/i.test(String(h)));
      if (dateIdx === -1) return null;
      let min = null, max = null;
      for (const r of rows) {
        const raw = r[dateIdx];
        const ymd = (raw || "").slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) continue;
        if (!min || ymd < min) min = ymd;
        if (!max || ymd > max) max = ymd;
      }
      return (min && max) ? { min, max } : null;
    },

    ensureAdvancedSheetLoaded() {
      const tsv = this.reportRequest?.result || "";
      const { headers, rows } = this.parseTSV(tsv);
      const grid = [headers, ...rows];
      if (!this.showAdvancedSheet) {
        this.showAdvancedSheet = true;
        this.$nextTick(() => {
          const comp = this.$refs.advancedSheet;
          if (comp?.loadData) comp.loadData(grid);
          comp?.render?.();
        });
      } else {
        const comp = this.$refs.advancedSheet;
        if (comp?.loadData) comp.loadData(grid);
        comp?.render?.();
      }
    },

    downloadAdvanced(format = "tsv") {
      const raw = this.reportRequest?.result || "";
      if (!raw) return;
      const blob =
        format === "csv"
          ? new Blob([this.tsvToCSV(raw)], { type: "text/csv;charset=utf-8" })
          : new Blob([raw], { type: "text/tab-separated-values;charset=utf-8" });
      const filename = `advanced-report.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },

    tsvToCSV(tsv) {
      const lines = (tsv || "").split(/\r?\n/);
      return lines
        .map(line =>
          line.split("\t").map(cell => {
            const s = String(cell ?? "");
            const needsQuotes = /[",\n]/.test(s);
            const escaped = s.replace(/"/g, '""');
            return needsQuotes ? `"${escaped}"` : escaped;
          }).join(",")
        ).join("\n");
    },

    // ====== Inline Prior Analysis modal ======
    openInlineReportForRow(row) {
      const req = row?._history?.best;
      if (!req || String(req.status).toLowerCase() !== 'complete' || !req.result) {
        // fallback to main Analysis Tool if something changed
        const rid = row?._history?.bestRequestId;
        if (rid) this.$router.push({ path: '/AnalysisTool', query: { open: String(rid) } });
        return;
      }

      // Decide Normal vs Advanced for inline view
      const modeSaysAdv = !!req.mode && req.mode !== 'bucket';
      const firstLine = (req.result || '').split(/\r?\n/, 1)[0] || '';
      const looksTSV = firstLine.includes('\t') && /(Label|Hash|FromAddress|ToAddress|Date)/i.test(firstLine);
      this.inlineReportIsAdvanced = modeSaysAdv || looksTSV;
      this.inlineReportReq = req;

      if (this.inlineReportIsAdvanced) {
        // lightweight summary for advanced
        const { headers, rows } = this.parseTSV(req.result || "");
        const range = this.computeDateRange(headers, rows);
        const lines = [];
        lines.push({ text: `Advanced analysis — ${rows.length.toLocaleString()} rows, ${headers.length} columns.` });
        if (range?.min && range?.max) lines.push({ text: `Date range: ${range.min} → ${range.max}.` });
        this.inlineReportLines = lines;
      } else {
        // Reuse your interpreter if present; fallback to plain lines
        if (this.parseReport && this.interpretReport) {
          const parsed = this.parseReport(req.result || "");
          this.inlineReportLines = this.interpretReport(parsed);
        } else {
          this.inlineReportLines = (req.result || "").split(/\r?\n/).filter(Boolean).map(t => ({ text: t }));
        }
      }

      this.inlineReportOpen = true;
    },
    closeInlineReport() {
      this.inlineReportOpen = false;
      this.inlineReportReq = null;
      this.inlineReportLines = [];
      this.inlineReportIsAdvanced = false;
    },

  dismissGuide() {
    this.showGuide = false;
    try { localStorage.setItem('csvhelper.guide.hidden', '1'); } catch (e) { /* non-fatal */ }
  },
    // Compute request_type for archival/analytics. Keep simple: “batch” for this runner.
computeRequestType(row) {
  if (!row) {
    console.log("no row");
  }
  // If you want to distinguish LinkCheck runs, you can branch on this.bulkMode.
  // e.g. return this.bulkMode === 'linkcheck' ? 'linkcheck' : 'batch';
  return 'batch';
},

// Actual submit to your create_request.php endpoint
async _submitReal(row) {
  const token = localStorage.getItem('token');
  const username = localStorage.getItem('username');
  if (!token || !username) return { ok: false, reason: 'Missing auth' };

  const payload = {
    username,
    transaction_hash: (row.txHash || '').trim(),
    to_address: (row.toAddress && row.toAddress !== '—') ? String(row.toAddress).trim() : null,
    request_type: this.computeRequestType(row),              // e.g. 'batch'
    mode: this.bulkMode || 'bucket',
  hop_logic: this.bulkHopLogic || 'fifo'   // NEW                          // endpoint normalizes anyway
  };

  if (!payload.transaction_hash) {
    return { ok: false, reason: 'Missing transaction hash' };
  }

  let resp;
  try {
    // Use ABSOLUTE path so we don’t hit the SPA route
    resp = await fetch('/api/etherscan/create_request.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
  } catch (e) {
    return { ok: false, reason: 'Network error' };
  }

  // If not ok, try to read text for a better reason
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    if (resp.status === 403) return { ok: false, reason: 'Access denied / insufficient credits' };
    if (resp.status === 405) return { ok: false, reason: 'Invalid method' };
    if (resp.status >= 500)  return { ok: false, reason: 'Server error' };
    return { ok: false, reason: `HTTP ${resp.status}${text ? ` (${text.slice(0,140)})` : ''}` };
  }

  // Try JSON, but show a helpful snippet if the body isn’t JSON
  const ct = (resp.headers.get('content-type') || '').toLowerCase();
  let data;
  if (!ct.includes('application/json')) {
    const text = await resp.text().catch(() => '');
    return { ok: false, reason: `Non-JSON response: ${ct || 'unknown'} ${text ? `— ${text.slice(0,140)}` : ''}` };
  }

  try {
    data = await resp.json();
  } catch {
    const text = await resp.clone().text().catch(() => '');
    return { ok: false, reason: `Bad JSON from server${text ? ` — ${text.slice(0,140)}` : ''}` };
  }

if (data?.status === 'success') {
  // Optimistically add a queued record locally
  try {
    const now = new Date().toISOString();
    this.userRequests = [
      {
        request_id: data.request_id || null,
        username: payload.username,
        transaction_hash: payload.transaction_hash,
        to_address: payload.to_address,
        request_type: payload.request_type,
        mode: payload.mode,
        status: 'queued',
        created_at: now
      },
      ...(this.userRequests || [])
    ];
    // Rebuild index and re-annotate just this row
    this.buildHistoryIndex();
    if (row) this.annotateRowWithHistory(row);
  } catch (_) { /* non-fatal */ }

  return { ok: true, requestId: data.request_id || null };
}

  return { ok: false, reason: data?.message || 'Submit failed' };
}
,
// Patterns shared with the main tool
validateTransactionHash(hash) {
  const ethereumPattern = /^0x[a-fA-F0-9]{64}$/;
  const bitcoinPattern  = /^[a-fA-F0-9]{64}$/;
  const tronPattern     = /^T[a-zA-Z0-9]{33}$/;

  if (ethereumPattern.test(hash)) return 'Ethereum';
  if (bitcoinPattern.test(hash))  return 'Bitcoin';
  if (tronPattern.test(hash))     return 'Tron';
  return null;
},

looksLikeAddressOrWrongInput(input) {
  const s = String(input || '').trim();
  const ethAddress   = /^0x[a-fA-F0-9]{40}$/;
  const btcAddress   = /^(bc1([a-zA-HJ-NP-Z0-9]{6,87})|[13][a-km-zA-HJ-NP-Z1-9]{25,34})$/;
  const tronAddress  = /^T[a-zA-Z0-9]{33}$/;
  const explorerURL  = /(etherscan\.io|tronscan\.org|blockchain\.com|bscscan\.com)\/(tx|address)\/[a-zA-Z0-9]+/;
  const tooShort     = s.length < 40;
  const hasWhitespace= /\s/.test(s);

  return (
    ethAddress.test(s) || btcAddress.test(s) || tronAddress.test(s) ||
    explorerURL.test(s) || tooShort || hasWhitespace
  );
},

// Minimal "looks like address" for the hash-field misentry case
looksLikeAddress(input) {
  const s = String(input || '').trim();
  return /^0x[a-fA-F0-9]{40}$/.test(s) ||                    // ETH
         /^(bc1([a-zA-HJ-NP-Z0-9]{6,87})|[13][a-km-zA-HJ-NP-Z1-9]{25,34})$/.test(s) || // BTC
         /^T[a-zA-Z0-9]{33}$/.test(s);                       // TRON
},

// Validate a To Address for the detected chain
isValidAddressForNetwork(addr, chain) {
  if (!addr) return false;
  const s = String(addr).trim();

  switch (chain) {
    case 'Ethereum':
      return /^0x[a-fA-F0-9]{40}$/.test(s);
    case 'Bitcoin':
      return /^(bc1([a-zA-HJ-NP-Z0-9]{6,87})|[13][a-km-zA-HJ-NP-Z1-9]{25,34})$/.test(s);
    case 'Tron':
      return /^T[a-zA-Z0-9]{33}$/.test(s);
    default:
      return false;
  }
},
async _performQualityChecks(row, job) {

  console.log(job);
  // Cooldown knobs
  const checkDelay = (this.runnerCheckCooldownMs ?? this.runnerCooldownMs ?? 500);

  // 0) Already analyzed exact?
  if (row && row._history && row._history.exact) {
    return { ok: false, reason: 'Already analyzed' };
  }

  // 1) Login / access gates (same spirit as main tool)
  const username = localStorage.getItem('username') || null;
 // const token    = localStorage.getItem('token') || null;
  const isLoggedIn = !!username;
  if (!isLoggedIn) return { ok: false, reason: 'Not logged in' };

  if (!this.isUnlimitedUser && !this.isSubscriptionUser && (this.creditBalance|0) === 0) {
    return { ok: false, reason: 'No credits / plan' };
  }

  // 2) Mode gate: block "advanced" (your 'normal' value) without permission
  // (Matches your bulk-mode select where value="normal" is Advanced)
  if (this.bulkMode === 'normal' && !this.canUseNormalMode) {
    return { ok: false, reason: 'No permission for advanced mode' };
  }

  // Pull fields from the row
  const txHash   = (row.txHash || '').trim();
  const toAddrIn = (row.toAddress && row.toAddress !== '—') ? String(row.toAddress).trim() : '';
  const hasHash  = !!txHash;

  // 3) Quick bad-input screen: hash looks like an address or junk
  if (hasHash && this.looksLikeAddress(txHash)) {
    return { ok: false, reason: 'Hash field looks like an address' };
  }
  if (hasHash && this.looksLikeAddressOrWrongInput(txHash)) {
    // In the main tool you’d show a helper; runner rejects
    return { ok: false, reason: 'Invalid transaction hash format' };
  }

  // 4) Must have tx hash by now (runner won’t assist on address-only cases)
  if (!hasHash) {
    return { ok: false, reason: 'Missing transaction hash' };
  }

  // 5) Detect chain from the hash
  const chain = this.validateTransactionHash(txHash); // 'Ethereum' | 'Bitcoin' | 'Tron' | null
  if (!chain) {
    return { ok: false, reason: 'Unrecognized hash format' };
  }

  // 6) If To Address is provided, it must match the chain format
  if (toAddrIn && !this.isValidAddressForNetwork(toAddrIn, chain)) {
    return { ok: false, reason: 'Invalid destination for chain' };
  }

  // 7) Check whether a To Address is REQUIRED for this tx (exactly like main tool)
  try {
    const resp = await fetch(TO_ADDR_CHECK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        transaction_hash: txHash,
        network: chain,
        to_address: toAddrIn ? toAddrIn.toLowerCase() : null
      })
    });
    await this._sleep(checkDelay);

    let check = null;
    try { check = await resp.json(); } catch (_) { console.log("eslint: empty block statement"); }

    if (!check || typeof check !== 'object') {
      return { ok: false, reason: 'To-Address check failed' };
    }

    // Case A: To is required but missing -> fail (runner does not assist)
    if (check.requiresToAddress && !toAddrIn) {
      return { ok: false, reason: `Requires To Address (${check.reason || 'ambiguous outputs'})` };
    }

    // Case B: Provided To but mismatch -> fail (runner does not assist)
    if (toAddrIn && check.requiresToAddress && check.matchedTo === false) {
      return { ok: false, reason: `To Address mismatch (${check.reason || 'not in outputs'})` };
    }
  } catch (e) {
    return { ok: false, reason: 'To-Address check error' };
  }

  // 8) Existence checks (ETH -> BTC -> TRON), authorized like the main tool
  try {
    const headers = this.getAuthHeaders();

    // Try chain indicated by the hash first
    const order = (chain === 'Ethereum')
      ? [CHECK_ETH_URL, CHECK_BTC_URL, CHECK_TRX_URL]
      : (chain === 'Bitcoin')
        ? [CHECK_BTC_URL, CHECK_ETH_URL, CHECK_TRX_URL]
        : [CHECK_TRX_URL, CHECK_ETH_URL, CHECK_BTC_URL];

    const payloads = {
      [CHECK_ETH_URL]: JSON.stringify({ txHash }),
      [CHECK_BTC_URL]: JSON.stringify({ txHash }),
      [CHECK_TRX_URL]: JSON.stringify({ txHash }),
    };

    let found = false;
    for (const url of order) {
      const r = await fetch(url, { method: 'POST', headers, body: payloads[url] });
      await this._sleep(checkDelay);
      let j = null;
      try { j = await r.json(); } catch (_) { console.log("eslint: empty block statement"); }

      // ETH uses "exists", BTC/TRON use "found" per your original endpoints
      if (j && j.status === 'ok' && (j.exists === true || j.found === true)) {
        found = true;
        break;
      }
    }
    if (!found) return { ok: false, reason: 'Tx not found on ETH/BTC/TRON' };
  } catch (e) {
    return { ok: false, reason: 'Network error during chain checks' };
  }

  // 9) Passed all automated checks
  return { ok: true };
},

getAuthHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
},

      isAssetResolvableForNetwork,
canRowRunResolve,
canRowLookupHash,

// Direct send (no extra resolving). Keep strict so you don't enqueue junk:
canSendDirect(row) {
  if (!row || !row.txHash) return false;
  const okHash = /^0x[a-fA-F0-9]{64}$/.test(row.txHash) || /^[A-Fa-f0-9]{64}$/.test(row.txHash);
  if (!okHash) return false;

  const net = coerceNormalizedNetworkFromRow(row);
  if (!net) return false;

  // ETH/TRON often require To when multiple recipients exist; keep this strict
  if ((net === 'ethereum' || net === 'tron') && (!row.toAddress || row.toAddress === '—')) {
    return false;
  }

  if (row._history && row._history.exact) return false; // already analyzed
  return true;
},

// A row is actionable if we can either send it now OR we offer a resolve affordance (hash or address).
rowIsActionable(row) {
  if (!row) return false;
  if (row._status === 'queued' || row._status === 'complete') return false;
  return this.canSendDirect(row) || this.canRowRunResolve(row) || this.canRowLookupHash(row);
},

 async onBulkModalConfirm() {
  if (!this.modalSelectedCount) return;

  const items = this.stagedRows.map((r, i) => ({
    id: `${Date.now()}_${i}`,
    row: r,
    phase: 'queued',
    msg: ''
  }));

  this.showBulkCreditModal = false;

  // Optional: immediately deselect all staged rows before processing,
  // so the bulk bar updates to the 1 failed you described after the run.
  // We’ll also force-untick on success/skip in the runner below for robustness.
  for (const r of this.stagedRows) r._sel = false;

  this.prepareRunner(items);
  this.startRunner();
},

    // Runner lifecycle
    // Modal entry point (called by onBulkModalConfirm)
  prepareRunner(items) {
    this.runnerJobs = items;
    this.runnerStats = { total: items.length, ok: 0, failed: 0, skipped: 0 };
    this.runnerActiveIndex = -1;
    this.runnerPaused = false;
    this.runnerCancelled = false;
    this.runnerState = 'running';
    this.runnerVisible = true;
    this.runnerJobEls.clear();

    /* ONLY FOR SIMULATION
    // Reflect queued in the main table
    for (const job of this.runnerJobs) {
      if (job?.row) {
        job.row._status = 'queued';
        job.row._reason = '';
      }
    }*/

    this.runnerLog = [];
    this._log(`Queued ${items.length} item(s).`);

    this.$nextTick(() => {
      // Focus Pause for quick keyboard access
      this.$refs.runnerPauseBtn?.focus?.();
    });
  },

async startRunner() {
    for (let i = 0; i < this.runnerJobs.length; i++) {
      if (this.runnerCancelled) break;

      this.runnerActiveIndex = i;
      const job = this.runnerJobs[i];
      const row = job.row;

      // Auto-scroll active job into view
      this.$nextTick(() => {
        const el = this.runnerJobEls.get(i);
        if (el && this.$refs.runnerGrid) {
          el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      });

      // Respect pause
      while (this.runnerPaused && !this.runnerCancelled) {
        await this._sleep(150);
      }
      if (this.runnerCancelled) break;

      try {
     // 1) Resolve pieces for this row (real calls, sequential)
if (this._rowNeedsResolve(row)) {
  // A) Try to find the tx hash if eligible
  const needHash = !!row.canLookupTxHash && !row.txHash && this.canRowLookupHash(row);
  if (needHash) {
    this._setPhase(job, 'resolving', 'Finding transaction hash');
    await this._performLookupTxHash(row);               // <- real call
    await this._sleep(this.runnerResolveCooldownMs);    // <- deliberate cooldown
    if (this.runnerCancelled) break;
  }

  // B) If destination still unknown and eligible, resolve toAddress from tx
  const needAddr = (!row.toAddress || row.toAddress === '—')
                   && !!row.txHash && row.amountRaw != null
                   && this.canRowRunResolve(row);
  if (needAddr) {
    this._setPhase(job, 'resolving', 'Resolving destination from transaction');
    await this._performResolve(row);                    // <- real call
    await this._sleep(this.runnerResolveCooldownMs);    // <- deliberate cooldown
    if (this.runnerCancelled) break;
  }
}

/* SIMULATION VERSION
this._setPhase(job, 'checking', 'Running standard pre-checks');
const check = await this._simulateChecks(row, job);
await this._sleep(300);
*/

    this._setPhase(job, 'checking', 'Running quality checks');
const check = await this._performQualityChecks(row, job);
await this._sleep(this.runnerCheckCooldownMs ?? this.runnerCooldownMs ?? 500);


        if (!check.ok) {
      job.phase = 'skipped';
job.msg = check.reason || 'Did not pass checks';
row._status = 'skipped';
row._reason = job.msg;
this.runnerStats.skipped++;
this._log(`Row #${(row?.rowIndex ?? -1)+1}: skipped - ${job.msg}`);

// Skipped items are not immediately actionable; clear selection
row._sel = false;
          await this._sleep(this.runnerCooldownMs);
          continue;
        }
/* SIMULATION
        this._setPhase(job, 'submitting', 'Submitting to processor (simulated)');
        const submit = await this._simulateSubmit(row, job);
        await this._sleep(this.runnerCooldownMs);
*/
this._setPhase(job, 'submitting', 'Submitting to processor');
const submit = await this._submitReal(row, job);

// Respect rate limits between real submissions (adjust as needed)
await this._sleep(this.runnerSubmitCooldownMs ?? this.runnerCooldownMs ?? 600);


       // After a successful "submit"
if (submit.ok) {
  job.phase = 'done';
  job.msg = 'Enqueued';
  row._status = 'queued';
  row._requestId = submit.requestId;
  this.runnerStats.ok++;
  this._log(`Row #${(row?.rowIndex ?? -1)+1}: enqueued (#${submit.requestId})`);

  // Deselect successful items
  row._sel = false;

    try {
    const tx = (row.txHash || '').trim();
    const to = (row.toAddress && row.toAddress !== '—') ? row.toAddress.trim() : null;
    if (tx) {
      this.userRequests.unshift({
        request_id: null,
        transaction_hash: tx,
        to_address: to,
        request_type: this.computeRequestType(row),
        mode: this.bulkMode || 'bucket',
        status: 'queued',
        request_time: new Date().toISOString(),
        result: null,
        bug_reported: 'no',
        bug_status: null
      });
      this.buildHistoryIndex();          // reindex
      this.annotateRowWithHistory(row);  // re-annotate this row
    }
  } catch (_) { console.log("catch"); }

} else {
  job.phase = 'failed';
  job.msg = submit.reason || 'Submit failed';
  row._status = 'error';
  row._reason = job.msg;
  this.runnerStats.failed++;
  this._log(`Row #${(row?.rowIndex ?? -1)+1}: failed - ${job.msg}`);

  // Keep selection to allow an immediate retry if the user wants
}

      } catch {
        job.phase = 'failed';
        job.msg = 'Unexpected error';
        if (row) { row._status = 'error'; row._reason = 'Unexpected error'; }
        this.runnerStats.failed++;
        this._log(`Row #${(row?.rowIndex ?? -1)+1}: unexpected error`);
      }
    }

    this.runnerActiveIndex = -1;

    if (this.runnerCancelled) {
      // Mark all remaining as skipped: cancelled
      for (let j = 0; j < this.runnerJobs.length; j++) {
        const job = this.runnerJobs[j];
        if (job.phase === 'queued' || job.phase === 'resolving' || job.phase === 'checking' || job.phase === 'submitting') {
          job.phase = 'skipped';
          job.msg = 'Cancelled';
          if (job.row) { job.row._status = 'skipped'; job.row._reason = 'Cancelled'; }
          this.runnerStats.skipped++;
        }
      }
      this.runnerState = 'cancelled';
      const { ok, failed, skipped, total } = this.runnerStats;
      const msg = `Batch cancelled: ${ok} ok, ${failed} failed, ${skipped} skipped (total ${total}).`;
      this._log(msg);
      this.notify(msg, 'warn');
       await this._sleep(450 + Math.floor(Math.random()*300));

  // NEW: one-time refresh
  await this._postRunnerRefresh();
  this.pollState.enabled = true;
this._startInterpreterPolling(true);

    } else {
      this.runnerState = 'complete';
      const { ok, failed, skipped, total } = this.runnerStats;
      const msg = `Batch complete: ${ok} ok, ${failed} failed, ${skipped} skipped (total ${total}).`;
      this._log(msg);
      this.notify(msg, failed ? 'warn' : 'success');
       await this._sleep(450 + Math.floor(Math.random()*300));

await this.initHistoryCache();
if (this.analysis?.withdrawals) {
  this.analysis.withdrawals.forEach(w => this.annotateRowWithHistory(w));
}

this.pollState.enabled = true;
this._startInterpreterPolling(true);


    }
  },
  // Pull latest queue/history, rebuild indexes, re-annotate current rows, refresh credits.
async _postRunnerRefresh({
  reloadRequests = true,
  reannotateRows = true,
  refreshCredits = true,
} = {}) {
  try {
    const auth = this._getAuth && this._getAuth();
    if (reloadRequests && auth) {
      const all = await this._fetchAllRequests(auth.username, auth.token, 200, 6);
      this.userRequests = all;
      this.buildHistoryIndex();
    }

    if (reannotateRows && this.analysis && Array.isArray(this.analysis.withdrawals)) {
      this.analysis.withdrawals.forEach(w => this.annotateRowWithHistory(w));
    }

    if (refreshCredits && typeof this.refreshUserCredits === 'function') {
      await this.refreshUserCredits();
    }
  } catch (e) {
    console.debug('[post-run refresh] skipped:', e?.message || e);
  }
}
,
  toggleRunnerPause() {
    if (this.runnerState !== 'running' && this.runnerState !== 'paused') return;
    this.runnerPaused = !this.runnerPaused;
    this.runnerState = this.runnerPaused ? 'paused' : 'running';
    this._log(this.runnerPaused ? 'Paused' : 'Resumed');
  },

  cancelRunner() {
    if (this.runnerState === 'cancelled' || this.runnerState === 'complete') return;
    this.runnerCancelled = true;
    this.runnerPaused = false;     // make sure we don’t look "paused"
    this._log('Cancel requested');
  },closeRunnerModal() {
    this.runnerVisible = false;
    this.runnerState = 'idle';
    this.runnerJobEls.clear();
  },

  onRunnerBackdrop() {
    // If running/paused, clicking backdrop requests cancel; after cancel/complete, it closes.
    if (this.runnerState === 'running' || this.runnerState === 'paused') {
      this.cancelRunner();
    } else {
      this.closeRunnerModal();
    }
  },
    // Phase helpers
    _setPhase(job, phase, msg) {
      job.phase = phase;
      job.msg = msg || '';
    },
    phaseLabel(phase) {
      switch (phase) {
        case 'queued': return 'Queued';
        case 'resolving': return 'Resolving';
        case 'checking': return 'Checking';
        case 'submitting': return 'Submitting';
        case 'done': return 'Done';
        case 'failed': return 'Failed';
        case 'skipped': return 'Skipped';
        default: return '—';
      }
    },
      jobHoverTitle(job) {
      const rowNo = job?.row?.rowIndex != null ? `Row #${job.row.rowIndex + 1}` : 'Row';
      return `${rowNo} • ${this.phaseLabel(job.phase)}${job.msg ? ' - ' + job.msg : ''}`;
    },

    // Determine whether this row should try a resolve step
  _rowNeedsResolve(row) {
  if (!row) return false;
  // Address resolve permitted?
  const needsAddr = (!row.toAddress || row.toAddress === '—') && !!row.txHash && row.amountRaw != null && canRowRunResolve(row);
  // Hash lookup permitted?
  const needsHash = !!row.canLookupTxHash && !row.txHash && canRowLookupHash(row);
  return needsAddr || needsHash;
},

    // === Simulated steps (replace later with real logic) ===

    async _simulateResolve(row, job) {
      // Visual pacing
      await this._sleep(450 + Math.floor(Math.random()*300));

      // 15% chance "resolution not needed" or no-op
      if (Math.random() < 0.15) {
        job.msg = 'Nothing to resolve';
        return;
      }

      // 20% chance "no match found"
      if (Math.random() < 0.20) {
        job.msg = 'Could not resolve';
        row.resolveMsg = 'No match found';
        return;
      }

      // Otherwise pretend we resolved missing destination or hash
      if (!row.toAddress || row.toAddress === '—') {
        row.toAddress = '0x' + Array.from(crypto.getRandomValues(new Uint8Array(20)))
          .map(b => b.toString(16).padStart(2,'0')).join('');
      }
      if (!row.txHash && row.canLookupTxHash) {
        row.txHash = '0x' + Array.from(crypto.getRandomValues(new Uint8Array(32)))
          .map(b => b.toString(16).padStart(2,'0')).join('');
      }
      row.resolveMsg = 'Resolved (simulated)';
      job.msg = 'Resolved';
    },

    async _simulateChecks(row, job) {
      await this._sleep(300 + Math.floor(Math.random()*300));

      console.log(job);
      // Quick mock rules: require network + txHash + toAddress for "sendable"
      if (!this.isRowSendable(row)) {
        return { ok: false, reason: 'Not sendable' };
      }

      // Probability knob: 10% fail as proof-of-concept
      if (Math.random() < 0.10) {
        return { ok: false, reason: 'Failed quality checks (simulated)' };
      }
      return { ok: true };
    },

    async _simulateSubmit(row, job) {
      // Simulate rate-limited single-submission
      await this._sleep(400 + Math.floor(Math.random()*400));

      // Small failure chance
      if (Math.random() < 0.08) {
        return { ok: false, reason: 'Network busy (simulated)' };
      }
console.log(row + job);
      // Fake request ID for display
      const rid = Math.floor(Math.random()*900000 + 100000);
      return { ok: true, requestId: rid };
    },
     _log(s) { this.runnerLog.unshift(`[${new Date().toLocaleTimeString()}] ${s}`); },

     
     // Replace the old toggleSelectAll with a "valid-aware" setter
    setAllValid(checked) {
      const rows = this.analysis?.withdrawals || [];
      for (const r of rows) {
        if (this.isRowSelectable(r)) r._sel = !!checked;
      }
    },
 // Keyboard UX for ESC on runner modal too
  onGlobalKeydown(e) {
    // Existing credit modal ESC handling...
    if (this.showBulkCreditModal && (e.key === 'Escape' || e.key === 'Esc')) {
      e.preventDefault();
      this.onBulkModalCancel();
      return;
    }

    if (!this.runnerVisible) return;

    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      if (this.runnerState === 'running' || this.runnerState === 'paused') {
        this.cancelRunner();
      } else {
        this.closeRunnerModal();
      }
    }

    // Optional: Space toggles pause while running
    if (e.code === 'Space' && (this.runnerState === 'running' || this.runnerState === 'paused')) {
      e.preventDefault();
      this.toggleRunnerPause();
    }
  },
 // Track DOM nodes for auto-scroll
  setJobRef(el, idx) {
    if (el) this.runnerJobEls.set(idx, el);
    else this.runnerJobEls.delete(idx);
  },

 
    async refreshUserCredits() {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get('/etherscan/get_credits.php', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.status === 'success') {
        this.creditBalance = res.data.balance;
        this.subscriptionExpiresAt = res.data.subscription_expires_at;

        const expires = this.subscriptionExpiresAt ? new Date(this.subscriptionExpiresAt) : null;
        // treat same as main tool: whole day active
        if (expires) expires.setHours(0, 0, 0, 0);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const isExpired = !expires || expires < today;

        this.isSubscriptionUser = !!res.data.is_subscription_user && !isExpired;
        this.isUnlimitedUser   = !!res.data.is_unlimited_user;
        this.role              = res.data.role;

        // days remaining (prefer server)
        let derivedDays = 0;
        if (this.isSubscriptionUser && this.subscriptionExpiresAt) {
          const diffMs = new Date(this.subscriptionExpiresAt) - new Date();
          derivedDays = diffMs > 0 ? Math.floor(diffMs / (1000 * 60 * 60 * 24)) : 0;
        }
        const serverDays = (typeof res.data.subscriptionDays === 'number' && res.data.subscriptionDays >= 0)
          ? res.data.subscriptionDays
          : null;
        this.subscriptionDays = serverDays ?? derivedDays;
      } else {
        console.warn('Could not fetch credit/subscription info.');
      }
    } catch (err) {
      console.error('Error fetching credit info:', err);
    }
  },
   // Price fetch for a specific mode (per-request)
  // Price fetch for a specific mode (per-request), using the same contract
// as the main Analysis tool's modal (fetch_platform_settings.php).
async fetchBulkUnitPrice(mode) {
  if (this.isUnlimitedUser || this.isSubscriptionUser) {
    this.bulkPrice = {
      baseCost: 0,
      discountCost: 0,
      discountLabel: null,
      discountStartsUtc: null,
      discountEndsUtc: null,
      serverNowUtc: new Date().toISOString(),
    };
    this.bulkUnitPriceBusy = false;
    this.bulkUnitPriceErr  = '';
    return;
  }

  this.bulkUnitPriceBusy = true;
  this.bulkUnitPriceErr  = '';
  this.bulkPrice = null;

  try {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Not authenticated');

    const res = await axios.get('/etherscan/fetch_platform_settings.php', {
      params: {
        context: 'main_analysis',
        mode, // 'bucket' (Normal) or 'normal' (Advanced)
      },
      headers: { Authorization: `Bearer ${token}` },
    });

    const data = res.data || {};
    if (data.status !== 'success' || !data.pricing) {
      throw new Error(data.message || 'Missing pricing data');
    }

    const p = data.pricing || {};
    const mapped = {
      baseCost: Number(p.base_cost ?? p.baseCredits ?? 0),
      discountCost:
        p.discount_cost != null
          ? Number(p.discount_cost)
          : p.discountCredits != null
          ? Number(p.discountCredits)
          : null,
      discountLabel: p.discount_label || p.discountLabel || '',
      discountStartsUtc: p.discount_starts_utc || p.discountStartsUtc || null,
      discountEndsUtc: p.discount_ends_utc || p.discountEndsUtc || null,
      serverNowUtc: data.server_now_utc || data.serverNowUtc || null,
    };

    // Sanity: if discount is not actually cheaper, drop it
    if (
      mapped.discountCost != null &&
      (isNaN(mapped.discountCost) || mapped.discountCost >= mapped.baseCost)
    ) {
      mapped.discountCost = null;
    }

    if (!mapped.baseCost || !mapped.serverNowUtc) {
      throw new Error('Incomplete pricing response');
    }

    this.bulkPrice = mapped;
  } catch (e) {
    console.error('Pricing fetch error', e);
    this.bulkPrice = null;
    this.bulkUnitPriceErr = 'Unable to fetch pricing right now';
  } finally {
    this.bulkUnitPriceBusy = false;
  }
},
finalizeAnalysisInit() {
  const rows = this.analysis?.withdrawals || [];
  for (const r of rows) {
    // Reset ephemeral UI fields
    r._status     = r._status ?? null;     // 'queued' | 'error' | 'needs_input' | 'skipped' | 'complete' | null
    r._reason     = r._reason ?? '';
    r._requestId  = r._requestId ?? null;
    r.resolveBusy = false;
    // keep any prior resolveMsg you want to show; otherwise uncomment:
    // r.resolveMsg  = '';

    // Auto-select anything ACTIONABLE (direct-send OR resolve-first OR hash-lookup),
    // but never re-select exact duplicates the user already ran.
    const alreadyRanExact = !!(r._history && r._history.exact);
    r._sel = this.rowIsActionable(r) && !alreadyRanExact;
  }
}
,
    // === Bulk submit flow ===
 // User clicked "Submit N checked" -> open modal, refresh account + pricing
    async beginBulkSubmit() {
      if (!this.selectedCount) return;

         const rows = this.selectedRowsSendable;
    if (!rows.length) {
      this.notify('No valid selections to submit.', 'warn');
      return;
    }

      // remember focus, then open
      this.lastFocusedEl = document.activeElement;     // NEW

    this.stagedRows = rows.slice(); // snapshot of references
    this.showBulkCreditModal = true;
      this.creditModalBusy = true;

      const remembered = localStorage.getItem('pt_bulk_mode');
      if (remembered === 'bucket' || remembered === 'normal') this.bulkMode = remembered;

      try {
        await this.refreshUserCredits();
        await this.fetchBulkUnitPrice(this.bulkMode);
      } finally {
        this.creditModalBusy = false;
        this.$nextTick(() => {
          // Prefer focusing close button; fall back to overlay
          const btn = this.$refs.bulkCloseBtn;
          if (btn && typeof btn.focus === 'function') btn.focus();
          else if (this.$refs.bulkModalOverlay && typeof this.$refs.bulkModalOverlay.focus === 'function') {
            this.$refs.bulkModalOverlay.focus();
          }
        });
      }
    },
  // Mode changed inside modal -> refetch price and persist preference
  async onBulkModeChange() {
    localStorage.setItem('pt_bulk_mode', this.bulkMode);
    await this.fetchBulkUnitPrice(this.bulkMode);
  },
 onBulkModalCancel() {
      this.showBulkCreditModal = false;
      // restore focus to previous control
      if (this.lastFocusedEl && typeof this.lastFocusedEl.focus === 'function') {
        this.lastFocusedEl.focus();
      }
      this.lastFocusedEl = null;
    },

  // small helper
  notify(msg, type='info') { this.$emit('notify', { msg, type }); },

     
// Replace your old isRowSendable with the strict direct-send gate:
isRowSendable(row) {
  return this.canSendDirect(row);
},

isRowSelectable(row) {
  return this.rowIsActionable(row);
},

  toggleSelectAll(checked) {
    const rows = this.analysis?.withdrawals || [];
    for (const r of rows) {
      if (!this.isRowSendable(r)) continue;
      r._sel = !!checked;
    }
  },
  clearSelection() {
    const rows = this.analysis?.withdrawals || [];
    for (const r of rows) r._sel = false;
  },
  
      // --- AUTH + FETCH ----------------------------------------------------------
  _getAuth() {
    const username = localStorage.getItem('username');
    const token    = localStorage.getItem('token');
    const ok = !!username && !!token;
    this.isLoggedIn = ok;
    return ok ? { username, token } : null;
  },

  async initHistoryCache() {
    const auth = this._getAuth();
    if (!auth) return; // not logged in, skip

    try {
      // Pull enough recent requests to be useful; paginated fetch with a hard cap
      const all = await this._fetchAllRequests(auth.username, auth.token, /*limit*/200, /*maxPages*/6);
      this.userRequests = all;
      this.buildHistoryIndex();

      // If results already exist, annotate rows now
      if (this.analysis && Array.isArray(this.analysis.withdrawals)) {
        this.analysis.withdrawals.forEach(w => this.annotateRowWithHistory(w));
      }
    } catch (e) {
      // Stay silent; this is a UX enhancement, not core-path critical
      console.debug('[history-cache] skipped:', e?.message || e);
    }
  },

  async _fetchAllRequests(username, token, limit = 200, maxPages = 6) {
    const headers = { Authorization: `Bearer ${token}` };
    let page = 1;
    let total = null;
    const out = [];

    while (page <= maxPages) {
      const resp = await axios.get('etherscan/fetch_requests.php', {
        params: { username, page, limit },
        headers
      });
      if (!resp?.data || resp.data.status !== 'success') break;

      const { requests = [], total: t } = resp.data;
      out.push(...requests);
      if (total == null && Number.isFinite(+t)) total = +t;

      // Stop if we’ve reached the end
      const totalPages = total ? Math.ceil(total / limit) : (requests.length ? page + 1 : page);
      if (page >= totalPages) break;

      page += 1;
    }
    return out;
  },
/*
  buildHistoryIndex() {
    const exact = new Set();        // key: `${tx}|${to}`
    const byTx  = new Map();        // key: tx -> count
    const byTo  = new Map();        // key: to -> count

    const norm = v => String(v || '').trim().toLowerCase();

    // Only consider meaningful statuses (already run or running).
    const meaningful = new Set(['complete','processing','in progress','queued','pending']); // in progress might be meaningful 

    for (const r of this.userRequests) {
      const status = norm(r.status);
      if (!meaningful.has(status)) continue;

      const tx = norm(r.transaction_hash);
      const to = norm(r.to_address);

      if (tx) byTx.set(tx, (byTx.get(tx) || 0) + 1);
      if (to) byTo.set(to, (byTo.get(to) || 0) + 1);
      if (tx && to) exact.add(`${tx}|${to}`);
    }

    this.historyIndex = {
      exact,
      byTx,
      byTo,
      total: this.userRequests.length,
      ts: Date.now()
    };
  },*/
 buildHistoryIndex() {
  const exact = new Set();        // existing: `${tx}|${to}`
  const byTx  = new Map();        // existing: tx -> count
  const byTo  = new Map();        // existing: to -> count
  const byExactBest = new Map();  // NEW: key `${tx}|${to}` -> best request object
  const byTxBest = new Map();     // NEW: fallback if only tx matches
  const byToBest = new Map();     // NEW: fallback if only to matches

  const norm = v => String(v || '').trim().toLowerCase();

  const meaningful = new Set(['complete','processing','in progress','queued','pending']);

  // Prefer: complete > has result > newer > advanced (slight)
  const scoreRequest = (r) => {
    let s = 0;
    if (String(r.status).toLowerCase() === 'complete') s += 1000;
    if (r.result && String(r.result).length) s += 100;
    const ts = new Date(r.request_time || 0).getTime();
    if (Number.isFinite(ts)) s += (ts / 1e9); // coarse recency bump
    if (String(r.mode) !== 'bucket') s += 5;  // nudge advanced
    return s;
  };

  const considerBest = (map, key, req) => {
    const prev = map.get(key);
    if (!prev || scoreRequest(req) > scoreRequest(prev)) {
      map.set(key, req);
    }
  };

  for (const r of this.userRequests) {
    const status = norm(r.status);
    if (!meaningful.has(status)) continue;

    const tx = norm(r.transaction_hash);
    const to = norm(r.to_address);

    if (tx) byTx.set(tx, (byTx.get(tx) || 0) + 1);
    if (to) byTo.set(to, (byTo.get(to) || 0) + 1);
    if (tx && to) {
      const k = `${tx}|${to}`;
      exact.add(k);
      considerBest(byExactBest, k, r);
    }
    if (tx) considerBest(byTxBest, tx, r);
    if (to) considerBest(byToBest, to, r);
  }

  this.historyIndex = {
    exact,
    byTx,
    byTo,
    byExactBest, // NEW
    byTxBest,    // NEW
    byToBest,    // NEW
    total: this.userRequests.length,
    ts: Date.now()
  };
},

/*
  annotateRowWithHistory(row) {
    if (!this.historyIndex) return;
    const norm = v => String(v || '').trim().toLowerCase();

    const tx = norm(row.txHash);
    const to = norm(row.toAddress === '—' ? '' : row.toAddress);

    const exact  = (tx && to) ? this.historyIndex.exact.has(`${tx}|${to}`) : false;
    const txSeen = tx ? this.historyIndex.byTx.has(tx) : false;
    const toSeen = to ? this.historyIndex.byTo.has(to) : false;

    row._history = { exact, txSeen, toSeen };
  },*/
annotateRowWithHistory(row) {
  if (!this.historyIndex) return;
  const norm = v => String(v || '').trim().toLowerCase();

  const tx = norm(row.txHash);
  const to = norm(row.toAddress === '—' ? '' : row.toAddress);

  const key = (tx && to) ? `${tx}|${to}` : null;

  const exact  = key ? this.historyIndex.exact.has(key) : false;
  const txSeen = tx ? this.historyIndex.byTx.has(tx) : false;
  const toSeen = to ? this.historyIndex.byTo.has(to) : false;

  // Pick “best” prior request if we have one
  let best = null;
  if (key) best = this.historyIndex.byExactBest.get(key) || null;
  if (!best && tx) best = this.historyIndex.byTxBest.get(tx) || null;
  if (!best && to) best = this.historyIndex.byToBest.get(to) || null;

  row._history = {
    exact,
    txSeen,
    toSeen,
    bestRequestId: best?.request_id || null,
    bestStatus: best?.status || null,
    bestHasResult: !!(best?.result),
    best // keep for inline view
  };
},




 canSendToAnalysis(row) {
   // NEW: if we know it’s already been run with this (tx,to) pair, do not allow sending
  if (row && row._history && row._history.exact) return false;


    const hasTx = !!(row.txHash && String(row.txHash).trim());
    const hasTo = !!(row.toAddress && row.toAddress !== '—' && String(row.toAddress).trim());
    return hasTx || hasTo;
  }, 
  
  
  shouldShowSendButton(row) {
    // 0) If we can’t even prefill (no hash and no destination), don’t show.
    if (!this.canSendToAnalysis(row)) return false;

    const asset = row.assetSymbol || '';
    const net   = row.network || '';

    // 1) If network inference already says BTC / ETH-like / TRON, allow.
    if (isSupportedNetworkTag(net)) return true;

    // 2) No detected asset? Show (let the user try BTC/ETH/TRON in the Analysis tool).
    const ticker = normalizeTicker(asset);
    if (!ticker) return true;

    // 3) Core assets are allowed (BTC, ETH, USDT, TRX).
    if (isCoreAsset(ticker)) return true;

    // 4) Well-known ERC20s (USDC, DAI, LINK, etc.) are allowed.
    if (isWellKnownErc20(ticker)) return true;

    // 5) Explicit blocklist (XRP, SOL, …) -> hide.
    if (isExplicitlyUnsupported(ticker)) return false;

    // 6) Default: hide to avoid unsupported chains/tokens.
    return false;
  },

  _cleanAlnum(s, maxLen) {
    if (!s) return '';
    const out = String(s).replace(/[^A-Za-z0-9]/g, '');
    return typeof maxLen === 'number' ? out.slice(0, maxLen) : out;
  },

  sendToAnalysis(row) {
    // Prepare a compact payload; expand later if you want (asset, amount, network)
    const txRaw = (row.txHash || '').trim();
    const toRaw = (row.toAddress === '—') ? '' : (row.toAddress || '').trim();

    const payload = {
      tx: this._cleanAlnum(txRaw, 66), // keeps 0x… up to 66 chars
      to: this._cleanAlnum(toRaw, 64)  // alnum only to match your input pattern
    };

    try {
      sessionStorage.setItem('pt:analysisPrefill', JSON.stringify(payload));
    } catch (_) {
      // fallback: do nothing; user can still copy manually later if needed
    }

    // Navigate without query params
    this.$router.push({ path: '/AnalysisTool' });
  },
 toggleAdvancedPanel() {
    this.showAdvanced = !this.showAdvanced;
    if (!this.showAdvanced) this.showOverridesPanel = false;
  },

   enqueueWork(taskType, row) {
  if (!row) return;

  if (taskType === 'addr') {
    if (!canRowRunResolve(row)) {
      row.resolveMsg = 'Unsupported for resolve';
      return;
    }
  } else if (taskType === 'txhash') {
    if (!canRowLookupHash(row)) {
      row.resolveMsg = 'Unsupported for hash lookup';
      return;
    }
  }

  const alreadyQueued = this.resolveQueue.some(it => it && it.type === taskType && it.row === row);
  if (alreadyQueued || row.resolveBusy) {
    row.resolveMsg = 'Already queued or processing';
    return;
  }
  if (this.resolveQueue.length >= this.maxQueuedResolves) {
    row.resolveMsg = 'Please wait... queue is full';
    return;
  }

  this.resolveQueue.push({ type: taskType, row });
  row.resolveMsg = this.resolveProcessing ? `Queued (#${this.resolveQueue.length})` : 'Queued';
  if (!this.resolveProcessing) this.processResolveQueue();
},

enqueueLookupTxHash(row) {
  this.enqueueWork('txhash', row);
},

// Normalize whatever is currently in the grid so analysis behaves like the upload path
prepareFromSheet() {
  const hot = this.$refs.outputSpreadsheet;
  if (!hot || typeof hot.getData !== 'function') return null;

  const rows = hot.getData() || [];
  return Array.isArray(rows) ? rows : [];
},

    _sleep(ms) { return new Promise(r => setTimeout(r, ms)); },
enqueueResolve(row) {
  this.enqueueWork('addr', row);
},

async processResolveQueue() {
  this.resolveProcessing = true;
  try {
    while (this.resolveQueue.length > 0) {
      const it = this.resolveQueue.shift(); // { type, row }
      const row = it && it.row;

      // Update "Queued (#N)" for remaining items
      this.resolveQueue.forEach((q, i) => {
        const r = q && q.row;
        if (r && !r.resolveBusy) r.resolveMsg = `Queued (#${i + 1})`;
      });

      if (row) {
        if (it.type === 'addr') {
          await this._performResolve(row);        // your existing address-lookup by tx hash
        } else if (it.type === 'txhash') {
          await this._performLookupTxHash(row);   // new hash-lookup implementation hook
        }
      }

      // Global cooldown shared by all tasks
      await this._sleep(this.resolveCooldownMs);
    }
  } finally {
    this.resolveProcessing = false;
  }
},


  async _performLookupTxHash(row) {
    if (!canRowLookupHash(row)) { row.resolveMsg = 'Unsupported'; return; }
  row.resolveBusy = true;
  row.resolveMsg = 'Finding hash...';

  try {
    // Normalize network for the API
    let net = (row.network || '').toString().toLowerCase();
    if (net === 'eth-like' || net === 'ethlike' || net === 'evm') net = 'ethereum';
    if (net === 'btc' || net === 'bitcoin') net = 'bitcoin';
    if (net === 'tron' || net === 'trc20' || net === 'trx') net = 'tron';

const c = row.lookupContext || {};
const payload = {
  network: net,
  toAddress: c.toAddress || row.toAddress,
  amount: c.amountAbs,
  asset: row.assetSymbol || c.assetSymbol || '',
  timestamp: (typeof c.timestampEpoch === 'number' ? c.timestampEpoch : (c.timestamp || null)),
  debug: true
};

    // Quick guards so we fail fast and keep the queue moving
    if (!payload.toAddress || payload.amount == null || !payload.asset) {
      row.resolveMsg = 'Insufficient info';
      return;
    }

    const resp = await fetch(LOOKUP_TXHASH_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    let json = null;
    try { json = await resp.json(); } catch (_) { console.log("eslint says no empty block statements like {}")}
if (json && json.debug) {
    row._lookupDebugText = json.debug_text;
  // 1) Print as one clean, copyable block in console
  console.log("\n========== LOOKUP DEBUG ==========\n" + json.debug_text + "\n==================================\n");

} else {
  console.log("lookup has nothing for debug");
}
    if (json && json.status === 'success' && json.txHash) {
      row.txHash = json.txHash;
      // If the endpoint returns a normalized network, prefer it
      if (json.network) row.network = (json.network === 'ethereum' ? 'ETH-like' : json.network.toUpperCase());
      row.resolveMsg = 'Match found';
      // NEW: immediately re-annotate against history
      if (this.historyIndex) this.annotateRowWithHistory(row);
    } else {
      row.resolveMsg = (json && json.message) ? json.message : 'No matching transaction found';
    }
  } catch (e) {
    row.resolveMsg = 'Lookup error';
  } finally {
    row.resolveBusy = false;
  }
},
/**
 * Detect "TxID + From/To + DateTime + # of Coins + Coin Name" CSV.
 *
 * Expected columns (order may vary, but these labels are typical):
 *   A: Txn #                  -> internal id (ignore for txHash)
 *   B: TXID                   -> blockchain hash
 *   C: "From" Wallet Address  -> source (not used as destination)
 *   D: "TO" Wallet ADDRESS    -> destination address
 *   E: Date & Time            -> timestamp
 *   F: # of Coins             -> token/native amount (units, not fiat)
 *   G: Coin Name              -> asset symbol/name
 */



    isAllowedTabularUpload(file) {
      const name = String(file?.name || '').toLowerCase();
      if (!name) return false;
      return name.endsWith('.csv') || name.endsWith('.tsv') || name.endsWith('.txt');
    },
    onFileInput(e) {
      const file = e.target.files && e.target.files[0];
      if (file) this.handleFile(file);
      e.target.value = '';
    },
  onDragEnter(e) {
  this.dragDepth++;

  const dt = e.dataTransfer;
  const file = dt?.files?.[0] || null;

  let csvOk = false;

  if (file) {
    csvOk = isCsvLike(file);
  } else if (dt?.types) {
    const types = Array.from(dt.types || []);
    csvOk = types.includes('Files');
  }

  this.isDragOver = true;
  this.isDragInvalid = !csvOk;
  this.hoverFilename = csvOk && file?.name ? file.name : '';
},

onDragOver(e) {
  const dt = e.dataTransfer;
  const file = dt?.files?.[0] || null;

  let csvOk = false;

  if (file) {
    csvOk = isCsvLike(file);
  } else if (dt?.types) {
    const types = Array.from(dt.types || []);
    csvOk = types.includes('Files');
  }

  this.isDragOver = true;
  this.isDragInvalid = !csvOk;

  if (dt) {
    dt.dropEffect = csvOk ? 'copy' : 'none';
  }

  this.hoverFilename = csvOk && file?.name ? file.name : '';
},

onDrop(e) {
  const dt = e.dataTransfer;
  const file = dt?.files?.[0] || null;

  this.resetDragState();

  if (!file) {
    this.errorMessage = 'No file was dropped.';
    return;
  }

if (!isCsvLike(file)) { this.errorMessage = 'Only CSV, TSV, or TXT files are allowed.'; return; }

  this.handleFile(file);
},
    resetDragState() {
      this.isDragOver = false;
      this.isDragInvalid = false;
      this.dragDepth = 0;
      this.hoverFilename = '';
    },
    async handleFile(file) {
      if (this.isAnalyzing) { this.errorMessage = 'Please wait a moment, currently analyzing.'; return; }

      this.errorMessage = '';
      this.analyzeError = '';
      this.analysis = null;
      this.headerRowIdx = -1;
      this.headerRaw = [];
      this.headerNorm = [];
      this.detectionStats = { txHashScore: [], addrScore: [], amountScore: [] };
      this.selectedCols = { txHash: -1, toAddress: -1, transferType: -1, asset: -1, amount: -1 };
      this.showOverridesPanel = false;

      if (!this.isAllowedTabularUpload(file)) { this.errorMessage = 'Only CSV, TSV, or TXT files are allowed.'; return; }
      if (file.size > this.MAX_BYTES) { this.errorMessage = `File too large. Max ${Math.round(this.MAX_BYTES / (1024 * 1024))} MB.`; return; }
      if (file.name && file.name.startsWith('.')) { this.errorMessage = 'Hidden/system files are not allowed.'; return; }

      try {
        const sample = await file.slice(0, 2048).text();
        if (sample.includes('\0') || isLikelyBinary(sample)) {
          this.errorMessage = 'The file appears to be binary. Please provide a plain-text CSV, TSV, or TXT file.';
          return;
        }

        const parsed = await parseTabularFile(file, { mode: 'csv' });
        let rows = Array.isArray(parsed?.rows) ? parsed.rows : [];
        rows = rows.slice(0, this.MAX_ROWS).map((row) => (Array.isArray(row) ? row.slice(0, this.MAX_COLS) : []));
        this.pushIntoSpreadsheet(rows);

        if (this.autoAnalyzeOnLoad) {
          await this.analyze();
          return;
        }

        const preview = analyzeTabularTransactions(rows, {
          selectedCols: this.selectedCols,
          assumeAllWithdrawalsWhenNoType: this.assumeAllWithdrawalsWhenNoType,
          strictAnchorValidation: this.strictAnchorValidation,
          treatMethodTransferAsWithdrawal: this.treatMethodTransferAsWithdrawal,
          maxRows: this.MAX_ROWS,
          maxCols: this.MAX_COLS,
          maxCellChars: this.MAX_CELL_CHARS,
          trimWhitespace: this.trimWhitespace,
          neutralizeFormulas: this.neutralizeFormulas
        });

        this.headerRowIdx = Number.isInteger(preview?.headerRowIndex) ? preview.headerRowIndex : -1;
        this.headerRaw = Array.isArray(preview?.headerRaw) ? [...preview.headerRaw] : [];
        this.headerNorm = Array.isArray(preview?.headerNorm) ? [...preview.headerNorm] : [];
        this.detectionStats = { ...(preview?.detectionStats || {}) };
        this.selectedCols = { ...(preview?.selectedCols || preview?.columns || this.selectedCols) };
      } catch (err) {
        this.errorMessage = `Parse failed: ${String((err && err.message) || err)}`;
      }
    },
   
    pushIntoSpreadsheet(rows) {
      if (this.$refs.outputSpreadsheet && typeof this.$refs.outputSpreadsheet.setData === 'function') {
        this.$refs.outputSpreadsheet.setData(rows);
      } else {
        this.errorMessage = 'Spreadsheet component is missing setData(rows). Please expose it.';
      }
    },
// Heuristic: find another address-like column to consider as "To" if we cannot
// reliably identify a From column from headers (or if the mapped To looks wrong).
 findAltAddressColumn(rows, dataStart, excludeCols = [], maxScan = 200) {
  const totalCols = rows.reduce((m, r) => Math.max(m, (r ? r.length : 0)), 0);
  const excluded = new Set(excludeCols.filter(i => i >= 0));
  const candidates = [];

const looksLikeAddress = (val) => {
  const s = String(val || '').trim();
  if (!s) return false;
  if (/^0x[a-fA-F0-9]{40}$/.test(s)) return true;                 // EVM (ETH, AVAX-C, Base, etc.)
  if (/^bc1[0-9ac-hj-np-z]{25,62}$/i.test(s)) return true;        // BTC bech32
  if (/^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(s)) return true;   // BTC legacy
  if (/^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(s)) return true;         // Tron
  if (/^(X|P|C)-avax1[0-9ac-hj-np-z]{20,}$/i.test(s)) return true;// Avalanche X/P/C
  return false;
};


  for (let c = 0; c < totalCols; c++) {
    if (excluded.has(c)) continue;
    let hits = 0, samples = 0;

    for (let r = dataStart; r < rows.length && samples < maxScan; r++) {
      const cell = rows[r]?.[c];
      if (cell == null || cell === '') continue;
      samples++;

      // Try to extract an embedded address first, then validate.
      const text = String(cell);
      const embedded = extractFirstAddressFromText(text) || text;
      if (looksLikeAddress(embedded)) hits++;
    }

    if (samples > 0) {
      const rate = hits / samples;
      // Require at least a modest signal to avoid picking random text columns
      if (hits >= 3 && rate >= 0.50) {
        candidates.push({ col: c, hits, rate });
      }
    }
  }

  if (!candidates.length) return -1;
  candidates.sort((a, b) => (b.rate - a.rate) || (b.hits - a.hits));
  return candidates[0].col;
},
   

async evmCandidateMatches(txHash, candidateAddr, network, amountAbs, assetSymbol) {
  if (!candidateAddr) return false;
  const cand = String(candidateAddr).toLowerCase();

  const facts = await this.fetchTxFactsEvm(txHash, network, amountAbs, assetSymbol);
  if (!facts) return false;

  if (facts.mainTo && facts.mainTo === cand) return true;
  if (facts.logs && facts.logs.includes(cand)) return true;
  if (facts.traces && facts.traces.includes(cand)) return true;

  // If the resolver "final" to (selected by amount) equals candidate, accept that too.
  if (facts.to && facts.to === cand) return true;

  return false;
}
,

    clearHighlights() {
      this.$refs.outputSpreadsheet?.clearRecolors?.();
    },

    syncSharedAnalysisMeta(sharedAnalysis) {
      this.headerRowIdx = Number.isInteger(sharedAnalysis?.headerRowIndex) ? sharedAnalysis.headerRowIndex : -1;
      this.headerRaw = Array.isArray(sharedAnalysis?.headerRaw) ? [...sharedAnalysis.headerRaw] : [];
      this.headerNorm = Array.isArray(sharedAnalysis?.headerNorm) ? [...sharedAnalysis.headerNorm] : [];
      this.detectionStats = { ...(sharedAnalysis?.detectionStats || {}) };
      this.selectedCols = { ...(sharedAnalysis?.selectedCols || sharedAnalysis?.columns || this.selectedCols) };
    },

    buildLegacyWithdrawalRows(sharedAnalysis, previousWithdrawals = []) {
      const previousByKey = new Map((previousWithdrawals || []).map((row) => [row?.key, row]));

      return (sharedAnalysis?.candidates || []).map((candidate) => {
        const key = candidate?.key || candidate?.id || `row-${candidate?.rowIndex ?? 'x'}-${candidate?.txHash || 'nohash'}-${candidate?.toAddress || 'noaddr'}`;
        const previous = previousByKey.get(key) || {};

        return {
          key,
          rowIndex: candidate?.rowIndex ?? previous?.rowIndex ?? -1,
          txHash: String(candidate?.txHash || '').trim(),
          toAddress: String(candidate?.toAddress || '').trim() || '—',
          transferType: String(candidate?.transferType || '').trim() || '—',
          network: String(candidate?.network || '').trim() || 'Unknown',
          amountDisplay: candidate?.amountDisplay ?? null,
          amountRaw: candidate?.amountRaw ?? null,
          assetSymbol: String(candidate?.assetSymbol || '').trim() || null,
          canLookupTxHash: Boolean(candidate?.canLookupTxHash),
          lookupContext: candidate?.lookupContext ? { ...candidate.lookupContext } : null,
          warnings: Array.isArray(candidate?.warnings) ? [...candidate.warnings] : [],
          resolveBusy: false,
          resolveMsg: previous?.resolveMsg || '',
          _history: previous?._history ? { ...previous._history } : { exact: false, txSeen: false, toSeen: false },
          _fromAddrCandidate: String(candidate?._fromAddrCandidate || candidate?.fromAddressCandidate || '').trim(),
          _sel: Boolean(previous?._sel),
          _status: previous?._status ?? null,
          _reason: previous?._reason || '',
          _requestId: previous?._requestId ?? null,
          _result: previous?._result ?? null,
        };
      });
    },

    repaintSharedAnalysisRows(rows, sharedAnalysis) {
      const hot = this.$refs.outputSpreadsheet;
      if (!hot) return;

      this.clearHighlights();

      const hasHeader = Boolean(sharedAnalysis?.hasHeader);
      const headerRowIndex = Number.isInteger(sharedAnalysis?.headerRowIndex) ? sharedAnalysis.headerRowIndex : 0;
      const columns = { ...(sharedAnalysis?.columns || {}) };
      const meta = sharedAnalysis?.meta || {};
      const notesCol = Number.isInteger(meta?.notesCol) ? meta.notesCol : -1;
      const candidates = Array.isArray(sharedAnalysis?.candidates) ? sharedAnalysis.candidates : [];

      const HASH_COLOR = '#6ba0c2';
      const ADDR_COLOR = '#d19a66';
      const TYPE_COLOR = '#2b5bd7';
      const ROW_COLOR = '#e6f7ff';
      const HDR_TX = '#fff3cd';
      const HDR_ADDR = '#ffe0b2';
      const HDR_TYPE = '#e1bee7';
      const HDR_AST = '#dcedc8';
      const HDR_AMT = '#c8e6c9';
      const HDR_NOTE = '#cfd8dc';

      [HASH_COLOR, ADDR_COLOR, TYPE_COLOR, ROW_COLOR, HDR_TX, HDR_ADDR, HDR_TYPE, HDR_AST, HDR_AMT, HDR_NOTE]
        .forEach((hex) => hot.ensureColorStyle?.(hex));

      const markHeader = (col, hex) => {
        if (hasHeader && typeof col === 'number' && col >= 0) {
          hot.setRangeClass?.(headerRowIndex, col, headerRowIndex, col, `color-${hex.slice(1)}`);
        }
      };

      markHeader(columns.txHash, HDR_TX);
      markHeader(columns.toAddress, HDR_ADDR);
      markHeader(columns.transferType, HDR_TYPE);
      markHeader(columns.asset, HDR_AST);
      markHeader(columns.amount, HDR_AMT);
      if (notesCol >= 0) markHeader(notesCol, HDR_NOTE);

      for (const candidate of candidates) {
        const rowIndex = Number.isInteger(candidate?.rowIndex) ? candidate.rowIndex : -1;
        if (rowIndex < 0 || rowIndex >= rows.length) continue;

        const row = rows[rowIndex] || [];
        const lastCol = Math.max((row || []).length - 1, 0);
        const mappedToValue = typeof columns.toAddress === 'number' && columns.toAddress >= 0
          ? String(row?.[columns.toAddress] || '').trim()
          : '';

        if (typeof columns.txHash === 'number' && columns.txHash >= 0 && candidate?.txHash) {
          hot.setRangeClass?.(rowIndex, columns.txHash, rowIndex, columns.txHash, `color-${HASH_COLOR.slice(1)}`);
        }
        if (typeof columns.toAddress === 'number' && columns.toAddress >= 0 && candidate?.toAddress) {
          hot.setRangeClass?.(rowIndex, columns.toAddress, rowIndex, columns.toAddress, `color-${ADDR_COLOR.slice(1)}`);
        }
        if (!mappedToValue && notesCol >= 0 && candidate?.toAddress) {
          hot.setRangeClass?.(rowIndex, notesCol, rowIndex, notesCol, `color-${ADDR_COLOR.slice(1)}`);
        }
        if (hasHeader && typeof columns.transferType === 'number' && columns.transferType >= 0) {
          hot.setRangeClass?.(rowIndex, columns.transferType, rowIndex, columns.transferType, `color-${TYPE_COLOR.slice(1)}`);
        }
        if (candidate?.txHash || candidate?.toAddress) {
          hot.setRangeClass?.(rowIndex, 0, rowIndex, lastCol, `color-${ROW_COLOR.slice(1)}`);
        }
      }
    },

    buildValidationNote(warnings = [], extraMessages = []) {
      return [...(warnings || []), ...(extraMessages || [])]
        .filter(Boolean)
        .join(' ');
    },

    async analyze() {
      this.extraFindings = null;
      this.analyzeError = '';
      this.isAnalyzing = true;

      const previousWithdrawals = this.analysis?.withdrawals || [];
      this.analysis = null;

      try {
        const hot = this.$refs.outputSpreadsheet;
        if (!hot || typeof hot.getData !== 'function') {
          this.analyzeError = 'Spreadsheet not ready.';
          return;
        }

        const rawRows = this.prepareFromSheet();
        if (rawRows === null) {
          this.analyzeError = 'Spreadsheet not ready.';
          return;
        }

        const sharedAnalysis = analyzeTabularTransactions(rawRows, {
          selectedCols: this.selectedCols,
          assumeAllWithdrawalsWhenNoType: this.assumeAllWithdrawalsWhenNoType,
          strictAnchorValidation: this.strictAnchorValidation,
          treatMethodTransferAsWithdrawal: this.treatMethodTransferAsWithdrawal,
          maxRows: this.MAX_ROWS,
          maxCols: this.MAX_COLS,
          maxCellChars: this.MAX_CELL_CHARS,
          trimWhitespace: this.trimWhitespace,
          neutralizeFormulas: this.neutralizeFormulas
        });

        const rows = Array.isArray(sharedAnalysis?.rows) ? sharedAnalysis.rows : [];
        if (!rows.length || !rows[0]?.length) {
          this.analyzeError = 'No data to analyze.';
          return;
        }

        this.pushIntoSpreadsheet(rows);
        this.syncSharedAnalysisMeta(sharedAnalysis);
        this.repaintSharedAnalysisRows(rows, sharedAnalysis);

        const columns = { ...(sharedAnalysis?.columns || {}) };
        const meta = sharedAnalysis?.meta || {};
        const flags = meta?.flags || {};
        const hasHeader = Boolean(sharedAnalysis?.hasHeader);
        const headerRowIdx = Number.isInteger(sharedAnalysis?.headerRowIndex) ? sharedAnalysis.headerRowIndex : 0;
        const notesCol = Number.isInteger(meta?.notesCol) ? meta.notesCol : -1;
        const timeCol = Number.isInteger(meta?.timeCol) ? meta.timeCol : -1;
        const directionCol = Number.isInteger(meta?.directionCol) ? meta.directionCol : -1;
        const fromAddressCol = Number.isInteger(meta?.fromAddressCol) ? meta.fromAddressCol : -1;

        const withdrawals = this.buildLegacyWithdrawalRows(sharedAnalysis, previousWithdrawals);
        const dataStartForExtras = hasHeader ? (headerRowIdx + 1) : 0;

        this.extraFindings = this.computeExtraFindings(
          rows,
          withdrawals,
          dataStartForExtras,
          {
            columns,
            headerRaw: this.headerRaw,
            headerNorm: this.headerNorm,
            notesCol,
            timeCol,
            directionCol,
            isLedgerCsvV1: !!flags.isLedgerCsvV1,
            isCsvTxidFromToV1: !!flags.isCsvTxidFromToV1,
            isCsvDebitCreditCashoutV1: !!flags.isCsvDebitCreditCashoutV1,
            isCsvActivityDetailsV1: !!flags.isCsvActivityDetailsV1,
            isCsvStandardSetC: !!flags.isCsvStandardSetC,
          }
        );

        if (this.historyIndex) {
          withdrawals.forEach((row) => this.annotateRowWithHistory(row));
        }

        let addrCheck = { ran: false, ok: true, sample: null, reason: 'no-supported-sample' };
        let swapped = false;
        let swapTried = false;
        let swapTriedCol = -1;

        const canValidateTo =
          (typeof columns.txHash === 'number' && columns.txHash >= 0) &&
          withdrawals.some((row) => row.txHash && row.network && row.network !== 'Unknown');

        if (canValidateTo) {
          const t0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
          addrCheck = await this.validateDetectedAddress(columns, withdrawals);
          const t1 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
          console.debug('[addr-validate] ran=%s ok=%s time=%sms sampleRow=%s',
            addrCheck.ran, addrCheck.ok, Math.round(t1 - t0),
            addrCheck.sample ? (addrCheck.sample.rowIndex + 1) : 'n/a'
          );

          if (addrCheck.ran && !addrCheck.ok && addrCheck.sample && addrCheck.sample.network === 'Ethereum') {
            const sample = addrCheck.sample;
            try {
              const okEvm = await this.evmCandidateMatches(
                sample.txHash,
                sample.toAddress,
                sample.network,
                sample.amountRaw,
                sample.assetSymbol
              );
              if (okEvm) {
                addrCheck.ok = true;
                addrCheck.reason = 'evm-log-proof';
              }
            } catch {
              console.log('catch');
            }
          }

          if (addrCheck.ran && !addrCheck.ok) {
            let altCol = (fromAddressCol >= 0)
              ? fromAddressCol
              : this.findAltAddressColumn(
                  rows,
                  hasHeader ? (headerRowIdx + 1) : 0,
                  [columns.txHash, columns.toAddress, columns.amount].filter((col) => typeof col === 'number' && col >= 0)
                );

            if (typeof altCol === 'number' && altCol >= 0 && altCol !== columns.toAddress) {
              swapTried = true;
              swapTriedCol = altCol;

              const oldToCol = columns.toAddress;
              columns.toAddress = altCol;

              this.applyToAddressSwapInPlace(columns, withdrawals, rows, altCol);

              if (this.historyIndex) {
                withdrawals.forEach((row) => this.annotateRowWithHistory(row));
              }

              const addrCheck2 = await this.validateDetectedAddress(columns, withdrawals);
              if (addrCheck2 && addrCheck2.ran && !addrCheck2.ok && addrCheck.sample && addrCheck.sample.network === 'Ethereum') {
                const txh = addrCheck.sample.txHash;
                const w2 = withdrawals.find((row) => row.txHash === txh);
                const mappedAfterSwap = w2 ? w2.toAddress : null;

                try {
                  const okEvm2 = await this.evmCandidateMatches(
                    txh,
                    mappedAfterSwap,
                    'Ethereum',
                    w2 ? w2.amountRaw : null,
                    w2 ? w2.assetSymbol : null
                  );
                  if (okEvm2) {
                    addrCheck2.ok = true;
                    addrCheck2.reason = 'evm-log-proof';
                  }
                } catch {
                  console.log('catch2');
                }
              }

              swapped = !!(addrCheck2 && addrCheck2.ran && addrCheck2.ok);

              if (!swapped) {
                columns.toAddress = oldToCol;
                this.applyToAddressSwapInPlace(columns, withdrawals, rows, oldToCol);
              } else {
                const HDR_SWAP_OK = '#d4edda';
                hot.ensureColorStyle?.(HDR_SWAP_OK);
                if (hasHeader && typeof columns.toAddress === 'number' && columns.toAddress >= 0) {
                  hot.setRangeClass?.(headerRowIdx, columns.toAddress, headerRowIdx, columns.toAddress, `color-${HDR_SWAP_OK.slice(1)}`);
                }
              }
            } else {
              addrCheck.reason = addrCheck.reason || 'no-alternate-address-column';
            }

            if (!swapped) {
              const HDR_BAD = '#f8d7da';
              hot.ensureColorStyle?.(HDR_BAD);
              if (hasHeader && typeof columns.toAddress === 'number' && columns.toAddress >= 0) {
                hot.setRangeClass?.(headerRowIdx, columns.toAddress, headerRowIdx, columns.toAddress, `color-${HDR_BAD.slice(1)}`);
              }
            }
          }
        }

        this.toAddrValidationDetail = {
          ran: addrCheck.ran,
          ok: addrCheck.ok,
          reason: addrCheck.reason || (addrCheck.ran ? (addrCheck.ok ? 'match' : 'mismatch') : 'skipped'),
          sampleRow: addrCheck.sample ? (addrCheck.sample.rowIndex + 1) : null,
          sampleNetwork: addrCheck.sample ? addrCheck.sample.network : null,
          mappedToCol: columns.toAddress,
          mappedToColName: this.colName(columns.toAddress),
          swapTried,
          swapFromHeaderCol: (fromAddressCol >= 0) ? fromAddressCol : null,
          swapTriedAltCol: (swapTriedCol >= 0) ? swapTriedCol : null,
          swapped,
        };

        const det = this.toAddrValidationDetail;
        const ranStr = det?.ran ? 'ran' : `skipped (${det?.reason || 'n/a'})`;
        const colStr = (typeof det?.mappedToCol === 'number' && det.mappedToCol >= 0)
          ? `${det.mappedToColName || `Column ${det.mappedToCol + 1}`}`
          : 'not mapped';
        const swapBits = det?.swapTried
          ? [`swap tried`, det?.swapTriedAltCol >= 0 ? `alt=${this.colName(det.swapTriedAltCol)}` : null, det?.swapped ? 'swap confirmed' : 'swap not confirmed']
              .filter(Boolean)
              .join(', ')
          : 'no swap attempted';
        const auditMessage = `To-address validation ${ranStr}; mapped column: ${colStr}; ${swapBits}.`;

        const validationNote = this.buildValidationNote(sharedAnalysis?.warnings || [], [auditMessage]);

        this.analysis = {
          headerRow: hasHeader ? headerRowIdx : null,
          format: sharedAnalysis?.format || null,
          isLedgerCsvV1: !!flags.isLedgerCsvV1,
          columns,
          auxiliaryColumns: {
            notes: notesCol,
            timestamp: timeCol,
            ledgerTime: meta?.ledgerTimeCols || null,
          },
          withdrawals,
          validationNote,
          warnings: Array.isArray(sharedAnalysis?.warnings) ? [...sharedAnalysis.warnings] : [],
          summary: { ...(sharedAnalysis?.summary || {}) },
          toAddrValidation: (addrCheck.ran ? (addrCheck.ok ? 'ok' : 'mismatch') : 'skipped'),
        };

        this.$nextTick(() => this.finalizeAnalysisInit());
        this.startPollingAfterAnalysis();
      } catch (e) {
        this.analyzeError = String((e && e.message) || e);
      } finally {
        this.isAnalyzing = false;
      }
    },

computeExtraFindings(rows, withdrawals, dataStart, ctx = {}) {
  const {
    columns = {},
    headerRaw = [],
    //headerNorm = [], eslint says not used!
    notesCol = -1,
    timeCol = -1,
    directionCol = -1,
    isLedgerCsvV1 = false,
    isCsvTxidFromToV1 = false,
    isCsvDebitCreditCashoutV1 = false,
    isCsvActivityDetailsV1 = false,
  //  isCsvStandardSetC = false // eslint says not used!
  } = ctx;

  // 1) Build exclusion sets from Interpreter Results
  const usedHashes = new Set();
  const usedAddrs  = new Set();
  for (const w of (withdrawals || [])) {
    if (w && w.txHash) usedHashes.add(String(w.txHash).toLowerCase());
    if (w && w.toAddress && w.toAddress !== '—') usedAddrs.add(String(w.toAddress).toLowerCase());
  }

  // 2) Utilities
  const norm = s => String(s || '').toLowerCase();
  const H = i => (i != null && i >= 0 ? (headerRaw[i] || `C${i+1}`) : null);
  const safeStr = v => (v == null ? '' : String(v));

  // 3) Matchers
  const RE = {
    // txids
    hex64With0x: /0x[a-fA-F0-9]{64}\b/g,
    hex64Bare:   /\b[a-fA-F0-9]{64}\b/g,

    // addrs
    evmAddr:     /0x[a-fA-F0-9]{40}\b/g,
    btcBase58:   /\b[13][a-km-zA-HJ-NP-Z1-9]{25,34}\b/g,
    btcBech32:   /\bbc1[02-9ac-hj-np-z]{25,90}\b/gi, // simplified
    tronAddr:    /\bT[1-9A-HJ-NP-Za-km-z]{33}\b/g,
    xrpAddr:     /\br[1-9A-HJ-NP-Za-km-z]{24,34}\b/g
  };

  // 4) Helpers to tag network and source
  const inferAddrNet = (textAddr, assetUpper) => {
    try { return inferNetworkFromAddrOrAsset(textAddr, assetUpper) || null; }
    catch { return null; }
  };
  const inferHashNet = (txid, assetUpper) => {
    try { return inferNetwork(txid, assetUpper) || null; }
    catch { return null; }
  };

  const uniqueHashesSet    = new Set();
  const uniqueAddressesSet = new Set();
  const byRow = [];

  // 5) Row scan with context
  for (let r = dataStart; r < rows.length; r++) {
    const row = rows[r] || [];

    const rowHashSet = new Set();
    const rowAddrSet = new Set();

    const hashTokens = [];
    const addrTokens = [];

    // Pull row-level context
    const rawType = (columns.transferType >= 0) ? safeStr(row[columns.transferType]) : '';
    const typeLower = rawType.trim().toLowerCase();

    let amtDisp = null, /*amtAbs = null, */ amtSigned = null;// eslint says amtAbs never used
    let assetUpper = '';
    if (columns.amount >= 0) {
      let amtCell = row[columns.amount];
      if ((isLedgerCsvV1 || isCsvTxidFromToV1 || isCsvDebitCreditCashoutV1 || isCsvActivityDetailsV1) && typeof amtCell === 'string') {
        amtCell = amtCell.replace(/\$/g,'').replace(/,/g,'').replace(/\s+/g,' ').replace(/^\((.*)\)$/,'-$1').trim();
      }
      const parsed = parseAmount(amtCell);
      if (parsed) {
        amtDisp   = parsed.disp.replace(/^-/, '');
   //     amtAbs    = Math.abs(parsed.num);
        amtSigned = parsed.num;
      }
      
   //     console.log("amtAbs " + amtAbs);
    }
    if (columns.asset >= 0) {
      const a = safeStr(row[columns.asset]).trim();
      assetUpper = a ? a.toUpperCase() : '';
    }

    const tsVal = (timeCol >= 0) ? safeStr(row[timeCol]).trim() : null;

    // Optional "direction" hints from CSVs that provide it
    const directionRaw = (directionCol >= 0) ? safeStr(row[directionCol]) : '';

    // Scan each cell, collect matches with origin
    for (let c = 0; c < row.length; c++) {
      const s = safeStr(row[c]);
      if (!s) continue;

      const headerName = H(c);
      const source =
        (c === columns.txHash) ? 'TX column' :
        (c === columns.toAddress) ? 'Destination col' :
        (c === notesCol) ? 'Notes' :
        null;

      // Addresses first
      const pushAddr = (value) => {
        const n = norm(value);
        if (usedAddrs.has(n)) return;
        if (!rowAddrSet.has(n)) {
          rowAddrSet.add(n);
          addrTokens.push({
            value,
            net: inferAddrNet(value, assetUpper),
            colIndex: c,
            header: headerName,
            source
          });
        }
      };

      let m;
      if ((m = s.match(RE.evmAddr)))     m.forEach(pushAddr);
      if ((m = s.match(RE.btcBase58)))   m.forEach(pushAddr);
      if ((m = s.match(RE.btcBech32)))   m.forEach(pushAddr);
      if ((m = s.match(RE.tronAddr)))    m.forEach(pushAddr);
      if ((m = s.match(RE.xrpAddr)))     m.forEach(pushAddr);

      // Hashes next
      const pushHash = (value) => {
        const n = norm(value);
        if (usedHashes.has(n)) return;
        if (!rowHashSet.has(n)) {
          rowHashSet.add(n);
          hashTokens.push({
            value,
            net: inferHashNet(value, assetUpper),
            colIndex: c,
            header: headerName,
            source
          });
        }
      };

      if ((m = s.match(RE.hex64With0x))) m.forEach(pushHash);
      if ((m = s.match(RE.hex64Bare)))   m.forEach(pushHash);
    }

    if (!rowHashSet.size && !rowAddrSet.size) continue;

    // Row-level classification note
    const parts = [];

    // Direction inference
    let dirHint = null;
    if (amtSigned != null) dirHint = (amtSigned < 0) ? 'outgoing-like' : 'incoming-like';

    // Map type strings (re-use your term sets)
    const { outTerms, inTerms } = termSets();
    if (!dirHint && typeLower) {
      if (outTerms.has(typeLower)) dirHint = 'outgoing-like';
      else if (inTerms.has(typeLower)) dirHint = 'incoming-like';
    }
    // Some CSVs provide explicit "Direction"
    if (directionRaw) {
      const dl = directionRaw.toLowerCase();
      if (/\b(send|out|debit|withdraw|withdrawal|external|to\s*wallet)\b/.test(dl)) dirHint = 'outgoing-like';
      if (/\b(receive|in|credit|deposit|from\s*wallet)\b/.test(dl)) dirHint = 'incoming-like';
    }

    if (dirHint) parts.push(`Direction: ${dirHint}`);
    if (amtDisp || assetUpper) {
      parts.push(`Amount: ${amtDisp ? amtDisp : '—'}${assetUpper ? ' ' + assetUpper : ''}`);
    }
    if (typeLower) parts.push(`Type: “${rawType.trim()}”`);
    if (tsVal) parts.push(`Time: ${tsVal}`);

    // Summarize sources (which columns had matches)
    const srcHeaders = Array.from(new Set(
      [...hashTokens, ...addrTokens]
        .map(t => t.header || (t.colIndex != null ? `C${t.colIndex+1}` : null))
        .filter(Boolean)
    ));
    if (srcHeaders.length) parts.push(`Found in: ${srcHeaders.join(', ')}`);

    const contextNote = parts.length ? parts.join(' · ') : '';

    // Convert sets to arrays for backward compatibility
    const hashesArr = Array.from(rowHashSet);
    const addrsArr  = Array.from(rowAddrSet);

    // Track uniques across the whole file
    hashesArr.forEach(h => uniqueHashesSet.add(norm(h)));
    addrsArr.forEach(a => uniqueAddressesSet.add(norm(a)));

    byRow.push({
      rowIndex: r,
      hashes: hashesArr,
      addresses: addrsArr,
      hashTokens,
      addrTokens,
      contextNote
    });
  }

  return {
    byRow,
    uniqueHashes: Array.from(uniqueHashesSet),
    uniqueAddresses: Array.from(uniqueAddressesSet)
  };
},
async validateDetectedAddress(columns, withdrawals) {
  const isSupported = (net) => {
    const n = String(net || '').toLowerCase();
    if (typeof isSupportedNetworkTag === 'function') return isSupportedNetworkTag(n);
    return n === 'btc' || n === 'bitcoin' ||
           n === 'eth-like' || n === 'ethereum' ||
           n === 'tron' || n === 'trx' || n === 'trc20';
  };

  const candidates = withdrawals.filter(w =>
    w && w.txHash && w.toAddress && w.toAddress !== '—' &&
    w.amountRaw != null && isSupported(w.network)
  );
  if (!candidates.length) return { ran:false, ok:true };

  const pick = candidates[0];
  try {
    const payload = {
      txHash:  pick.txHash,
      network: pick.network,
      amount:  pick.amountRaw,
      asset:   pick.assetSymbol || null
    };

    const resp = await fetch(RESOLVE_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(payload)
    });

    const json = await resp.json().catch(() => null);
    if (!json || json.status !== 'success' || !json.toAddress) {
      return { ran:true, ok:false, found:null, sample: pick };
    }

    const found   = String(json.toAddress).toLowerCase();
    const claimed = String(pick.toAddress).toLowerCase();
    return { ran:true, ok:(found === claimed), found, sample: pick };

  } catch (_) {
    return { ran:true, ok:true, sample: pick, transient:true };
  }
}
,
/**
 * Returns: { to, logs:[to...], traces:[to...], mainTo:string|null } or null
 */
async fetchTxFactsEvm(txHash, network, amountAbs, assetSymbol) {
  const key = `${network}|${txHash}|${amountAbs}|${assetSymbol||''}`;
  if (_txFactsCache.has(key)) return _txFactsCache.get(key);

  const payload = {
    txHash,
    network,                // 'Ethereum'
    amount: amountAbs,      // your absolute amount if known (helps resolver pick)
    asset: assetSymbol || null,
    debug: true             // <-- enables evidence in response
  };

  try {
    const resp = await fetch('/api/etherscan/resolve_destination_by_hash2.php', {
      method: 'POST',
      headers: { 'Content-Type':'application/json' },
      body: JSON.stringify(payload)
    });
    const j = await resp.json().catch(()=>null);
    if (!j) { _txFactsCache.set(key, null); return null; }

    const ev = j.evidence || {};
    const res = {
      to: (j.status === 'success' && j.toAddress) ? String(j.toAddress).toLowerCase() : null,
      mainTo: ev.toMain ? String(ev.toMain).toLowerCase() : null,
      logs: Array.isArray(ev.erc20To) ? ev.erc20To.map(a => String(a||'').toLowerCase()).filter(Boolean) : [],
      traces: Array.isArray(ev.internalTo) ? ev.internalTo.map(a => String(a||'').toLowerCase()).filter(Boolean) : []
    };
    _txFactsCache.set(key, res);
    return res;
  } catch {
    _txFactsCache.set(key, null);
    return null;
  }
},
applyToAddressSwapInPlace(columns, withdrawals, rows, newToCol) {
  const hasHeader = Array.isArray(this.headerRaw) && this.headerRaw.length > 0;
  //const dataStart = hasHeader ? (this.headerRowIdx + 1) : 0;
  const toHeaderLabelRaw = hasHeader ? String(this.headerRaw[newToCol] || '') : '';

  const norm = (s) => String(s || '').toLowerCase()
    .replace(/\u2014|\u2013/g, '-').replace(/[\s_]+/g, ' ')
    .replace(/[^\w\- ]+/g, '').trim();

  const HEADER_SENTINELS = new Set(['transaction id','txid','txn hash','hash','hash id','hashid','transaction hash','to','to address','destination','recipient','address','withdraw address','from','from address','notes','note','memo','description','date','time','timestamp','amount','currency','asset','unknown','na','n/a','-','—']);

  const isHeaderish = (val, label) => {
    const v = norm(val);
    return !!v && ( (label && v === norm(label)) || HEADER_SENTINELS.has(v) );
  };

  const looksLikeAddress = (val) => {
    const s = String(val || '').trim();
    if (!s) return false;
    if (/^0x[a-fA-F0-9]{40}$/.test(s)) return true;                 // EVM
    if (/^bc1[0-9ac-hj-np-z]{25,62}$/i.test(s)) return true;        // BTC bech32
    if (/^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(s)) return true;   // BTC legacy
    if (/^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(s)) return true;         // Tron
    if (/^(X|P|C)-avax1[0-9ac-hj-np-z]{20,}$/i.test(s)) return true;// Avalanche X/P/C
    return false;
  };

  for (const w of withdrawals) {
    const r = w.rowIndex;
    let v = (rows[r] && rows[r][newToCol]) != null ? String(rows[r][newToCol]).trim() : '';

    const embedded = extractFirstAddressFromText(v);
    if (embedded) v = embedded;

    if (this.strictAnchorValidation) {
      if (isHeaderish(v, toHeaderLabelRaw) || !looksLikeAddress(v)) v = '';
    }

    w.toAddress = v || '—';

    if (!w.txHash) {
      const sym = w.assetSymbol || '';
      w.network = w.toAddress ? inferNetworkFromAddrOrAsset(w.toAddress, sym) : w.network;
    }

    w.canLookupTxHash = (!w.txHash && !!w.toAddress && w.amountRaw != null && !!(w.assetSymbol || ''));
    if (w.lookupContext && w.canLookupTxHash) {
      w.lookupContext.toAddress = w.toAddress;
    }
  }
}

,
likelySwapFromVsTo(addrCheck, fromAddressCol, rows) {
  if (!addrCheck || !addrCheck.ran || addrCheck.ok) return false;
  if (!addrCheck.found) return false;
  if (typeof fromAddressCol !== 'number' || fromAddressCol < 0) return false;

  const s = addrCheck.sample;
  if (!s || typeof s.rowIndex !== 'number') return false;

  const raw = (rows[s.rowIndex] && rows[s.rowIndex][fromAddressCol] != null)
    ? String(rows[s.rowIndex][fromAddressCol]).trim()
    : '';
  if (!raw) return false;

  const ex = extractFirstAddressFromText(raw);
  const fromCandidate = (ex ? ex : raw).toLowerCase();
  return (fromCandidate === addrCheck.found);
}
,
async _performResolve(row) {
    if (!canRowRunResolve(row)) { row.resolveMsg = 'Unsupported'; return; }

  if (!row || !row.txHash || row.amountRaw == null) {
    row.resolveMsg = 'Missing tx hash or amount';
    return;
  }

  row.resolveBusy = true;
  row.resolveMsg = 'Resolving...';

  try {
    const payload = {
      txHash: row.txHash,
      network: row.network,
      amount: row.amountRaw,
      asset:  row.assetSymbol || null
    };

    const resp = await fetch(RESOLVE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    // Defensive: network hiccups, non-JSON, etc.
    let json = null;
    try { json = await resp.json(); } catch (_) { console.log("_"); }

    if (json && json.status === 'success' && json.toAddress) {
      row.toAddress = json.toAddress;
      row.resolveMsg = 'Found';
      // NEW: immediately re-annotate against history
      if (this.historyIndex) this.annotateRowWithHistory(row);
    } else {
      row.resolveMsg = (json && json.message) ? json.message : 'Not found';
    }
  } catch (err) {
    row.resolveMsg = 'Error';
  } finally {
    row.resolveBusy = false;
  }
},
resolveDestination(row) {
  // Instead of firing immediately, put it into the global queue
  this.enqueueResolve(row);
},

lookupTitle(row) { // cant figure out how to move this into a utils file because of its use in template
  const c = row.lookupContext || {};
  const amt = (typeof c.amountAbs === 'number') ? String(c.amountAbs) : (c.amountAbs || '?');
  const sym = c.assetSymbol || '';
  const ts  = c.timestamp ? ` near ${c.timestamp}` : '';
  const addr = c.toAddress ? ` to ${c.toAddress}` : '';
  return `Match ${amt}${sym ? ' ' + sym : ''}${addr}${ts}`;
},




    colName(idx) { return (idx == null || idx < 0) ? '—' : `C${idx + 1}`; },
    prettyHeader(h, i) { return h ? `${h} (C${i+1})` : `C${i+1}`; },

  }
};
</script>

<style scoped>
/* Shell */
.csv-page-shell {
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px;
  background:
    radial-gradient(1200px 600px at 15% -10%, rgba(43,91,215,.12), transparent 70%),
    radial-gradient(900px 500px at 85% 10%, rgba(49,209,126,.10), transparent 70%),
    #f7f9ff;
}
.center-card {
  width: min(1100px, 96vw);
  background: linear-gradient(180deg, rgba(255,255,255,.85), rgba(255,255,255,.92));
  backdrop-filter: blur(3px);
  border: 1px solid rgba(23, 43, 77, 0.08);
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(16, 24, 40, 0.08);
  padding: 20px 20px 28px;
}
.head { margin-bottom: 10px; }
.muted { opacity: 0.7; font-size: 0.9rem; }

/* Dropzone */
.panel { margin-bottom: 16px; }
.dropzone {
  position: relative;
  border: 2px dashed #87a1ff;
  border-radius: 12px;
  padding: 24px;
  min-height: 160px;
  display: flex; align-items: center; justify-content: center;
  text-align: center; user-select: none; background: #fafbff;
  transition: border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease, opacity 120ms ease;
}
.dropzone--over { border-color: #2b5bd7; background: rgba(43, 91, 215, 0.06); box-shadow: 0 6px 14px rgba(43, 91, 215, 0.18); }
.dropzone--invalid { border-color: #ff5d5d; background: #ffecec; box-shadow: 0 6px 14px rgba(255, 93, 93, 0.25); }
.dz-default { pointer-events: none; transition: opacity 120ms ease; }
.dropzone--over .dz-default { opacity: 0.35; }
.dz-overlay { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; opacity: 0; pointer-events: none; transition: opacity 120ms ease; }
.dropzone--over .dz-overlay { opacity: 1; }
.dz-callout { font-size: 1.05rem; font-weight: 600; margin: 0; }
.dz-invalid { font-weight: 500; color: #b10000; }

/* Actions */
.actions { display: flex; align-items: center; gap: 12px; margin-top: 12px; flex-wrap: wrap; }
.primary {
  appearance: none; border: none; border-radius: 10px; padding: 10px 14px;
  background: #2b5bd7; color: white; font-weight: 600; cursor: pointer;
  box-shadow: 0 4px 12px rgba(43,91,215,.22);
}
.primary:disabled { opacity: .6; cursor: not-allowed; }
.ghost {
  appearance: none; border: 1px solid rgba(23,43,77,.15); background: #fff;
  border-radius: 10px; padding: 9px 12px; cursor: pointer;
}
.inline { display: inline-flex; align-items: center; gap: 6px; }

/* Lite panel */
.panel.lite { border: 1px dashed rgba(23, 43, 77, 0.12); border-radius: 12px; padding: 14px; }
.panel-title { margin: 0 0 8px; }

/* Grid */
.grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.field { display: grid; gap: 6px; margin-bottom: 10px; }
select { padding: 6px 8px; border: 1px solid rgba(23,43,77,.2); border-radius: 8px; }

/* Results */
.section h2 { margin-top: 4px; }
.analysis { margin-top: 18px; }
.analysis__head { margin-bottom: 10px; }
.results {
  width: 100%; border-collapse: collapse; background: #fff;
  border: 1px solid rgba(23, 43, 77, 0.08); border-radius: 10px; overflow: hidden;
}
.results th, .results td { padding: 8px 10px; border-bottom: 1px solid rgba(23,43,77,0.06); }
.results thead th { background: #f4f6ff; text-align: left; }
.results .mono { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 12.5px; }
.results.mini th, .results.mini td { padding: 6px 8px; }
.right { text-align: right; }
.empty { padding: 10px 12px; border-left: 4px solid #ffb74d; background: #fff7e6; border-radius: 6px; }
.err { color: #b10000; font-size: .95rem; }

/* Buttons */
.file-btn {
  display: inline-block; padding: 8px 14px; border-radius: 8px;
  background: #2b5bd7; color: #fff; cursor: pointer; margin-top: 6px;
}

.advanced-row {
  margin-top: 8px;
  border-top: 1px dashed rgba(23,43,77,.12);
  padding-top: 10px;
}


/* add to this component’s <style scoped> or your global css */
.dz-default .file-btn,
.dz-default .file-btn input {
  pointer-events: auto;
}


/* visually hidden but still actionable by label clicks on every browser */
#csvInput {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
}
.badge { padding: 0 6px; border-radius: 10px; font-size: 11px; margin-left: 6px; background: var(--surface-2, #f2f2f2); }
.chip.chip--ghost { border: 1px solid rgba(0,0,0,.12); border-radius: 10px; padding: 0 6px; font-size: 11px; margin-left: 6px; }
.token-line { margin-bottom: 4px; }

.summary-grid { margin-top: 8px; }
.summary-row { display:flex; justify-content:space-between; margin:6px 0; }
.summary-row .label { color: var(--muted-fg, #777); }
.summary-row .value { text-align:right; }
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(16, 24, 40, 0.50);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999; /* above page chrome */
}

.modal {
  width: min(560px, 92vw);
  max-height: 86vh;
  overflow: auto;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 16px 48px rgba(16,24,40,.24), 0 2px 8px rgba(16,24,40,.12);
  border: 1px solid rgba(23,43,77,.08);
}

.modal__head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px; border-bottom: 1px solid rgba(23,43,77,.08);
}

.modal__body { padding: 16px; }
.modal__actions {
  display:flex; align-items:center; justify-content:flex-end; gap:10px;
  padding: 12px 16px; border-top: 1px solid rgba(23,43,77,.08);
}

.close {
  appearance:none; border:none; background:transparent; font-size:20px; cursor:pointer;
}

.submitting .spinner.md { margin: 6px auto 10px; }
/* Generic button base for this component (works for <button> and <router-link>) */
.btn,
.btn:visited {
  display: inline-block;
  appearance: none;
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  line-height: 1.1;
}

/* Primary (blue) */
.btn--primary {
  background: #2b5bd7;
  color: #fff;
  border: 1px solid rgba(0,0,0,0);
}
.btn--primary:hover { filter: brightness(0.95); }
.btn--primary:focus { outline: 2px solid rgba(43,91,215,.35); outline-offset: 2px; }

/* Ghost (white with border) */
.btn--ghost {
  background: #fff;
  color: #1f2a44;
  border: 1px solid rgba(23,43,77,.15);
}
.btn--ghost:hover { background: #f5f7ff; color: #1f2a44; }
.btn--ghost:focus { outline: 2px solid rgba(43,91,215,.25); outline-offset: 2px; }

/* Disabled state */
.btn[disabled],
.btn--primary[disabled],
.btn--ghost[disabled] {
  opacity: .6;
  cursor: not-allowed;
}
/* Bulk bar layout */
.bulkbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  margin: 10px 0;
  background: #f6f8ff;
  border: 1px solid rgba(23,43,77,.10);
  border-radius: 10px;
  flex-wrap: wrap; /* allow wrap on small screens */
}

.bulkbar__left,
.bulkbar__right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn--sm { padding: 6px 10px; font-size: 0.9rem; }

/* Hide the trailing total rows on very small screens */
@media (max-width: 640px) {
  .hide-sm { display: none; }
  .bulkbar { padding: 10px; }
  .bulkbar__right { width: 100%; justify-content: space-between; }
}
/* Runner panel */
.runner {
  margin-top: 16px;
  border: 1px solid rgba(23,43,77,.10);
  border-radius: 12px;
  background: #fff;
  padding: 12px;
}

.runner__head {
  display:flex; align-items:center; justify-content:space-between;
  margin-bottom: 8px;
}
.runner__controls { display:flex; align-items:center; gap:8px; }

.runner__belt {
  position: relative;
  overflow: hidden;
  border: 1px dashed rgba(23,43,77,.18);
  border-radius: 10px;
  padding: 10px;
  background: #fbfcff;
}
.runner__belt-bg {
  position:absolute; inset:0;
  background-size: 30px 30px;
  background-image:
    linear-gradient(135deg, rgba(43,91,215,.10) 25%, transparent 25%, transparent 50%, rgba(43,91,215,.10) 50%, rgba(43,91,215,.10) 75%, transparent 75%, transparent);
  animation: beltMove 1.2s linear infinite;
  opacity: .35;
  pointer-events: none;
}
@keyframes beltMove {
  from { background-position: 0 0; }
  to   { background-position: 30px 30px; }
}

.runner__boxes {
  position: relative;
  display: flex; gap: 10px;
  overflow-x: auto; padding: 2px;
}

.runner__box {
  min-width: 160px;
  background: #fff;
  border: 1px solid rgba(23,43,77,.12);
  border-radius: 10px;
  padding: 10px;
  box-shadow: 0 2px 8px rgba(16,24,40,.06);
}
.runner__box.is-active {
  border-color: #2b5bd7;
  box-shadow: 0 6px 16px rgba(43,91,215,.16);
}
.runner__badge {
  display:inline-block;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 999px;
  background: #f1f4ff;
  color: #2b5bd7;
  margin-bottom: 6px;
}
.runner__phase { font-weight: 600; margin-bottom: 4px; }

.runner__box.phase--queued .runner__phase { color: #64748b; }
.runner__box.phase--resolving .runner__phase { color: #8b5cf6; }
.runner__box.phase--checking .runner__phase { color: #0ea5e9; }
.runner__box.phase--submitting .runner__phase { color: #2b5bd7; }
.runner__box.phase--done .runner__phase { color: #16a34a; }
.runner__box.phase--failed .runner__phase,
.runner__box.phase--skipped .runner__phase { color: #dc2626; }

.runner__progress {
  height: 8px;
  background: #eef2ff;
  border-radius: 999px;
  margin: 10px 0 4px;
  overflow: hidden;
}
.runner__bar {
  height: 100%;
  background: #2b5bd7;
  width: 0;
  transition: width 180ms ease;
}

.runner__log {
  margin-top: 6px;
}
.runner__log summary {
  cursor: pointer;
  font-weight: 600;
}
.runner__log ul { margin: 6px 0 0; padding-left: 18px; }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; }
/* Runner modal sizing */
.modal--runner {
  width: min(980px, 96vw);
  max-height: 90vh;
  overflow: auto;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 16px 48px rgba(16,24,40,.24), 0 2px 8px rgba(16,24,40,.12);
  border: 1px solid rgba(23,43,77,.08);
}

.runner__head-actions {
  display:flex; align-items:center; gap:8px;
}

/* State banners */
.runner__banner {
  padding: 10px 12px;
  margin: 10px 16px 0;
  border-radius: 10px;
  font-weight: 600;
}
.runner__banner--paused {
  background: #fff3cd;
  color: #7a5d00;
  border: 1px solid #ffe08a;
}
.runner__banner--cancelled {
  background: #fdecea;
  color: #8f1d1d;
  border: 1px solid #f5c2c0;
}
.runner__banner--done {
  background: #e9f7ef;
  color: #0f6d2a;
  border: 1px solid #bde5c8;
}

/* Belt + background animation control */
.runner__belt {
  position: relative;
  overflow: hidden;
  border: 1px dashed rgba(23,43,77,.18);
  border-radius: 10px;
  padding: 10px;
  background: #fbfcff;
  margin: 12px 16px;
}

.runner__belt-bg {
  position:absolute; inset:0;
  background-size: 30px 30px;
  background-image:
    linear-gradient(135deg, rgba(43,91,215,.10) 25%, transparent 25%, transparent 50%, rgba(43,91,215,.10) 50%, rgba(43,91,215,.10) 75%, transparent 75%, transparent);
  animation: beltMove 1.2s linear infinite;
  opacity: .35;
  pointer-events: none;
}
@keyframes beltMove {
  from { background-position: 0 0; }
  to   { background-position: 30px 30px; }
}

/* Pause/stop the belt subtly */
.runner__belt.is-paused .runner__belt-bg { animation-play-state: paused; filter: grayscale(1); opacity: .22; }
.runner__belt.is-stopped .runner__belt-bg { animation-play-state: paused; opacity: .12; }

/* Grid of boxes with vertical scroll for large batches */
.runner__grid {
  position: relative;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  grid-auto-rows: auto;
  gap: 10px;
  max-height: 360px; /* keep modal compact */
  overflow: auto;
  padding: 2px;
}

/* Job cards */
.runner__box {
  min-width: 0;
  background: #fff;
  border: 1px solid rgba(23,43,77,.12);
  border-radius: 10px;
  padding: 10px;
  box-shadow: 0 2px 8px rgba(16,24,40,.06);
}
.runner__box.is-active {
  border-color: #2b5bd7;
  box-shadow: 0 6px 16px rgba(43,91,215,.16);
}
.runner__badge {
  display:inline-block;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 999px;
  background: #f1f4ff;
  color: #2b5bd7;
  margin-bottom: 6px;
}
.runner__phase { font-weight: 600; margin-bottom: 4px; }

.runner__box.phase--queued .runner__phase { color: #64748b; }
.runner__box.phase--resolving .runner__phase { color: #8b5cf6; }
.runner__box.phase--checking .runner__phase { color: #0ea5e9; }
.runner__box.phase--submitting .runner__phase { color: #2b5bd7; }
.runner__box.phase--done .runner__phase { color: #16a34a; }
.runner__box.phase--failed .runner__phase,
.runner__box.phase--skipped .runner__phase { color: #dc2626; }

.runner__meta { padding: 0 16px 6px; }

.runner__progress {
  height: 8px;
  background: #eef2ff;
  border-radius: 999px;
  margin: 8px 0 4px;
  overflow: hidden;
}
.runner__bar {
  height: 100%;
  background: #2b5bd7;
  width: 0;
  transition: width 180ms ease;
}

.runner__counts { text-align: right; }

.runner__log { margin: 6px 16px 12px; }
.runner__log summary { cursor: pointer; font-weight: 600; }
.runner__log ul { margin: 6px 0 0; padding-left: 18px; }

.btn--sm { padding: 6px 10px; font-size: 0.9rem; }



.report__disclaimer {
user-select: none;
}
.disclaimer__summary strong { font-weight: 700; }
.disclaimer__version { color: #6b7280; font-size: 12px; }
.disclaimer__hint { margin-left: auto; color: #9ca3af; font-size: 12px; }


/* Card */
.disclaimer__card {
margin-top: 8px;
background: #f8fafc;
border: 1px solid #e5e7eb;
border-radius: 8px;
padding: 12px 12px 8px;
max-height: 180px; /* fits in modal without crowding */
overflow: auto; /* scroll if long */
position: relative;
}


/* subtle top/bottom fade for scrollables */
.disclaimer__card::after {
content: '';
position: sticky;
bottom: -1px;
left: 0; right: 0;
height: 18px;
background: linear-gradient(to bottom, rgba(248,250,252,0), rgba(248,250,252,1));
pointer-events: none;
}


.disclaimer__list {
margin: 8px 0 0 18px;
padding: 0;
line-height: 1.35;
font-size: 13px;
color: #111827;
}
.disclaimer__list li { margin-bottom: 6px; }
.muted { color: #6b7280; font-size: 12px; }


/* Acknowledgement row */
.disclaimer__ack {
display: flex;
align-items: center;
gap: 10px;
margin-top: 10px;
}
.checkbox { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; }


/* Print: always show full disclaimer, no scroll */
@media print {
.disclaimer__card { max-height: none; overflow: visible; background: transparent; border-color: #ddd; }
.disclaimer__summary .disclaimer__hint { display: none; }
}
.advanced-actions { display:flex; gap:.5rem; flex-wrap:wrap; margin:.75rem 0; }

/* Report modal tweaks */
.modal--report { max-width: 760px; }
.report__meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: .4rem .8rem;
  padding: .2rem 0 .6rem;
  border-bottom: 1px solid var(--border);
}
.report__meta .meta__row { display: contents; }
.report__meta .label { color: var(--muted); font-size: .9rem; }
.report__meta .value { font-weight: 600; min-width: 0; }
.report__meta .truncate { display: inline-block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; vertical-align: bottom; }

.report__body { padding: .8rem 0 .4rem; }
.report__text {
  white-space: pre-wrap;
  line-height: 1.5;
  margin: 0;
}

.report__actions {
  display: flex;
  gap: .5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-top: .6rem;
}
/* Full‑screen overlay; does not alter body scrolling */
.report-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0,0,0,0.45);
  display: grid;
  place-items: start center;    /* top-ish; change to center if you prefer */
  padding: 24px 16px;
}

/* Your modal content scrolls internally if tall */
.modal--report {
  max-width: 760px;
  width: calc(100% - 32px);
  max-height: min(85vh, 900px);
  overflow: auto;               /* only the modal scrolls, page remains as-is */
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.25);
}

.copy-btn {
  position: relative;
  /* Keep width steady so label swap doesn’t jiggle layout */
  min-width: 104px; /* adjust to your font/UI */
}

.copy-btn .copy-label-default,
.copy-btn .copy-label-done {
  display: inline-block;
  transition: opacity 140ms ease;
}

.copy-btn .copy-label-done {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  pointer-events: none;
}

.copy-btn.is-copied .copy-label-default { opacity: 0; }
.copy-btn.is-copied .copy-label-done   { opacity: 1; }

/* Optional: subtle success ring */
.copy-btn.is-copied {
  box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.45);
  animation: copyPulse 600ms ease;
}

@keyframes copyPulse {
  0%   { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.45); }
  70%  { box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
  100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
}

/* Disabled state stays readable */
.copy-btn:disabled {
  opacity: 0.8;
  cursor: not-allowed;
}
/* Distinct action styles */
.btn--image {
  background: #eef6ff;
  border-color: #cfe0fb;
  color: #0b63a9;
}
.btn--image:hover { filter: none; background:#e6f1ff; }

.btn--bug {
  background: #fff6f0;
  border-color: #f7d2bd;
  color: #7a3b00;
}
.btn--bug:hover { filter: none; background:#ffeede; }

/* Legend */
.flags { list-style: none; padding: 0; margin: .25rem 0 0; display: grid; gap: .4rem; }
.flag { width: 18px; vertical-align: middle; margin-right: 8px; }
.flag { width: 18px; height: 18px; margin-top: 2px; }

/* Table layout */
.results-wrap {
  position: relative;
  overflow-x: auto;
}
.results {
  width: 100%;
  table-layout: fixed; /* enables predictable truncation */
}

/* Monospace and clip utilities */
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace; }
.cell-clip { max-width: 260px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cell-clip .truncate { display: inline-block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; vertical-align: bottom; }

/* Tiny inline copy button only appears when hovering the cell */
.copychip {
  margin-left: 8px;
  font-size: 11px;
  line-height: 1;
  padding: 2px 6px;
  border: 1px solid var(--ink-200, #d0d4d9);
  background: var(--card-bg, #fff);
  border-radius: 6px;
  cursor: pointer;
  opacity: 0;
  transition: opacity .12s ease;
}
.cell-clip:hover .copychip { opacity: 1; }

/* Right sticky columns */
:root {
  --col-status: 120px;
  --col-send:   110px;
  --col-resolve:130px;
  --sticky-bg: var(--card-bg, #fff);
}

th.sticky-status, td.sticky-status {
  position: sticky; right: 0;
  background: var(--sticky-bg);
  z-index: 3; /* above rows */
  width: var(--col-status);
}

th.sticky-send, td.sticky-send {
  position: sticky; right: var(--col-status);
  background: var(--sticky-bg);
  z-index: 3;
  width: var(--col-send);
}

th.sticky-resolve, td.sticky-resolve {
  position: sticky; right: calc(var(--col-status) + var(--col-send));
  background: var(--sticky-bg);
  z-index: 3;
  width: var(--col-resolve);
}

/* Subtle divider between sticky group and scrollable area */
td.sticky-resolve, th.sticky-resolve {
  box-shadow: -1px 0 0 rgba(0,0,0,0.06) inset;
}

/* Right scroll fade to hint there is more */
.scroll-fade-right {
  position: sticky;
  right: 0;
  top: 0;
  width: 28px;
  height: 100%;
  pointer-events: none;
  background: linear-gradient(to left, var(--card-bg, #fff), rgba(255,255,255,0));
  z-index: 4;
}

/* Responsive tightening: hide "Type" on narrower widths if needed */
@media (max-width: 1280px) {
  .results colgroup col:nth-child(6) { width: 0 !important; }
  .results thead th:nth-child(6),
  .results tbody td:nth-child(6) { display: none; }
  .cell-clip { max-width: 220px; }
}
@media (max-width: 1024px) {
  .cell-clip { max-width: 180px; }
}
/* Keep fixed layout; we’ll enforce minimums per-column */
.results { table-layout: fixed; }

/* Prevent header wrapping/overlap and keep ellipsis if space is tight */
.results thead th {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Column minimums tuned to show ~8 chars of tx and ~6 of address (monospace) */
:root {
  /* Adjust these two if you want a bit more or less */
  --tx-min-ch: 18ch;  /* ~0x + 8–10 visible chars + breathing room */
  --to-min-ch: 16ch;  /* ~0x + 6–8 visible chars + breathing room */
}

/* Apply to header + cell for both columns */
.th-tx, .td-tx { min-width: var(--tx-min-ch); }
.th-to, .td-to { min-width: var(--to-min-ch); }

/* Ensure the text truncates predictably and the Copy chip doesn’t squeeze text */
.cell-clip {
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 56px; /* reserve space so the chip never overlaps text */
}

/* The visible text line; can be omitted if you already use .truncate globally */
.cell-clip .truncate {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
}

/* Make the Copy chip absolute so it doesn’t change the column width */
.copychip {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 11px;
  line-height: 1;
  padding: 2px 6px;
  border: 1px solid var(--ink-200, #d0d4d9);
  background: var(--card-bg, #fff);
  border-radius: 6px;
  cursor: pointer;
  opacity: 0;
  transition: opacity .12s ease;
}
.cell-clip:hover .copychip { opacity: 1; }

/* Slightly tighter on smaller screens while still keeping the targets readable */
@media (max-width: 1280px) {
  :root {
    --tx-min-ch: 16ch;
    --to-min-ch: 14ch;
  }
}
@media (max-width: 1024px) {
  :root {
    --tx-min-ch: 15ch;
    --to-min-ch: 13ch;
  }
}
/* Keep fixed layout for performance and stickiness */
.results { table-layout: fixed; }

/* HEADERS: always show fully; never ellipsis */
.results thead th {
  white-space: nowrap;
  overflow: visible;
  text-overflow: clip;
}

/* Tx/Dest columns should get the leftover width, but never collapse too far */
:root {
  /* Tune visible characters: bump up/down these to taste */
  --tx-ch: 24ch;  /* shows ~0x + 10–12 hex comfortably */
  --to-ch: 20ch;  /* shows ~0x + 8–10 hex comfortably */
}

/* Ensure the Tx/Dest column content can breathe */
.td-tx .truncate { max-width: var(--tx-ch); }
.td-to .truncate { max-width: var(--to-ch); }

/* Clip the cell text, but keep a gutter so the Copy chip never overlaps */
.cell-clip {
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 56px; /* space for Copy chip */
}

/* Copy chip absolutely positioned, so it doesn't squeeze column width */
.copychip {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 11px;
  line-height: 1;
  padding: 2px 6px;
  border: 1px solid var(--ink-200, #d0d4d9);
  background: var(--card-bg, #fff);
  border-radius: 6px;
  cursor: pointer;
  opacity: 0;
  transition: opacity .12s ease;
}
.cell-clip:hover .copychip { opacity: 1; }

/* Slightly tighter on narrower screens */
@media (max-width: 1280px) {
  :root { --tx-ch: 22ch; --to-ch: 18ch; }
  /* You can also shave a bit more here if needed */
}
@media (max-width: 1024px) {
  :root { --tx-ch: 20ch; --to-ch: 16ch; }
}
.inline-row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.inline-row .field.inline {
  flex: 1 1 220px;
}

</style>

<style>
.route-tool #app > nav { z-index: 1000; }
.route-tool,
.route-tool #app,
.route-tool #app > main { background: #f7f9fc; }
</style>
